/**
 * Responsibility: hold every tuning constant for Vibe Flyer in one authoritative place.
 *
 * Per the Named Constants Rule in the steering file, no numeric value with domain
 * meaning may appear bare anywhere else in the codebase. If you find yourself typing a
 * number into logic, it belongs here with a comment explaining what it does.
 *
 * Units used throughout:
 *   - distance: world units (roughly 1 unit = 1 metre, but nothing depends on that)
 *   - angles:   radians in code, but values below are written in degrees and converted
 *   - time:     seconds
 */

const DEG = Math.PI / 180;

/** Convert degrees to radians. Exported so tests and tuning code share one conversion. */
export function degToRad(deg) {
  return deg * DEG;
}

// ---------------------------------------------------------------------------
// World generation
// ---------------------------------------------------------------------------

export const WORLD = {
  /**
   * Default seed used when there is no save and the player has not entered one.
   * Any 32-bit integer works. Change this to get a different default landscape.
   */
  DEFAULT_SEED: 1337,

  /**
   * Edge length of one terrain tile in world units. Larger tiles mean fewer draw calls
   * but coarser load granularity and bigger hitches when one is built.
   *
   * This has to be chosen against FOG.FAR. Terrain must be present all the way out to
   * the fog limit or the player sees it appear, so the resident tile count is roughly
   * (2 * FOG.FAR / TILE_SIZE)^2. At 1600 against a 7000 fog limit that is about 81
   * tiles, which is comfortable. Dropping to 400 would demand over a thousand.
   */
  TILE_SIZE: 1600,

  /**
   * Number of vertices along one edge of a tile's height grid. The tile becomes a
   * (RESOLUTION - 1) x (RESOLUTION - 1) grid of quads. This is the single biggest lever
   * on both visual line density and performance.
   *
   * 33 gives 32 quads per edge, so 50 world units per line at TILE_SIZE 1600. Each tile
   * is then 2112 line segments. Powers-of-two-plus-one keep the grid symmetric.
   */
  TILE_RESOLUTION: 33,
};

/**
 * Distance between adjacent terrain lattice points, in world units.
 *
 * Derived rather than authored, so it cannot drift out of step with TILE_SIZE and
 * TILE_RESOLUTION. This is the spacing of the lines you actually see, which makes it the
 * spacing anything asking "how high is the visible ground" has to work in — see
 * `heightfield.sampleSurface`.
 */
export const LATTICE_SPACING = WORLD.TILE_SIZE / (WORLD.TILE_RESOLUTION - 1);

// ---------------------------------------------------------------------------
// Tile streaming — what is resident around the viewer at any moment
// ---------------------------------------------------------------------------

export const TILES = {
  /**
   * A tile is built once any part of it comes within this distance of the viewer.
   *
   * **Must stay greater than FOG.FAR.** That is the whole contract that makes an endless
   * world invisible: terrain has already faded completely to black by FOG.FAR, so a tile
   * appearing at this distance cannot be seen appearing. Drop this below FOG.FAR and the
   * player watches terrain pop into existence, which is the one thing this system exists
   * to prevent. There is a test guarding the relationship.
   *
   * 7800 is FOG.FAR (7000) plus half a tile of margin, so a tile is always complete and
   * resident before it has any chance of being even faintly visible.
   */
  LOAD_DISTANCE: 7800,

  /**
   * A tile is disposed once every part of it is beyond this distance.
   *
   * The gap between this and LOAD_DISTANCE is hysteresis, and it is not optional. With a
   * single threshold, a viewer hovering near it would build and dispose the same tile
   * repeatedly, every frame, forever. One full TILE_SIZE of gap means a tile must be
   * genuinely left behind before it is released.
   */
  EVICT_DISTANCE: 9400,

  /**
   * Maximum tiles built in a single frame, so the cost of terrain entering range is spread over frames
   * instead of landing as a hitch.
   *
   * Steady flight brings in about 1.6 tiles per second. A budget of 1 supplies 60 a second, roughly forty
   * times the demand, so it never falls behind — it only flattens bursts, such as a turn swinging a whole
   * new flank into range.
   *
   * **Per frame, and it used to be per simulation step, which is not the same thing at all.** The tile
   * update lived inside the fixed-timestep loop, so this budget was silently multiplied by however many
   * steps a frame ran — up to `MAX_FRAME_TIME / TIMESTEP`, which is fifteen. And because `TIMESTEP` is
   * exactly 1/60, a 60 Hz display sits on the boundary where the common case is two steps, so a single
   * long frame became two tile builds, which guaranteed the next frame was long too. Renamed as well as
   * moved, because the old name is what made the bug read as correct.
   *
   * **Lowered from 2 when the forest landed.** A forest tile then cost around 7 ms to build, and two of
   * those in one frame left nothing for rendering.
   *
   * Nearest tiles are always built first, so a one-frame backlog is terrain out at the fog limit that
   * nobody can see.
   */
  MAX_BUILDS_PER_FRAME: 1,

  /**
   * How far into a frame a tile build may still be started, in milliseconds.
   *
   * Measured against this frame's own work, so what it actually catches is a frame arriving with a lot of simulation to
   * do: after a tab switch or a breakpoint the clock releases up to fifteen steps at once, and adding a tile build on top
   * of that is how one stall becomes several.
   *
   * **A backstop, not the main protection.** What stops a slow frame compounding is `MAX_BUILDS_PER_FRAME` being per
   * *frame* — see the note there. In normal flight the work before the tile update is well under a millisecond, so this
   * never fires; it earns its place by making the intent explicit and by holding if the per-frame work ever grows.
   *
   * When it does fire the build is skipped and the backlog waits, which it can afford to: flight consumes about 1.6 tiles
   * a second against 60 opportunities, nearest tiles are built first, and anything left waiting is out beyond `FOG.FAR`
   * where it has already faded to black.
   */
  BUILD_DEADLINE_MS: 6,

  /**
   * Probes per axis used to decide which biomes could possibly appear in a tile.
   *
   * A biome with no presence near a tile would otherwise walk its whole grid of slots, sample its own
   * fields, and place nothing — and that was 38–58% of a tile build, because only three to eight of the
   * thirteen biomes are ever present at once. See `biomesNear` in `tileContent.js`.
   *
   * Five per axis over the tile plus its margin is a spacing of about 1,200 units, against suitability
   * fields whose finest feature is thousands of units across. More probes cost a biome-field sample each
   * and buy nothing.
   */
  PRESENCE_PROBES: 5,

  /**
   * How far beyond a tile the presence probes reach, in world units.
   *
   * A structure can be *sited* outside the tile that draws it — an airfield's slot centre may be several
   * hundred units away, a telescope site further — and the weight that decides whether it exists is
   * sampled at that site. So the probes have to cover the ground a tile's contents could be decided from,
   * not just the tile.
   *
   * A full tile width, which is comfortably more than any installation's reach plus its jitter.
   */
  PRESENCE_MARGIN: 1600,

  /**
   * How many tiles the worker may be building at once.
   *
   * A cap on work *in flight*, not on work queued, and the difference matters during a turn: a fast heading
   * change brings a whole new arc into range at once, and without a cap the worker would spend a second on
   * tiles that had gone out of range again before it finished. The same reason the manager builds
   * nearest-first.
   *
   * Steady demand at cruise is about 1.6 tiles a second against builds of roughly 9 ms, so the worker is idle
   * most of the time and four is generous. Raise it if terrain ever fails to keep up during a hard turn.
   */
  MAX_TILES_IN_FLIGHT: 4,

  /**
   * How often the resident tile and line-segment counts are written to the console, in
   * seconds. This is how you confirm memory is flat over a long flight: the counts must
   * settle and stay settled. Set to 0 to silence it.
   */
  STATS_LOG_INTERVAL: 5,
};

// ---------------------------------------------------------------------------
// Noise (fractal Brownian motion over simplex noise)
// ---------------------------------------------------------------------------

export const NOISE = {
  /**
   * How many noise layers are summed. Each octave doubles frequency and reduces
   * amplitude by PERSISTENCE. More octaves means more fine detail and more CPU.
   * Beyond ~6 the added detail is smaller than a single grid cell and is wasted.
   */
  OCTAVES: 5,

  /**
   * Frequency of the first (broadest) octave, in cycles per world unit. Smaller values
   * make larger, smoother landforms. 1/2000 means the broadest feature spans ~2000 units.
   */
  BASE_FREQUENCY: 1 / 2000,

  /**
   * Amplitude multiplier applied per octave. 0.5 is the classic value: each octave
   * contributes half as much as the one before. Higher values (0.6-0.7) look rougher
   * and noisier; lower values (0.3-0.4) look smoother and more rolling.
   */
  PERSISTENCE: 0.5,

  /**
   * Frequency multiplier applied per octave. 2.0 is standard. Values slightly off 2.0
   * (like 2.13) avoid visible grid-aligned artefacts where octaves line up.
   */
  LACUNARITY: 2.13,
};

// ---------------------------------------------------------------------------
// Biome field — the low-frequency noise that decides what is where
// ---------------------------------------------------------------------------

export const BIOME = {
  /**
   * Wavelength of each biome's suitability field, in world units. Much longer than terrain
   * noise, because biome regions must be far larger than terrain features.
   *
   * At a 260-unit cruise speed, 28,000 units is roughly a minute and three quarters of flight across one
   * wavelength, so a region is something like a minute to cross.
   *
   * **Doubled from 14,000 once there were thirteen biomes.** With that many competing, regions changed too
   * often to settle into anywhere — the complaint was that the world "changes too quickly", and the cause
   * was not the size of any one region but how many different ones a minute of flight crossed.
   *
   * Note this changes only the *scale* of the world, not its statistics: uniformly stretching the domain
   * leaves the distribution of suitability values, and therefore the transitional and per-biome shares,
   * exactly where they were. Nothing needed rebalancing.
   */
  FIELD_WAVELENGTH: 28000,

  /**
   * How strongly the mega-scale term counts against the normal one, inside a mega area.
   *
   * **Must be well above 1 to actually change the scale of the regions.** The two terms are added, so at 1
   * they carry equal weight and the winning biome is decided by a mixture of both wavelengths — which comes
   * out at roughly twice normal, not four times. Above 1 the coarse term dominates the ranking and the
   * normal field survives as fine detail along the edges, which is what keeps a huge region from looking
   * like a smooth blob.
   *
   * A side effect worth knowing: it widens the total spread of suitabilities, so transitions inside a mega
   * area are proportionally narrower than elsewhere. That is the right direction — a huge region with a
   * proportionally huge transition would be nothing but transition.
   */
  MEGA_BOOST: 8,

  /**
   * How much larger a "mega" region is than a normal one, as a multiple of the wavelength.
   *
   * ## The occasional huge landmark
   *
   * Every so often the world should hold a city or a mountain range on a completely different scale — big
   * enough that crossing it is an event rather than a transition. At 4 against the 28,000 above, a mega
   * region runs on a 112,000-unit field, which is minutes of flight from one side to the other.
   *
   * ## Why it is blended at the suitability level and not by scaling coordinates
   *
   * The obvious implementation is to sample the field at `(x / s, z / s)` with `s` varying from 1 to 4. It
   * does not work, and the reason is worth recording: the sample position becomes `x / s(x)`, whose
   * derivative is `1/s - x·s'/s²`. At world coordinates in the hundreds of thousands that second term
   * dominates any non-zero `s'`, so the sampled position races around and the biome field turns to noise.
   * Domain warping is only safe near the origin.
   *
   * So each biome carries **two** suitability fields — one at the normal wavelength, one at this multiple
   * of it — and `MEGA_SHARE` decides how much the coarse one is trusted at a given place. Both fields are
   * continuous and the blend factor is continuous, so the result is too, and no coordinate is ever
   * distorted.
   */
  MEGA_SCALE: 4,

  /**
   * Share of the world where the coarse fields fully take over, as a fraction of area.
   *
   * ## Why area rather than "one region in ten"
   *
   * The request was a one-in-ten chance of a region being four times the size, and read literally by
   * *count* that means something surprising: a mega region covers sixteen times the area, so one in ten
   * regions being mega puts **64% of the world** at the larger scale, which would make huge the norm and
   * undo the doubling above.
   *
   * What actually matters while flying is how often you *cross into* one, and along a flight path that
   * works out as `(share / MEGA_SCALE) / ((share / MEGA_SCALE) + (1 - share))`.
   *
   * ## And why it is 0.12 rather than the 0.3 that would give one in ten
   *
   * **Cost.** Inside a mega area every biome's coarse field has to be sampled as well as its normal one, which
   * roughly doubles what the biome field costs — measured, terrain sampling went from 2.84 ms to 6.46 ms per
   * tile. That is affordable over a tenth of the world and not over a third of it.
   *
   * At 0.12 a mega region is something you meet every few minutes rather than every minute. Raising this back
   * toward 0.3 is one number, and the price is about 1.5 ms on every tile built in a mega area — worth doing
   * only if the coarse fields are made cheaper first, for which see `MEGA_FIELD_OCTAVES`.
   */
  MEGA_SHARE: 0.12,

  /**
   * Wavelength of the field that decides where the mega regions are, in world units.
   *
   * **Has to be much larger than the mega regions it contains**, or a patch would be smaller than the
   * region it is meant to hold and the larger scale would never fully express. At 420,000 against a
   * 112,000-unit mega field, a patch comfortably holds a whole one.
   */
  MEGA_WAVELENGTH: 420000,

  /** Octaves in that field. Few, for the same reason the suitability fields use few: rounded patches, not a fractal coastline. */
  MEGA_OCTAVES: 2,

  /**
   * Octaves in each biome's mega-scale suitability field.
   *
   * **Must match `FIELD_OCTAVES`, and this was tried at 1 to save a noise evaluation.** The mega composition
   * renormalises the sum of the two fields on the assumption that they have the same spread, and octave count
   * is what decides that spread — a one-octave field swings wider than a two-octave one, because successive
   * octaves partially cancel. Mismatching them made the total spread inside a mega area larger than outside,
   * which diluted every biome's `suitabilityBias` relative to it and gave the world two different balances
   * depending on scale: measured, city held 6.2% of normal ground against 1.8% of mega.
   *
   * Kept as its own key rather than reusing `FIELD_OCTAVES` directly so that the requirement is stated
   * somewhere rather than being an invisible coupling.
   */
  MEGA_FIELD_OCTAVES: 2,

  /**
   * Suitability level above which the mega field is fully in charge, and the band below it over which it
   * takes over.
   *
   * **Set by measurement, not by choosing a number.** fBm never approaches its extremes, so a threshold
   * picked by eye lands somewhere unintended in the distribution — the same lesson the forest stands, the
   * mesa terraces, the lake basins and the airfield embankments each taught once. Measured here, this field
   * occupies 0.19 to 0.80 with a median of 0.50 and a 70th percentile of 0.597, so the *top* of the ramp has
   * to sit at 0.597 to leave 30% of the world above it. The first attempt used 0.6 as the threshold with a
   * 0.16 ramp and produced 4% fully mega with a third of the world stuck partway.
   *
   * The ramp is a compromise. Wide enough that the changeover spans thousands of units and cannot be seen as
   * a contour; narrow enough that most of the world is decisively at one scale or the other, and that the
   * band paying for both sets of fields stays small.
   */
  MEGA_THRESHOLD: 0.638,
  MEGA_RAMP: 0.05,

  /**
   * Octaves in a suitability field. Deliberately few: a biome region wants an organic
   * outline, not a fractal coastline. More octaves make edges ragged and produce isolated
   * specks of one biome stranded inside another.
   */
  FIELD_OCTAVES: 2,

  /**
   * How many of the strongest biomes are blended at any coordinate.
   *
   * ## This was documented as a free cost cap, and with thirteen biomes it stopped being one
   *
   * The original reasoning: a biome is only ever displaced from the top few at the moment it *ties* with the
   * one replacing it, and tied biomes carry equal weight, so nothing jumps. The first half is true and the
   * second half does not follow. At the tie the two carry the same *weight*, but they are different biomes
   * with different elevation functions, so the blend jumps by that weight times the difference between them.
   * The cap is only safe when the biome being dropped carries a *small* weight — which needs the cap to be
   * comfortably larger than the number of biomes that are ever genuinely close together.
   *
   * Measured over 360,000 units of walking with the full thirteen-biome registry, the worst weight carried by
   * a biome at the moment it was dropped:
   *
   * | cap | worst dropped weight |
   * |---|---|
   * | 3 | 0.313 |
   * | 4 | 0.199 |
   * | 5 | 0.123 |
   * | 6 | 0.020 |
   *
   * At 3 that is a third of a biome's contribution appearing or vanishing between two adjacent samples — a
   * visible seam. **This is not new and was not caused by the scale change**: the same measurement at the old
   * wavelength with no mega scaling gives 0.308. It is what accumulated as biomes were added, since nothing
   * re-examined the cap after the third one.
   *
   * 5 keeps it at 0.12 for two more `Math.pow` calls per sample and a handful of comparisons. Raising it
   * further is cheap and 6 nearly eliminates the jump, so this is the number to revisit whenever a biome is
   * added.
   */
  MAX_BLEND_COUNT: 5,

  /**
   * How far below the leading biome's suitability another biome can be and still
   * contribute anything.
   *
   * This is the width of transition zones and the main reason pure regions exist at all.
   * The obvious scheme — normalising the top few suitabilities directly — never produces a
   * pure region: a leader on 0.8 against a runner-up on 0.7 still comes out 53/47, so the
   * whole world is a blend of everything and no biome ever looks like itself. Measuring
   * each biome's distance *behind the leader* instead means the runner-up reaches zero at a
   * definite distance, which is what makes somewhere unambiguously mountains.
   *
   * Larger values widen transitions and blur regions together. Smaller values sharpen the
   * boundaries and, taken far enough, produce visible seams.
   */
  BLEND_WIDTH: 0.13,

  /**
   * How many blend widths behind the leader a biome must be before a tile stops asking it for anything, as
   * a safety factor.
   *
   * A biome can only carry weight within one blend width of the leader, so **1.0 is the exact bound** and
   * anything above it is slack for the fact that the check is sampled on a grid rather than continuously.
   *
   * **It shipped at 2 and that made the skip nearly pointless.** Measured over 60 spread tiles, the number of
   * biomes kept alive was 10.1 of 13 at a safety of 2, against 5.6 that carry any weight at all — so the
   * saving the skip was written for was mostly being given straight back. At 1.5 it keeps 8.6, and the margin
   * still comfortably covers how much a suitability field can change between two probes 1,200 units apart.
   *
   * Being wrong in the cheap direction costs microseconds; being wrong in the other direction drops a
   * structure or, worse, a *reservation*, which is how something ends up standing in water. So this is guarded
   * by a test that pins byte-identical geometry against a pipeline that skips nothing — lower it only with
   * that test spread across boundary and mega-scale tiles.
   */
  SKIP_SAFETY: 1.5,

  /**
   * Exponent applied to each weight before normalising. Above 1 it pulls the middle of a
   * transition toward the dominant biome, so the lead-in starts gently and the handover
   * happens nearer the middle. At 1 the transition is a straight ramp.
   *
   * ## Raised from 1.8 to 2.6 when the world reached six biomes
   *
   * **This is a Task 8 dial and the change is deliberate.** More biomes means more places where two or
   * three are near a tie, so the share of the world reading as "a blend of things" grew as biomes were
   * added — measured across 32,400 samples:
   *
   * | biomes | transitional | unambiguous |
   * |---|---|---|
   * | four | 38.6% | 25.2% |
   * | six, at 1.8 | 49.1% | 25.2% |
   * | six, at 2.6 | **38.6%** | 32.3% |
   *
   * At 1.8 with six biomes, almost half the world was mid-transition and nowhere read as itself for long,
   * which undoes the distinctness approved at Task 8.
   *
   * Sharpness was raised rather than `BLEND_WIDTH` narrowed, on purpose: narrowing the width shortens the
   * *physical* transition, while sharpening keeps the same lead-in distance and resolves the middle of it
   * toward the leader.
   *
   * ## Raised a second time, to 3.2, when the world reached eight biomes
   *
   * The same pressure, and it does not go away — every biome added creates more places where two or three sit near a
   * tie. Measured across 28,900 samples:
   *
   * | | transitional | open ground | each special region |
   * |---|---|---|---|
   * | four biomes, at 1.8 | 38.6% | 27.8% | 9–12% |
   * | eight biomes, at 2.6 | 43.0% | 21.1% | 6.0–7.5% |
   * | eight biomes, at 3.2 | **38.1%** | **26.6%** | 5.9–7.4% |
   *
   * At 2.6 with eight biomes, transitional had climbed back to 43% and open ground had fallen to 21%. Sharpening
   * reclaims the whole transitional surplus and hands it back to every biome at once, which is why it beats the
   * alternative of starving the special regions to feed the default.
   *
   * A boundary now takes roughly 5.5 seconds of flight to cross, down from 6.8 and from 9.5 originally. Still by
   * some margin the slowest change in the world, and still a handover rather than a seam — but this is the second
   * time an approved dial has moved, so **revert to 2.6 if regions now switch too decisively.**
   */
  WEIGHT_SHARPNESS: 3.2,
};

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

export const RENDER = {
  /**
   * Scene background. Pure black is the intent; if it ever reads as flat we have room
   * to lift this to a very dark grey without touching anything else.
   */
  BACKGROUND_COLOR: 0x000000,

  /** Vertical camera field of view in degrees. Wider feels faster and more open. */
  FIELD_OF_VIEW: 60,

  /** Near clip plane. Small enough that the aircraft never clips at close camera range. */
  NEAR_CLIP: 1,

  /**
   * Far clip plane. Must sit comfortably beyond FOG_FAR so terrain is fully faded to
   * black before it is clipped, otherwise you see a hard edge.
   */
  FAR_CLIP: 12000,

  /**
   * Cap on device pixel ratio. Retina displays would otherwise render 4x the pixels for
   * a wireframe scene that does not benefit much, and bloom cost scales with pixel count.
   *
   * **Lowered 2 → 1.5 while chasing a frame time that was constant rather than spiky.** A frame time whose
   * worst is its average, on a scene whose resident geometry barely changes, means the cost is per *pixel* —
   * and this is the only dial that changes how many pixels there are. At 2 a HiDPI display is asked for four
   * times the pixels of a logical one; at 1.5 it is 2.25 times, which still resolves a 1px line cleanly.
   *
   * No effect whatever on an ordinary display, where the device ratio is 1 and this cap never binds. That is
   * worth knowing before reaching for it: if the reported frame time does not move, the display was already
   * at 1 and the pixels are not where the time is going.
   */
  MAX_PIXEL_RATIO: 1.5,
};

// ---------------------------------------------------------------------------
// Fog — the distance fade that hides the tile loading boundary
// ---------------------------------------------------------------------------

export const FOG = {
  /**
   * Distance at which terrain begins to dim. Anything closer renders at full brightness.
   */
  NEAR: 1200,

  /**
   * Distance at which terrain has faded completely to the background colour. This must
   * be shorter than the tile load radius, or the player would see terrain pop in.
   */
  FAR: 7000,
};

// ---------------------------------------------------------------------------
// Occlusion — hidden-line removal, so nearer geometry hides what is behind it
// ---------------------------------------------------------------------------

/**
 * Wireframe occludes nothing by default, and it compounds badly.
 *
 * Every line of every object behind the one you are looking at still rasterises and still
 * feeds bloom. Flying into a dense city meant seeing through every building at once; flying
 * along a ridge meant every ridge behind it stacking up. Both piled into a bright blur.
 *
 * The fix is a depth mask: filled geometry matching each surface, drawn before the lines,
 * writing depth but **no colour**. Lines behind a nearer surface then fail the depth test and
 * are never drawn at all — so they cost nothing to rasterise and contribute nothing to bloom.
 *
 * The effect is stronger than "see through four buildings": you see the nearest surface and
 * nothing behind it. Getting a specific number of visible layers would need alpha-blended
 * masks sorted back-to-front every frame, which is fragile and expensive for a result that is
 * harder to reason about.
 */
export const OCCLUSION = {
  /**
   * Mask the terrain surface, so a ridge hides the terrain behind it.
   *
   * Separate from structures on purpose. Masking buildings but not terrain is a real middle
   * setting: it fixes the city pileup, which is by far the worse of the two, while leaving
   * distant terrain visible through nearer ridges the way it was before.
   */
  MASK_TERRAIN: true,

  /** Mask buildings, so a tower reads as solid rather than as a see-through cage. */
  MASK_STRUCTURES: true,

  /**
   * Depth bias applied to mask geometry, pushing it away from the camera.
   *
   * **This is the z-fighting dial.** Terrain lines lie exactly on the surface that masks
   * them, so with no bias the two fight for the same depth and lines flicker in and out at
   * distance. Too much bias and a line starts to show through the surface in front of it.
   *
   * `FACTOR` scales with how steeply the surface is angled away, which is what matters for
   * terrain seen at a grazing angle. `UNITS` is a flat offset.
   */
  DEPTH_BIAS_FACTOR: 1.5,
  DEPTH_BIAS_UNITS: 2,
};

// ---------------------------------------------------------------------------
// Bloom — the neon glow. The most expensive pass in the pipeline.
// ---------------------------------------------------------------------------

export const BLOOM = {
  /**
   * Glow strength. This is the primary "how neon is it" dial and is exposed to the
   * player in settings so it can be dialled back on weak hardware.
   */
  STRENGTH: 0.9,

  /**
   * Glow spread in screen space. Larger values bleed light further and look softer and
   * dreamier; smaller values keep lines crisp.
   */
  RADIUS: 0.5,

  /**
   * Brightness below which pixels do not glow at all. Low values make even dim distant
   * lines glow, which can wash out the fog fade. Keep this above the fog floor.
   */
  THRESHOLD: 0.1,

  /**
   * Range and granularity of the player-facing glow slider.
   *
   * The single authoritative home for the range: the settings store clamps to it and the menu slider
   * spans it, so the two cannot disagree about what a valid strength is.
   *
   * Zero is allowed on purpose. Bloom is the most expensive pass in the pipeline, and being able to
   * turn it off entirely is the last resort on weak hardware — the world still reads as wireframe
   * without it, just flatter.
   */
  MIN_STRENGTH: 0,
  MAX_STRENGTH: 2,
  STRENGTH_STEP: 0.05,

  /**
   * Fraction of the frame the bloom chain runs at.
   *
   * **The cheapest large saving in the pipeline.** `UnrealBloomPass` is eleven fullscreen passes, and the two
   * largest of them scale with this squared — so a half-resolution input quarters the area of every level.
   * Bloom is a wide blur, so the difference is close to invisible; the same trick is standard in engines that
   * ship this effect.
   *
   * Lower it further if bloom turns out to be where the frame is going. At 0.25 the glow starts to look
   * blocky where a line crosses a dark area, which is the point to stop.
   */
  RESOLUTION_SCALE: 0.5,
};

// ---------------------------------------------------------------------------
// Simulation timing
// ---------------------------------------------------------------------------

export const SIM = {
  /**
   * Fixed simulation step in seconds. 1/60 keeps flight behaviour identical regardless
   * of display refresh rate. Rendering interpolates between steps.
   */
  TIMESTEP: 1 / 60,

  /**
   * Maximum simulation time processed in a single frame. Without this cap, a long stall
   * (tab backgrounded, breakpoint hit) would queue up hundreds of steps and freeze the
   * page trying to catch up. Excess time is discarded.
   */
  MAX_FRAME_TIME: 0.25,
};

// ---------------------------------------------------------------------------
// Biome: mountains
// ---------------------------------------------------------------------------

export const MOUNTAINS = {
  /**
   * Height of the highest possible ridge, in world units. Ridged noise reaches 1 only
   * rarely, so typical peaks land well below this.
   */
  MAX_ELEVATION: 900,

  /**
   * Wavelength of the broadest ridge structure in world units: roughly the distance from
   * one major ridge line to the next. Larger values give fewer, grander ranges.
   */
  RIDGE_WAVELENGTH: 2600,

  /** Noise octaves for the ridge field. More gives craggier rock at close range. */
  OCTAVES: 6,

  /**
   * Amplitude falloff per octave. Lower than the global default because ridged noise
   * already looks busy; too much high-frequency detail turns crests into visual noise.
   */
  PERSISTENCE: 0.45,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /**
   * Exponent applied to the normalised ridge value before scaling to elevation.
   * Above 1 this deepens valleys and narrows peaks, which reads as more dramatic
   * mountains. At 1 the range looks like rolling hills.
   */
  PEAK_SHARPNESS: 1.6,

  /**
   * How suitable this biome considers itself, added to its noise field before comparison
   * with every other biome.
   *
   * The thumb on the scale for how much of the world is mountainous. Raise it and ranges
   * appear more often and reach further; lower it and they become rarer islands.
   *
   * Zero is not a no-op so much as a stated neutral: mountains take whatever share their
   * noise field earns against `BASELINE.SUITABILITY`. Measured, that is about a quarter of
   * the world as unambiguous mountains.
   */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Biome: baseline — the low-feature ground between everything else
// ---------------------------------------------------------------------------

/**
 * The connective tissue of the world. Gentle, open, unremarkable on purpose: it is what
 * makes arriving somewhere feel like arriving, and it is where the eye rests.
 *
 * Unlike every other biome, baseline has no suitability *field* — it offers a flat constant
 * instead. That makes it the default rather than another competing blob: wherever no other
 * biome's noise rises above this level, baseline fills in. Giving it its own noise field
 * would leave places where two special biomes weakly overlap with no calm ground between
 * them, which is exactly the mush the design is trying to avoid.
 */
export const BASELINE = {
  /**
   * The constant every other biome's suitability is measured against.
   *
   * This single number sets the balance of the whole world. Raise it toward 0.6 for a mostly
   * open, calm world with occasional features; lower it toward 0.4 for a world crowded with
   * them. Only the *gap* between this and a biome's `suitabilityBias` matters, so with one
   * special biome this dial and that one are the same dial from opposite ends.
   *
   * **This has to rise as biomes are added.** Baseline only wins where *every* other field
   * falls below this level, so its share shrinks geometrically with each new competitor. It
   * was 0.52 with one special biome and 30% of the world open; adding the city dropped that to
   * 11% at the same setting. Expect to raise it again in Task 8 when the forest lands.
   *
   * It is also the dial for how much open ground sits *between* two special biomes. Raising it
   * makes every special region need a higher noise value to win, so regions shrink and pull
   * apart, and two of them touching directly becomes rarer. Measured across a 144,000-unit
   * square, with "overlap" meaning city and mountains both above a weight of 0.25:
   *
   * | level | overlap | pure open | pure mountains | pure city |
   * |-------|---------|-----------|----------------|-----------|
   * | 0.56  |  6.9%   |   17.3%   |     12.2%      |   9.4%    |
   * | 0.60  |  4.9%   |   24.4%   |      8.1%      |   6.4%    |
   * | 0.64  |  3.2%   |   33.0%   |      4.7%      |   3.6%    |
   * | 0.68  |  1.8%   |   42.9%   |      2.1%      |   1.6%    |
   *
   * 0.60 buys a third less overlap and half again as much open ground, while keeping mountains
   * and cities common enough to come across regularly. Going much further empties the world.
   */
  /**
   * How suitable baseline considers itself everywhere. **The thumb on the scale for how much open ground the world
   * has**, and the value that has to be revisited whenever a special region is added.
   *
   * Raised 0.62 → 0.645 when mesa and canyon country arrived. Open ground — baseline plus hills plus farmland — had
   * fallen to 21.1% from the 26-28% it had held since the beginning, because two new *special* regions ate into it.
   * Rolling hills and farmland needed no such change, since both are themselves open ground.
   *
   * Raised again 0.645 → 0.652 when suburbia arrived, for exactly the same reason: a fourteenth biome took
   * baseline's own share from 10.2% to 8.5%, and a town is emphatically a special region rather than more open
   * ground. 0.652 puts it back to 10.0%.
   *
   * Raised again 0.652 → 0.658 when industry arrived. The fifteenth biome took baseline from 10.0% to 8.6%, and an
   * industrial estate is the least restful region in the world, so it must not be paid for out of the open ground.
   * 0.658 puts it back to 10.0%.
   *
   * Raised again 0.658 → 0.664 when the fairground arrived, for the same reason and by the same amount. The
   * sixteenth biome took baseline from 9.9% to 8.5%; 0.664 puts it back to 10.0%. **Every special region added so
   * far has cost open ground about a point and a half**, and the correction has been six thousandths each time —
   * which is now a reliable enough pattern to expect, though not to skip measuring.
   *
   * Raised again 0.664 → 0.670 when the terraced vineyard arrived, and the pattern held for a third time: the
   * seventeenth biome took baseline from 9.9% to 8.6%.
   *
   * **The response is steeper than it looks, and steeper the higher it goes.** Measured at seventeen biomes:
   * 0.664 gives 8.6%, 0.670 gives 10.0%, 0.676 gives 11.6%. Move it in *thousandths* and measure.
   */
  SUITABILITY: 0.670,

  /**
   * Height of the tallest baseline rise, in world units. Deliberately a small fraction of
   * MOUNTAINS.MAX_ELEVATION — the contrast when leaving a range is most of the point.
   */
  MAX_ELEVATION: 210,

  /**
   * Wavelength of the broadest swell in world units. Long and low: these should read as
   * open ground with a gentle roll, not as small hills.
   */
  WAVELENGTH: 3800,

  /** Few octaves. Detail here would just be noise on ground meant to be restful. */
  OCTAVES: 3,

  /** Amplitude falloff per octave. High, so what little detail exists stays soft. */
  PERSISTENCE: 0.4,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,
};

// ---------------------------------------------------------------------------
// Biome: rolling hills — open country with real relief
// ---------------------------------------------------------------------------

/**
 * Hills, not mountains and not flat.
 *
 * ## What this is for
 *
 * Baseline is deliberately restful and mountains are deliberately dramatic, which left a gap: there was
 * nowhere that simply *undulated*. Flying baseline is calm to the point of being featureless, and the
 * only way to get vertical interest was to find a mountain range.
 *
 * ## Why plain fBm, like baseline and unlike mountains
 *
 * Ridged noise exists to make crests. Hills are the opposite — rounded, soft-shouldered, no sharp
 * ridgelines. The blobby look that makes plain fBm wrong for mountains is exactly right here; this biome
 * is essentially baseline with the amplitude turned up and the wavelength brought in.
 *
 * ## Why the wavelength matters more than the height
 *
 * Amplitude decides how big a hill is; wavelength decides how *often* one arrives, and that is what
 * makes terrain read as rolling. At `CRUISE_SPEED` 260 a 2200-unit wavelength puts a crest under the
 * aircraft roughly every 8 seconds — a rhythm. Baseline's 3800 is nearly 15 seconds, which reads as one
 * long swell rather than a series of hills.
 */
export const ROLLING_HILLS = {
  /**
   * Height of the tallest hill, in world units.
   *
   * Between forest's 320 and the mountains' 900. High enough that cresting one hides what is beyond it —
   * which is where the interest comes from — and low enough that it never competes with a real range.
   */
  MAX_ELEVATION: 470,

  /**
   * Distance from one hill crest to the next, in world units. **The main dial for this biome.**
   *
   * Shorter gives a busier, choppier landscape; longer flattens it back toward baseline's single swell.
   */
  WAVELENGTH: 2200,

  /**
   * Octaves. Few, deliberately: hills should be smooth. Extra octaves put rocky detail on slopes that
   * are supposed to read as grassy, and this biome's whole job is to be soft.
   */
  OCTAVES: 3,

  /** Amplitude falloff per octave. High, so what detail exists stays gentle. */
  PERSISTENCE: 0.42,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /**
   * Exponent applied to the normalised field before scaling to elevation.
   *
   * Slightly below 1, which lifts the mid-range: it makes the ground spend more of its time on a slope
   * and less sitting in a flat valley floor. At 1 the hills read as isolated bumps in flat land.
   */
  CREST_SHAPE: 0.85,

  /**
   * Thumb on the scale for how much of the world is hills.
   *
   * Neutral, like mountains and forest: hills compete on equal terms and take whatever share their noise
   * field earns.
   *
   * ## Why adding this biome needed no rebalancing
   *
   * Every previous biome forced `BASELINE.SUITABILITY` up, because each one ate into the open ground that
   * gives the world its contrast. Hills do not, because **hills are themselves open ground.** Measured
   * across 40,000 samples of a 900,000-unit square:
   *
   * | | four biomes | with hills |
   * |---|---|---|
   * | baseline | 27.8% | 17.3% |
   * | hills | — | 9.7% |
   * | **open ground total** | **27.8%** | **27.1%** |
   * | mountains | 12.1% | 10.2% |
   * | forest | 12.0% | 10.1% |
   * | city | 9.5% | 7.8% |
   * | transitional | 38.6% | 44.8% |
   *
   * So the restful share of the world is essentially unchanged; what shifted is which *flavour* of
   * restful. That is the reason `BASELINE.SUITABILITY` stayed at 0.62 — the rule is that adding a
   * *special* region requires rebalancing, not that adding any biome does.
   *
   * Raising this makes hills common at the expense of flat ground and every special region; a negative
   * value makes them a rarity. The response is steep, so move it in hundredths.
   */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Biome: mesa country — flat tops and cliff faces
// ---------------------------------------------------------------------------

/**
 * Monument Valley and Arches: flat-topped tables of rock with near-vertical sides.
 *
 * ## Why this cannot be made from smooth noise
 *
 * Every other terrain biome is fBm, which is smooth by construction — that is why mountains have crests and
 * hills have shoulders. A mesa has neither. It has a **flat top** and a **cliff**, and no amount of tuning a
 * smooth field produces either.
 *
 * The answer is to **quantise** the field: sample smooth noise, then snap the result to discrete levels. A
 * plateau is then everywhere the field falls in the same band, and a cliff is the boundary between bands. It
 * costs one noise sample and a floor division, which makes this one of the cheapest biomes in the world despite
 * being the most structurally distinct.
 *
 * ## How a cliff survives being drawn
 *
 * Terrain is sampled on a lattice and drawn as a bilinear surface, so a mathematically vertical cliff becomes a
 * ramp one lattice cell wide. At roughly 50 units per cell against a 120-unit step, that is a slope of about
 * 2.4 — steeper than the mountains' 90th percentile, and it reads as a cliff face from the air. The lattice is
 * the limit on how sharp any terrain feature can be, and this sits right at it.
 *
 * ## Why buttes appear for free
 *
 * A butte is an eroded remnant of a mesa, and that is exactly what quantising produces where the field only
 * just crosses a threshold: a small isolated patch at the higher level. Broad crossings give wide mesas, narrow
 * ones give buttes, and nothing special is needed to distinguish them.
 *
 * ## A bonus from the palette
 *
 * Colour is interpolated along normalised elevation, so a quantised height field produces **banded colour** —
 * each terrace a distinct shade. That reads as sedimentary strata, which is precisely right for this landscape
 * and came free.
 */
export const MESA = {
  /**
   * Height of one terrace step, in world units. **The main dial for this biome.**
   *
   * This is the height of a cliff. Larger means more dramatic drops but a coarser landscape, since the same
   * total elevation is divided into fewer bands. Below about 60 the steps stop reading as cliffs and the
   * terrain looks merely lumpy.
   */
  STEP_HEIGHT: 120,

  /**
   * Number of terrace levels, including the desert floor at level zero.
   *
   * Four gives a floor plus three tiers, which is enough for a mesa to stand above a bench that stands above
   * the flats. More tiers makes the landscape read as a staircase rather than as isolated tables.
   */
  LEVELS: 4,

  /**
   * Exponent applied to the field before quantising. **Controls how much of the land is high ground.**
   *
   * Above 1 pushes most of the field into the lowest band, so the desert floor dominates and the upper tiers
   * are isolated. That is what makes mesas *landmarks* rather than a terraced hillside. At 1 the levels are
   * evenly distributed and the whole biome becomes a staircase.
   *
   * **The response is very steep, so move this in tenths.** At 2.8 the measured split was 75% floor, 24% first
   * bench and 1.6% second, with the top tier never occurring at all.
   */
  RARITY: 2.1,

  /**
   * The band of the raw noise field that is stretched across the terraces.
   *
   * ## Why this is needed
   *
   * **fBm never approaches its own extremes.** Measured over 120,000 samples, this field runs from 0.20 at the
   * 1st percentile to 0.80 at the 99th, clustered around 0.50 — it uses about 60% of its nominal range. Quantising
   * the raw value therefore put the top terrace above a height the noise never produces, so it did not exist: the
   * measured split was 50% floor, 42% first bench, 8% second and **0.1% third**.
   *
   * This is the same trap the forest fell into, where a stand field ramped across its full nominal range gave 80
   * trees per tile instead of 377. Both fixes are the same idea: find the range the field actually occupies and
   * stretch that.
   *
   * Values outside the band clamp, which costs nothing — beyond the 1st and 99th percentiles the land is already
   * at the bottom or top terrace anyway.
   *
   * Derived from measurement rather than guessed. Re-measure if `OCTAVES` or `PERSISTENCE` change, because both
   * alter how tightly the field clusters.
   */
  FIELD_LOW: 0.2,
  FIELD_HIGH: 0.8,

  /**
   * Wavelength of the plateau field in world units: roughly the size of one mesa.
   *
   * Also has to stay comfortably above the terrain lattice spacing. A field that varies faster than the lattice
   * can resolve produces single-cell spikes — a 50-unit-wide, 120-unit-tall needle, which reads as a bug.
   */
  WAVELENGTH: 1900,

  /**
   * Octaves for the plateau field.
   *
   * These shape the *outline* of a plateau rather than its height, since height is quantised away. More octaves
   * give lobed, intricate mesa edges with side canyons; too many and the finest octave starts flipping
   * individual lattice cells between levels.
   */
  OCTAVES: 4,

  /** Amplitude falloff per octave. Low, so the fine octaves shape edges without destabilising the level. */
  PERSISTENCE: 0.42,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /**
   * Amplitude of the gentle undulation added on top of the terraces, in world units.
   *
   * Without it the desert floor is a mathematically flat plane, which is exactly the "large uniform expanse"
   * the steering file forbids — and a flat plane in wireframe has no distance cue at all.
   *
   * Small enough that a mesa top still reads as flat — well under `STEP_HEIGHT`, so a terrace is never in doubt.
   * Real mesa tops are not perfectly level either, so a little roll is more honest than a dead plane.
   *
   * **Raised from 26 after measuring a 23-second dead-flat run**, which is precisely the uniform expanse the
   * steering file forbids. Amplitude alone was not the problem: the wavelength was so long that the floor's
   * slope never rose above 0.04, so it read as a plane regardless.
   */
  FLOOR_RELIEF: 42,

  /**
   * Wavelength of that undulation, in world units.
   *
   * Short enough that the floor visibly rolls rather than tilting imperceptibly, long enough never to compete
   * with the terraces for attention. This and `FLOOR_RELIEF` together set the floor's slope, and the slope is
   * what decides whether the ground reads as flat.
   */
  FLOOR_WAVELENGTH: 1500,

  /**
   * Thumb on the scale for how much of the world is mesa country.
   *
   * Neutral, like the other non-default biomes. This is dramatic terrain and it will want watching — if it
   * turns out to dominate, this is the first number to lower.
   */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Rock columns — spires and hoodoos, shared by mesa and canyon country
// ---------------------------------------------------------------------------

/**
 * Standing columns of rock: tapering spires and cap-topped hoodoos.
 *
 * ## Why these have to be structures and not terrain
 *
 * Everything else in this rock family comes from the heightfield, but a heightfield cannot represent a column. Terrain
 * is sampled on a lattice about 48 units apart and drawn as a bilinear surface, so the narrowest thing it can express
 * is roughly one cell wide — and a spire is *narrower than that*, with sides that overhang. A hoodoo's caprock is
 * wider than the shaft beneath it, which a heightfield cannot represent at any resolution: a height field has one
 * height per coordinate by definition.
 *
 * So these are placed structures, like trees and towers, and they inherit that machinery for free — slot placement,
 * slope rejection, depth masks, and the cross-biome clearance rule.
 *
 * ## Why one module serves two biomes
 *
 * Mesa country and canyon country are the same geology, and columns belong in both. A shared generator called from
 * each biome's `createStructures` keeps one authoritative placement rule rather than two that drift.
 *
 * ## Two profiles, one shape function
 *
 * A **spire** tapers from a broad base to a near point. A **hoodoo** has a slender shaft carrying a *caprock* wider
 * than the shaft — the harder stratum that protected the column while everything around it eroded away. That
 * overhang is the entire silhouette of a hoodoo, and it is why the two profiles cannot just be one shape at
 * different scales.
 */
export const ROCK_COLUMNS = {
  /**
   * Spacing of the slot grid columns are placed on, in world units.
   *
   * The same mechanism as forest trees: one candidate per slot, so two columns can never occupy the same ground and
   * no collision check is needed anywhere.
   */
  SPACING: 190,

  /**
   * Chance a slot inside a dense group is filled.
   *
   * Below 1 so even a thick field of hoodoos has gaps. A fully packed grid reads as a fence.
   */
  DENSITY: 0.62,

  /**
   * Wavelength of the field that decides where groups form, in world units.
   *
   * Columns come in fields, not evenly sprinkled: Bryce is a dense forest of hoodoos, and Monument Valley has
   * isolated towers in empty desert. A separate medium-frequency field gives both, exactly as the forest's stand
   * field does — and for the same reason, since a uniform sprinkle is the failure mode in both cases.
   */
  GROUP_WAVELENGTH: 1400,

  /** Octaves for the group field. */
  GROUP_OCTAVES: 2,

  /**
   * Group field value above which columns appear at all.
   *
   * High, because columns should be a discovery rather than ground cover. Below this nothing stands, which is what
   * makes the empty desert between fields genuinely empty.
   */
  GROUP_THRESHOLD: 0.55,

  /**
   * Range over which group strength ramps from nothing to full, above the threshold.
   *
   * **Narrow on purpose, and this is a trap this project has hit twice.** fBm never approaches its own extremes, so
   * ramping across the whole range above a threshold leaves strength near zero everywhere and produces a thin
   * scatter instead of fields. The forest needed exactly this fix, and so did the mesa's terracing.
   */
  GROUP_RAMP: 0.13,

  /** Shortest and tallest column, in world units. Tall enough to read from the air, short of a mesa's cliff. */
  MIN_HEIGHT: 70,
  MAX_HEIGHT: 210,

  /**
   * Exponent on the height roll. Above 1 makes tall columns rare, so a field has a few standouts rather than a
   * uniform palisade.
   */
  HEIGHT_SKEW: 1.9,

  /**
   * Base radius as a fraction of height. **Small: these are columns, not cones.**
   *
   * A rock column that is as wide as it is tall is a boulder. Around a fifth reads as a spire from the air.
   */
  RADIUS_RATIO: 0.19,

  /**
   * Hard cap on base radius as a fraction of `SPACING`, whatever the height suggests.
   *
   * Guarantees two neighbouring columns can never touch — the same structural guarantee the city gets from street
   * width and the forest from crown spacing, rather than a collision check that can be subtly wrong.
   */
  MAX_RADIUS_FRACTION: 0.3,

  /** Sides of the polygon each column is drawn with. Five reads as faceted rock and stays cheap. */
  SIDES: 5,

  /** Fraction of columns that are hoodoos rather than spires. The rest taper to a point. */
  HOODOO_CHANCE: 0.42,

  /**
   * Steepest ground a column may stand on, as rise over run.
   *
   * More permissive than a building — a column needs only a small footing — but far from a cliff face. Columns stand
   * on the desert floor and on benches, not glued to a wall.
   */
  MAX_GROUND_SLOPE: 0.5,

  /**
   * Clear space a column needs from another biome's structures, in world units.
   *
   * Consulted through the same reservation the forest and farmland use, so a spire never grows through a tower.
   */
  CLEARANCE: 6,
};

// ---------------------------------------------------------------------------
// Natural rock arches — shared by mesa and canyon country
// ---------------------------------------------------------------------------

/**
 * Freestanding arches: two legs and a span with a hole through the middle. Delicate Arch, Rainbow Bridge.
 *
 * ## Why these are worth the effort
 *
 * A hole is the one shape wireframe renders better than solid graphics do. Everything else in this world is a
 * silhouette against fog; an arch is a silhouette with sky *inside* it, which nothing else here has.
 *
 * ## The same ring stack as a hoodoo, on a curved path
 *
 * A column threads polygon rings up a vertical line. An arch threads them along a curve. That is the only
 * difference, so the emitters and the depth mask are shared and this is a cheap feature for how distinctive it is.
 *
 * ## Landmarks, not ground cover
 *
 * Deliberately rare. A hoodoo field is texture; an arch is a thing you fly *through*. The density here is set so
 * one turns up every few tiles of rock country rather than every tile — if they become common they stop reading
 * as remarkable and start reading as a repeated prop.
 */
export const ARCHES = {
  /**
   * Spacing of the slot grid arches are placed on, in world units.
   *
   * Far coarser than the column grid, which is what makes an arch rare and also guarantees two can never touch
   * without any collision check.
   */
  SPACING: 1100,

  /**
   * Chance an eligible slot actually holds an arch, before biome weight is applied.
   *
   * The main rarity dial. Raise it if arches feel too hard to find, lower it if they stop feeling like landmarks.
   *
   * Set with the *measured* survival rate in mind, not the nominal one. Mesa terrain rejects over half of what
   * this offers because its terraces break up the ground, so the number here is well above what actually gets
   * built. Even at this setting a region is around thirty times sparser in arches than Arches National Park.
   */
  DENSITY: 0.42,

  /**
   * Span from one foot's centreline to the other, in world units.
   *
   * The largest is wider than a city block. An arch has to be big enough to fly through comfortably, or it reads
   * as a boulder with a hole and the one shape that makes it worth building is wasted.
   */
  MIN_SPAN: 200,
  MAX_SPAN: 420,

  /**
   * Height of the top of the rock, as a fraction of span.
   *
   * Below 1 in every case, so an arch is always wider than it is tall — which is what makes it read as a span
   * rather than as a doorway.
   */
  MIN_HEIGHT_RATIO: 0.55,
  MAX_HEIGHT_RATIO: 0.9,

  /**
   * Superellipse exponent for the curve: `|x/a|^n + |y/b|^n = 1`.
   *
   * **The single dial that decides whether this reads as an arch or a bridge.** At 2 it is a plain semi-ellipse,
   * which looks like infrastructure. Above 2 the legs stand up and the crown broadens, which is what erosion
   * actually leaves. Much above 4 it becomes a squared doorway.
   */
  SHOULDER_EXPONENT: 3.2,

  /** Cross-section radius at the crown, as a fraction of span. The thinnest part of the arch. */
  CROWN_THICKNESS_RATIO: 0.085,

  /**
   * How much thicker a leg is than the crown.
   *
   * Erosion thins the span, not the footings, so a uniform tube reads as pipework rather than rock.
   */
  LEG_THICKENING: 1.7,

  /**
   * How quickly thickness grows from crown to foot. Above 1 keeps the span slender for most of its length and
   * flares only near the ground.
   */
  LEG_TAPER_POWER: 1.8,

  /**
   * Rings along the curve.
   *
   * Enough that the shoulders read as a turn rather than a bevel. The legs are nearly straight and need very few,
   * which the angle parametrisation already accounts for.
   */
  STOPS: 13,

  /** Polygon sides of the cross-section. Five, matching the columns, so the two read as the same rock. */
  SIDES: 5,

  /**
   * How many orientations are tried before the flattest is kept.
   *
   * **Not a cosmetic dial.** Mesa country is quantised terrain, so its ground is either level or a cliff, and a
   * 250-unit span dropped at a random angle straddles a terrace edge most of the time — measured, only 22% of
   * candidates survived on a single rolled heading. Trying a few and keeping the one that fits turns that from a
   * rejection into a choice, and it aligns arches along terraces and canyon rims, which is what a rock fin does.
   *
   * Headings are spread over half a turn, because an arch is symmetric: heading and heading plus a half turn
   * describe the same arch with its feet swapped.
   */
  HEADING_ATTEMPTS: 5,

  /**
   * How far the feet are pushed below the sampled ground, in world units.
   *
   * Terrain between the two feet is never perfectly level, so without this one foot can end up visibly floating.
   * Cheaper and more robust than trying to bend the arch to the ground.
   */
  FOOT_SINK: 8,

  /**
   * Steepest ground an arch may stand on, as rise over run.
   *
   * Tighter than a rock column's limit, because an arch is hundreds of units across and has to plant two feet
   * rather than one. Comfortably below a mesa cliff or a canyon wall.
   */
  MAX_GROUND_SLOPE: 0.35,

  /**
   * Largest height difference tolerated across the three ground samples, in world units.
   *
   * A separate limit from slope, because slope is local: both feet can sit on gentle ground and still be a
   * terrace apart. That case has to be rejected or an arch straddles a bench with one leg in mid-air.
   */
  MAX_FOOT_SPREAD: 45,

  /**
   * Clear space an arch needs from other structures, in world units.
   *
   * Applies both ways: an arch keeps clear of another biome's buildings, and rock columns keep clear of arches.
   * A spire growing through a span would ruin the one thing an arch has, which is a clean hole.
   *
   * Note that the ground an arch reserves against rock columns is a *circle* of its own span, not a thin capsule
   * along the span. Deliberately generous: the heading is chosen from the terrain, so it is not known until after
   * the ground is sampled, and a footprint that depends on it could not be evaluated at an arbitrary coordinate.
   * The cost is a small clearing around each arch, which does a landmark no harm.
   */
  CLEARANCE: 14,
};

// ---------------------------------------------------------------------------
// Biome: canyon country — a high plateau cut by winding gorges
// ---------------------------------------------------------------------------

/**
 * Deep branching gorges incised into high tableland.
 *
 * ## The generative trick
 *
 * A canyon is a *line* feature — a winding, branching channel — and lines are hard to get out of noise. Thresholded
 * noise gives blobs, not channels.
 *
 * The answer is the **absolute value** of a smooth field. `abs(noise)` is near zero along the field's
 * zero-crossing contours, and the zero set of a 2D noise field is a family of winding, branching, continuous
 * curves. Treating "close to zero" as "inside a canyon" therefore yields a natural drainage network for the price
 * of one noise sample — no path tracing, no accumulated state, and evaluable at any coordinate independently,
 * which is what determinism requires.
 *
 * Channel width falls out of the same maths: width is the threshold divided by the local gradient, so the network
 * naturally narrows where the field changes quickly and opens into basins where it is flat.
 *
 * ## Cut down from a raised rim, never below zero
 *
 * The rim sits high and the canyon is subtracted from it, so the floor stays above zero. Terrain that goes negative
 * is possible but costs a palette change, and it would drag neighbouring biomes down through zero across a
 * transition thousands of units wide. Raising the rim is free.
 *
 * ## Terraced walls
 *
 * Depth is quantised in steps, exactly as `MESA` quantises height, so walls come out benched rather than as smooth
 * funnels. That is what canyon walls actually look like, and it makes canyon and mesa country read as related.
 */
export const CANYON = {
  /**
   * Height of the plateau the canyons are cut into, in world units.
   *
   * Has to exceed `DEPTH`, or the floor would want to go below zero and get clamped flat — which shows up as
   * unexplained flat-bottomed canyons all at the same height.
   */
  RIM_HEIGHT: 520,

  /**
   * Depth of a canyon at its centre line, in world units. **The main dial for this biome.**
   *
   * Nearly the full rim height, so flying into one is a real descent. Keep it below `RIM_HEIGHT` with margin to
   * spare.
   *
   * Deepened along with the widening below. A canyon narrower than it is deep is a *slot* canyon, and at 280 wide
   * against 320 deep the first version read, accurately, as a crack in the ground.
   */
  DEPTH: 430,

  /**
   * Wavelength of the channel field in world units: roughly twice the spacing between major gorges.
   *
   * **This is the size dial, and it is not obvious.** Channel width in world units is the threshold divided by the
   * local gradient, and gradient scales as 1/wavelength — so width scales *with* wavelength. Tripling this triples
   * how wide a canyon is **and** how far apart they sit, which leaves the incised share of the land unchanged.
   *
   * Widening via `CHANNEL_WIDTH` instead would have tripled the width without moving the spacing, and swallowed the
   * plateau: 0.17 already put 34% of the land inside a canyon against 25% at 0.12.
   *
   * Tripled from 3,000 after flying it. Canyons now run about 840 units across, roughly three seconds of flight,
   * with a few thousand units of tableland between them.
   */
  CHANNEL_WAVELENGTH: 9000,

  /**
   * Octaves for the channel field. More gives a branchier, more fractal drainage network.
   *
   * Raised to four alongside the wavelength: at 9,000 units with three octaves the finest detail was 2,000 units
   * across and the network came out smooth and featureless. A fourth octave brings side branches back without
   * getting anywhere near the terrain lattice.
   */
  CHANNEL_OCTAVES: 4,

  /** Amplitude falloff per octave. Higher makes side channels more pronounced relative to the main gorge. */
  CHANNEL_PERSISTENCE: 0.45,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /**
   * How close to the field's zero crossing counts as being in a canyon.
   *
   * **This is the width dial.** Not a distance: it is a threshold on the field value, and the world-space width
   * that produces depends on the local gradient. Larger values give broader gorges everywhere and, past about 0.3,
   * merge the network into open badlands with isolated remnants.
   *
   * Measured: 0.17 put 34% of the land inside a canyon, which reads as dissected badlands rather than as plateau
   * with gorges cut through it. Lowered so the plateau clearly dominates and a canyon is something you come across.
   */
  CHANNEL_WIDTH: 0.12,

  /**
   * Exponent shaping the wall profile from floor to rim.
   *
   * Above 1 holds the floor near full depth and then climbs steeply, which gives a flat-bottomed canyon with steep
   * walls. At 1 the cross-section is a plain V. Below 1 it becomes a funnel with no floor at all.
   */
  WALL_SHARPNESS: 2.6,

  /**
   * Height of one bench on a canyon wall, in world units.
   *
   * Quantising depth the way `MESA` quantises height, so walls are stepped rather than smooth. Zero would give
   * clean funnels; too large and the canyon becomes a few enormous steps.
   */
  STEP_HEIGHT: 80,

  /**
   * Amplitude of the gentle undulation on the plateau surface, in world units.
   *
   * The rim between canyons is otherwise a flat plane, which is the uniform expanse the steering file forbids — the
   * same trap the mesa floor fell into.
   */
  RIM_RELIEF: 40,

  /** Wavelength of that undulation, in world units. Short enough that the rim visibly rolls. */
  RIM_WAVELENGTH: 1700,

  /**
   * Thumb on the scale for how much of the world is canyon country.
   *
   * Neutral, like the other non-default biomes. This is the seventh non-default biome, so the world is getting
   * crowded — if regions start feeling too small, this and `MESA.SUITABILITY_BIAS` are the first to lower.
   */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Biome: badlands — soft rock dissected into a maze of small sharp ridges
// ---------------------------------------------------------------------------

/**
 * Dense rills, gullies and knife-edge ridgelines. Badlands National Park, Zabriskie Point.
 *
 * ## Its own biome, and the reason is the base elevation
 *
 * The test that separated canyon from mesa applies again: does a feature need its own *base* elevation, or does it
 * only modify one? Mesa's base is a quantised staircase, canyon's is a raised rim cut down through. Badlands is
 * neither — it is ridged relief at high frequency — so it cannot be expressed as a tweak to either. Folded into
 * mesa it would have to fight the quantisation instead of replacing it, and what came out would be a *rough mesa*,
 * which is not badlands.
 *
 * Being independent is also honest: the Dakota badlands are surrounded by prairie, not tableland, so badlands
 * bordering open ground is correct rather than a compromise.
 *
 * ## Ridged noise, which is the same trick canyons use, inverted
 *
 * `1 - |noise|` is near its maximum along the field's zero-crossing contours — the same winding, branching curve
 * family that gives canyons their channels. Ridged noise therefore lays *ridges* along those curves, and stacking
 * a few octaves superimposes three scales of them. That is what a dissected landscape is.
 *
 * ## What makes it read as badlands rather than as small mountains
 *
 * **Not steepness — spread of steepness.** Measured on the drawn lattice, badlands has a median slope of 0.85
 * where mountains have 1.02, yet its 90th and 99th percentiles are *lower* (1.58 and 2.27 against 2.30 and 3.72).
 * So it is almost as steep as a mountain range on average while never reaching a mountain crest or a canyon wall.
 * Every other biome is the opposite shape: mesa's median slope is 0.04 with a 90th percentile of 2.41 — level
 * ground punctuated by cliffs. Badlands is the only terrain in the world that is steep nearly everywhere.
 *
 * Height is what separates it from mountains: a quarter of their scale, at a fifth of their wavelength.
 *
 * ## Banded colour comes free, again
 *
 * Colour interpolates along elevation normalised against the biomes present, and badlands packs its full height
 * range into every ridge. So the whole gradient repeats across each crest, which reads as exposed strata — the
 * same accident that made mesa terraces band, arriving for a different reason.
 */
export const BADLANDS = {
  /**
   * Height of a crest above the gully floor, in world units. **The main dial for this biome.**
   *
   * Deliberately a quarter of the mountains' scale. Badlands are small features seen close up; at mountain height
   * the gully network stops reading as erosion and becomes a range.
   *
   * This and `WAVELENGTH` together set the slope, and the slope is the biome's whole identity — raising this
   * without lengthening the wavelength makes the ground steeper everywhere.
   */
  CREST_HEIGHT: 250,

  /**
   * Wavelength of the coarsest ridge network, in world units.
   *
   * **Bounded from below by what the lattice can draw, and that bound is the binding one here.** Terrain is drawn
   * on a 50-unit lattice, so a ridge needs roughly four cells across it to read as a ridge rather than as a
   * single-cell zigzag. With three octaves the finest is this divided by about 4.3, which puts it at 257 units —
   * five cells, and only just safe. Shortening this is the fastest way to make the biome look like an artefact.
   *
   * Measured: the *drawn* crest spacing barely moves between a 760-unit and an 1,100-unit setting, because what
   * you actually see is limited by the lattice rather than by the field. So there is nothing to gain by pushing it
   * down, which is a happy answer — real badlands are denser than this and the lattice simply cannot show it.
   */
  WAVELENGTH: 1100,

  /**
   * Octaves of ridged noise.
   *
   * Three gives three scales of dissection — the network, its side gullies, and their rills — which is what makes
   * the ground look eroded rather than merely bumpy. A fourth would put the finest octave under two lattice cells.
   */
  OCTAVES: 3,

  /** Amplitude falloff per octave. Even, so all three scales of gully are legible. */
  PERSISTENCE: 0.5,

  /** Frequency multiplier per octave. Not a round number, so octaves do not align into visible grids. */
  LACUNARITY: 2.07,

  /**
   * The band of the ridged field that is stretched across the full crest height.
   *
   * **The fBm-extremes trap, met for the fourth time in this project.** Measured over 160,000 samples this field
   * runs 0.10 at the 1st percentile to 0.85 at the 99th, clustered near 0.43. Used raw, crests topped out at 178
   * units of a configured 210 and the ground came out noticeably gentler than intended — median slope 0.69 rather
   * than 0.92.
   *
   * Milder than the correction the mesa's plain fBm needed (0.20 to 0.80), because folding at zero pushes a ridged
   * field closer to its own ends. Re-measure if `OCTAVES` or `PERSISTENCE` change.
   */
  FIELD_LOW: 0.1,
  FIELD_HIGH: 0.85,

  /**
   * Thumb on the scale for how much of the world is badlands.
   *
   * **Negative, unlike every other terrain biome, and deliberately so.** This is the third biome in the eroded
   * rock family, and three of nine regions being bare rock would make the world read as a desert with
   * interruptions. Held back so mesa and canyon stay the faces of that family and this stays the rarer one.
   */
  SUITABILITY_BIAS: -0.03,
};

// ---------------------------------------------------------------------------
// Biome: river valley — low country with water winding through the bottom of it
// ---------------------------------------------------------------------------

/**
 * Gentle land with low hills, cut by a meandering river.
 *
 * ## Why the river has to be carved and cannot be a flood
 *
 * The intuitive model is "put a water level across low ground and the low ground fills". That does not give a river.
 * The low regions of an fBm field are **blobs**, so a water level over rolling country produces scattered ponds — the
 * same trap recorded when canyons were built: thresholding a field gives blobs, not channels.
 *
 * So the channel is carved first, as a line feature, using the trick the canyon biome already proved: `abs(noise)` is
 * near zero along a field's **zero-crossing contours**, and the zero set of a 2D noise field is exactly a family of
 * winding, branching, continuous curves. Water then fills the cut. Branching comes free, which is what gives
 * tributaries — though every branch is the same width, since nothing here knows which one is the main stem.
 *
 * ## Three fields, and each does one job
 *
 * - a very long-wavelength **base**, which is the regional valley floor and the thing the water level follows
 * - a shorter **hill** field added on top, so the land has shape without becoming rolling hills
 * - the **channel** field, which cuts down to a level set by the base *alone*
 *
 * That last point is what makes it read as a valley: a real river's grade does not care how tall the hills it cuts
 * through are, so the channel floor is independent of the hill field. The visible effect is that the water is wider
 * where the land is low and narrower where a hill crowds it, with no code saying so.
 *
 * ## Why the water is shallow, and it is not an aesthetic choice
 *
 * `FLIGHT.MIN_GROUND_CLEARANCE` holds the aircraft 120 units above the ground and the camera 45. With the water
 * shallower than 45 units, **nothing can ever get underneath the surface** — so there is no need for the flight model
 * to know that water exists, and no chance of the camera surfacing under a river.
 */
export const RIVER = {
  /**
   * Height of the regional valley floor, in world units.
   *
   * Has to exceed `INCISION`, or the channel would want to cut below zero and get clamped flat — the same failure the
   * canyon's rim height guards against.
   */
  BASE_HEIGHT: 165,

  /** Amplitude and wavelength of the regional drift of that floor. Very long, so the water level moves slowly. */
  BASE_RELIEF: 55,
  BASE_WAVELENGTH: 5200,

  /**
   * Amplitude of the low hills laid over the base, in world units.
   *
   * **Deliberately modest.** This is meant to be low country — the point of the biome is the water, and hills tall
   * enough to compete with it would just be rolling hills with a stream in them. Rolling hills reaches 470; this
   * reaches a third of that.
   */
  HILL_RELIEF: 95,

  /** Wavelength of those hills. Short enough to give the ground shape between meanders. */
  HILL_WAVELENGTH: 1500,

  /** Octaves for the hill field, and their falloff. Few and soft: these are shoulders, not crests. */
  HILL_OCTAVES: 3,
  HILL_PERSISTENCE: 0.45,

  /**
   * Wavelength of the channel field, in world units.
   *
   * **Sets both how much a river meanders and how far apart rivers are, and it is the wrong dial for width.** The
   * canyon biome learned this the expensive way: channel width in world units is the threshold divided by the local
   * gradient, and gradient scales as 1/wavelength — so changing this scales the spacing and the width together.
   *
   * Measured, at 2,600 the water came out 80 units wide, a third of a second to cross, with another stream every four
   * seconds. That is a marsh, not a river. Widening had to come from here and the narrowing from `CHANNEL_WIDTH`,
   * which is what keeps a wide river *and* a long dry gap between rivers.
   */
  CHANNEL_WAVELENGTH: 9100,

  /** Octaves for the channel field, and their falloff. More octaves give a more sinuous, more branched network. */
  CHANNEL_OCTAVES: 3,
  CHANNEL_PERSISTENCE: 0.5,

  /** Frequency multiplier per octave, shared by all three fields. Not a round number, so octaves do not align. */
  LACUNARITY: 2.11,

  /**
   * Field value below which a coordinate is inside the valley, as a fraction.
   *
   * This is the *valley* width, not the water's. The water occupies only the part of the valley floor that ends up
   * below the water level, so it is always narrower than this and varies with the surrounding land.
   *
   * **This is the dial that decides how much of the biome is valley at all**, roughly in proportion, where the
   * wavelength decides how *big* each one is. Lowered alongside a much longer wavelength: together they give a wide
   * river with long dry country between rivers, where either alone gives a wide marsh or a narrow stream. Measured,
   * 13% of the biome is valley and 9% of it is water.
   */
  CHANNEL_WIDTH: 0.055,

  /**
   * How far the channel cuts below the base, in world units.
   *
   * The water fills the bottom `INCISION - WATER_DROP` of it, so the rest is dry bank — a floodplain, which is what
   * keeps the waterline off the valley wall.
   *
   * **Shallow, and this is what makes the valley gentle rather than the bank exponent below.** A shallow cut spread
   * across a wide valley gives a 90th-percentile slope of 0.32 — flatter than rolling hills — even with a
   * flat-bottomed profile.
   */
  INCISION: 78,

  /**
   * Exponent shaping the valley's cross-section, from floor to bank.
   *
   * Above 1 holds the floor near full depth before rising, giving a flat-bottomed valley.
   *
   * **High, and the reason is not the shape of the land — it is the width of the water.** The water covers the part of
   * the floor lying below the water level, so a flatter floor is a wider river: at 1.5 the water was 37% of the valley
   * half-width, and at 3 it is 67%. That was the difference between a stream and a river. The gentleness of the valley
   * comes from `INCISION` being shallow instead, which is a better place for it — the two are separable and it took a
   * sweep to see that.
   */
  BANK_SHARPNESS: 3,

  /**
   * How far the water level sits below the regional base, in world units.
   *
   * Together with `INCISION` this sets the depth of the water, and that difference has to stay under
   * `CAMERA.MIN_GROUND_CLEARANCE` of 45 so nothing can ever pass beneath the surface. Currently 38, measured at 38 at
   * its deepest — **a test pins this**, because the day it exceeds 45 the camera can surface underneath a river and the
   * flight model would have to learn what water is.
   */
  WATER_DROP: 40,

  /**
   * Spacing of the world-aligned grid the water surface is drawn on, in world units.
   *
   * **World-aligned, not tile-relative, which is the whole reason it works.** A river crosses tiles, so the surface
   * has to be drawn from a lattice that does not know about tiles, or every seam would show a jog. Coarser than the
   * terrain lattice because a water surface needs no fine detail, and every step down costs samples quadratically.
   */
  WATER_GRID: 70,

  /**
   * Thumb on the scale for how much of the world is river valley.
   *
   * Neutral. This is open, restful country with a feature in it rather than a dramatic region, so it does not carry
   * the rebalancing cost that mesa and canyon did.
   */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Airfield — an installation on open ground
// ---------------------------------------------------------------------------

/**
 * A rural airfield: one control tower, up to three runways, and rarely an aircraft sitting on one.
 *
 * ## Thematically the best fit in the whole register, and it needed the hardest ground
 *
 * A runway has to be dead level over roughly a thousand units. No terrain in this world provides that: even baseline, the
 * flattest biome, rolls by tens of units over that distance.
 *
 * ## Which is why the runway is on an embankment, and the ground is untouched
 *
 * The obvious answer is to grade a pad — and it is the wrong one. Flattening terrain under a structure inverts the rule
 * that structures sample terrain and never the reverse, and it would not even work: blended elevation is a weighted sum,
 * so a pad at 70% biome weight comes out 70% flat plus 30% of whatever the neighbour is doing, which tilts the runway
 * anyway.
 *
 * So the runway sits at one constant height and **embankment lines drop from its edges to the real ground**, which is what
 * a runway crossing uneven ground actually has. The terrain is not touched, the surface is genuinely level, and the site
 * only needs a loose flatness check — enough to stop the embankment becoming a viaduct.
 *
 * ## Why an installation on baseline
 *
 * By the usual test it needs *level* ground rather than a new kind of ground, so it is an installation. Baseline hosts it
 * because it is the flattest biome, because it has no structures of its own so nothing competes, and because open country
 * is where airstrips are. One host only: two independent site grids could put two airfields on top of each other across a
 * transition, which is the hazard the telescope arrays avoided the same way.
 */
export const AIRFIELD = {
  /**
   * Spacing of the slot grid sites are placed on, in world units.
   *
   * Coarser than the telescope arrays', which are themselves rare. An airfield is a large, unmistakable, man-made thing
   * and two of them in view would be absurd.
   */
  SLOT_SPACING: 5200,

  /** Chance an eligible slot is considered, before the ground checks. */
  DENSITY: 0.85,

  /** How far a site may be nudged from its slot centre, in world units. Small, for the reason the arrays' jitter is. */
  SITE_JITTER: 420,

  /**
   * Runway length range, in world units.
   *
   * At cruise a 900-unit runway takes about three and a half seconds to fly along: long enough to read as a runway rather
   * than a road, short enough that level ground for it can actually be found.
   *
   * **This is the dominant term in how rare airfields are**, because relief across the footprint grows with length and the
   * embankment budget is fixed. Measured on seed 1337, the share of candidate sites flat enough went 10% at 1,180 units,
   * 21% at 760 and 34% at 520. The range below is where a runway still looks like one and sites are not vanishingly rare.
   */
  MIN_RUNWAY: 620,
  MAX_RUNWAY: 920,

  /**
   * Runway width, in world units.
   *
   * The length-to-width ratio is what makes a runway read as a runway — around 30:1 here, which is close to the real
   * thing. Too wide and it becomes an apron; too narrow and it is a road.
   */
  RUNWAY_WIDTH: 34,

  /** Most runways an airfield may have. */
  MAX_RUNWAYS: 3,

  /**
   * Relative chance of one, two and three runways.
   *
   * A single strip is the common case so that three is worth seeing. Normalised on use, so these are weights rather than
   * probabilities and can be edited without having to make them sum to anything.
   */
  RUNWAY_COUNT_WEIGHTS: [36, 34, 30],

  /**
   * How far each runway after the first is shifted along its own heading, as a fraction of its length.
   *
   * Without this every runway passes through one point and the field comes out as an asterisk with all three centrelines
   * colliding in the middle. Offsetting them makes the classic triangle instead.
   */
  SECONDARY_OFFSET_RATIO: 0.22,

  /**
   * Height the runway surface sits above the highest ground beneath it, in world units.
   *
   * Small but not zero: at zero the surface lines would coincide exactly with the terrain wireframe at the field's high
   * point, and two lines in the same place read as one flickering line.
   */
  SURFACE_RISE: 6,

  /**
   * Least angle between any two runways, in radians.
   *
   * **Compared modulo half a turn, because a runway is bidirectional** — a heading and its opposite are the same strip of
   * ground. Without that the check passes for two runways lying exactly on top of each other. 35 degrees is enough to read
   * as deliberately different directions, and it produces the triangular layout that wartime airfields actually have.
   */
  MIN_HEADING_SEPARATION: 0.61,

  /** Dashes along a runway's centreline. The single most legible thing about a runway from the air. */
  CENTRELINE_DASHES: 11,

  /** Share of each dash's slot that is actually drawn. Below 1 or the centreline becomes a solid stripe. */
  CENTRELINE_DASH_RATIO: 0.55,

  /** Bars across each threshold. Says "this is the end of a runway" in a way a plain edge cannot. */
  THRESHOLD_BARS: 3,

  /** How far the threshold bars reach back from the runway end, in world units. */
  THRESHOLD_DEPTH: 54,

  /**
   * Points along each runway edge where an embankment line drops to the ground.
   *
   * Their feet are also joined up, which is what makes the embankment read as a bank of earth rather than as a row of
   * loose whiskers hanging off the runway.
   */
  SKIRT_STEPS: 7,

  /**
   * How much shorter each runway is than the one before it, as a ratio.
   *
   * Real airfields have one main strip and shorter crosswind ones. Equal-length runways read as a decorative asterisk.
   *
   * It also decides how often a multi-runway field is possible at all. A crosswind runway needs its *own* flat footprint,
   * pointing across the contour the primary was aligned to, so the ground has to be level over the whole union — measured,
   * three runways fitted at only a tenth of the sites where one did. Shorter crosswind strips shrink that union.
   */
  SECONDARY_LENGTH_RATIO: 0.7,

  /**
   * Most relief tolerated across the ground a field covers, in world units — the flatness check for the whole site.
   *
   * The runway sits `SURFACE_RISE` above the highest ground it crosses, so the tallest bank actually drawn is this plus that
   * rise. Larger accepts more sites and makes some of them look like viaducts; smaller is more honest and much rarer.
   *
   * **Set from measurement, after the first guess landed below the tenth percentile.** Relief across a runway footprint on
   * baseline runs a p10 of 36 units and a p50 of 81, so the original 34 was rejecting over 90% of otherwise good sites —
   * the same "fBm never approaches its extremes, so measure the occupied range" mistake that the forest stands, the mesa
   * terraces and the lake basins each made once. At 44 the bank is about 1.3 times the runway's width, which still reads
   * as earthworks rather than as a viaduct.
   */
  MAX_EMBANKMENT: 44,

  /**
   * Steepest ground the site centre may sit on, as rise over run.
   *
   * **A cheap rejection, not the real flatness test.** Relief measured across the actual footprint is a strictly better
   * answer to "is this flat enough", and it subsumes this — any site with a steep centre fails it anyway. So this exists
   * only to throw out the hopeless cases before paying for forty terrain samples, and it is set loose deliberately: at the
   * original 0.12 it rejected 56% of candidate slots that the footprint test would have judged on their merits, because
   * the median slope where baseline is present is 0.145. Tightening this buys nothing and costs airfields.
   */
  MAX_GROUND_SLOPE: 0.45,

  /** Control tower height above the runway surface, and the width of its shaft, in world units. */
  TOWER_HEIGHT: 92,
  TOWER_WIDTH: 20,

  /** How far the cab overhangs the shaft on each side, in world units. Gives the tower its unmistakable silhouette. */
  TOWER_CAB_OVERHANG: 9,

  /** Height of the cab itself, in world units. */
  TOWER_CAB_HEIGHT: 24,

  /** Bands around the tower shaft between its base and the cab. Gives the shaft scale against an empty sky. */
  TOWER_RINGS: 3,

  /** How far the tower stands off the primary runway's centreline, in world units. */
  TOWER_OFFSET: 96,

  /**
   * Positions along the primary runway the tower is tried at, as fractions of its length from the centre.
   *
   * A search order rather than a tuning value: a tower offset from the primary can still land on a *crossing* runway, so
   * candidates are tried in turn and the first one clear of every strip wins. Beside the middle of the field is where a
   * real tower stands, so the nearer stations come first.
   */
  TOWER_STATIONS: [0.3, -0.3, 0.46, -0.46],

  /** Space the tower keeps from the edge of any runway, in world units. */
  TOWER_CLEARANCE: 30,

  /** Chance an airfield has an aircraft sitting on its primary runway. Rare, so it is a small surprise when it happens. */
  AIRCRAFT_CHANCE: 0.25,

  /**
   * How far along the primary runway that aircraft may stand, as a fraction of the length either side of the middle.
   *
   * Kept well inside the thresholds so it is standing on the strip rather than hanging off the end of it.
   */
  AIRCRAFT_STATION_RANGE: 0.32,

  /** Wingspan and length of that parked aircraft, in world units. */
  AIRCRAFT_SPAN: 52,
  AIRCRAFT_LENGTH: 60,

  /** How high its body sits above the runway, in world units. Standing on its undercarriage rather than lying flat. */
  AIRCRAFT_RIDE_HEIGHT: 9,

  /** Where the wings attach, as a fraction back from the nose. */
  AIRCRAFT_WING_STATION: 0.42,

  /** Tailplane span as a fraction of the wingspan. */
  AIRCRAFT_TAIL_SPAN_RATIO: 0.38,

  /** Height of the fin above the body, in world units. The one thing that makes it read as an aircraft from the side. */
  AIRCRAFT_FIN_HEIGHT: 17,

  /**
   * Clear space the airfield needs from other structures, in world units, beyond its own reach.
   *
   * Published as a reservation so trees, towers, rock and farm structures all keep off the runways.
   */
  CLEARANCE: 50,
};

/**
 * Gradient for airfields.
 *
 * Fixed rather than taken from the host biome, the same exception bridges take and for the same reason: baseline's palette
 * is deliberately muted so that open ground recedes, and an airfield drawn in it would disappear. This is the second
 * unmistakably engineered thing in the world and it should look it.
 */
export const AIRFIELD_PALETTE = {
  /** Dark slate at the foot of the embankments and the base of the tower. */
  LOW: 0x2b3a4a,

  /** Cold near-white on the runway surface and the tower cab. Well over the bloom threshold, so the strips glow. */
  HIGH: 0xe8f4ff,
};

// ---------------------------------------------------------------------------
// Biome: industry — a working landscape at the same scale as the city
// ---------------------------------------------------------------------------

/**
 * An industrial region: block after block of cooling towers, tank farms, stacks and sheds, laced with
 * transmission lines running between substations.
 *
 * ## This began as an installation and that was the wrong shape
 *
 * The first version scattered self-contained works across open ground, a few hundred units across and rare. Flown,
 * it read as props dotted about rather than as anywhere — the ask was for **a region like the city**, where you
 * cross into it and everything around you for a minute is industry. So the parts survived unchanged and the
 * placement was thrown away: a block grid like the city's, at four to five times the size, filling a biome.
 *
 * Everything here is also **scaled to city height**. A cooling tower at 150 units was dwarfed by a 550-unit tower
 * block; at 340 it reads as the same class of object, which is what makes an industrial skyline sit beside a
 * downtown as an equal rather than as a model of one.
 *
 * ## The cooling tower is why this was cheap
 *
 * A hyperboloid is the one silhouette nothing else in this world has, and it is a *ring stack*: concentric
 * polygons threaded up a vertical line with a radius that narrows to a waist and flares again. The rock columns,
 * the natural arches and the telescope dishes already stack rings, so the emitter, the segment count and the
 * depth mask all existed. It needed a radius profile and nothing else.
 *
 * ## Substations are what stop the power lines going nowhere
 *
 * A transmission line that fades out mid-air reads as unfinished. So substations sit on a coarse lattice and a
 * line exists **only when the substations at both of its ends do** — which makes every line terminate at something
 * by construction, and makes the last substation at a region's edge simply have nothing leaving it.
 */
export const INDUSTRIAL = {
  /**
   * Highest ground this biome produces, in world units.
   *
   * Low, like the city's, and for the same reason: a rectangular grid of heavy plant on a hillside looks wrong,
   * so the biome selects flat ground by being flat.
   */
  MAX_ELEVATION: 155,

  /** Wavelength of that ground, in world units, plus its octaves and falloff. Long and gentle. */
  WAVELENGTH: 3000,
  OCTAVES: 3,
  PERSISTENCE: 0.45,
  LACUNARITY: 2.13,

  /** Thumb on the scale for how much of the world is industry. Neutral: it takes what its field earns. */
  SUITABILITY_BIAS: 0,

  /**
   * Edge of one block, in world units.
   *
   * **Far larger than the city's 220**, because the things standing in these blocks are far larger than a tower's
   * footprint: three cooling towers in a row span some 700 units, where a city tower is 150 across. This is the
   * number to move if the estate feels either cramped or empty.
   *
   * ## Why it is 700 and not 620
   *
   * Every group is laid out in the block's own *rotated* frame, so what has to fit inside the block is the group's
   * bounding **circle**, not its bounding box. A three-by-three tank farm at this spacing puts its corner tanks
   * 233 units out along the diagonal before their own radius is added — 305 in total, against the 255 a 620-unit
   * block leaves once the roadway is taken out. Measured, not guessed: two adjacent tank farms came within 10 units
   * of touching across a roadway that is supposed to be empty.
   *
   * 700 leaves 295, which every use now clears with room to spare. The cost is 22% fewer blocks in a tile, which
   * happens to put an industrial tile at 2,500-odd segments — the same as a city tile.
   */
  BLOCK_SIZE: 700,

  /** How much of a block is left as roadway around its contents, in world units. */
  ROAD_WIDTH: 110,

  /** Steepest ground a block will build on, as rise over run. Matches the city's, since both are grids of slabs. */
  MAX_GROUND_SLOPE: 0.3,

  /**
   * Relative chance of each use for a block that is not a substation or a line corridor.
   *
   * `yard` is open ground with nothing on it, and it is not padding: a wall-to-wall estate has no silhouette
   * because nothing has sky behind it. The gaps are what let the cooling towers read.
   */
  USE_WEIGHTS: {
    cooling: 22,
    tanks: 20,
    spheres: 12,
    stacks: 12,
    sheds: 16,
    pipes: 8,
    yard: 10,
  },

  // --- the transmission network -------------------------------------------

  /**
   * How many blocks apart the substations sit.
   *
   * Every block whose indices are both multiples of this holds a substation; every block on a row or column
   * between two of them is a line corridor. That lattice is what an industrial estate looks like from above, and
   * it costs two existence checks per block rather than any kind of routing.
   *
   * ## It decides how much of the estate is power network rather than plant
   *
   * A corridor block holds one pylon and nothing else, so this number sets the split directly: of every NxN cell, one
   * block is a substation and 2(N-1) are corridors. At 3 that is **five blocks in nine given over to the network**, and
   * measured across 24 tiles it came out at exactly that — 54 network blocks against 71 uses. Half an estate of pylons
   * standing on open ground is not what the cooling towers and tank farms were scaled up for.
   *
   * At 5 it is nine blocks in twenty-five, so a third, and the runs get longer: four pylons between substations instead
   * of two, which reads as a transmission line crossing the estate rather than as a pylon every other block.
   */
  SUBSTATION_EVERY: 5,

  /** Substation yard footprint and the height of its gantry, in world units. */
  SUBSTATION_SIZE: 300,
  SUBSTATION_GANTRY_HEIGHT: 120,

  /** Transformers in the yard, and their box dimensions in world units. */
  SUBSTATION_TRANSFORMERS: 3,
  TRANSFORMER_WIDTH: 46,
  TRANSFORMER_DEPTH: 34,
  TRANSFORMER_HEIGHT: 40,

  /** Insulator stacks on the gantry, which is what makes a fenced yard read as electrical. */
  SUBSTATION_INSULATORS: 5,
  INSULATOR_HEIGHT: 34,

  // --- cooling towers ------------------------------------------------------

  /** How many a cooling block gets. Two reads as a pair, three as a station; one reads as a mistake. */
  MIN_COOLING_TOWERS: 2,
  MAX_COOLING_TOWERS: 3,

  /**
   * Base radius and height of one, in world units.
   *
   * **Scaled up more than twofold when this became a biome.** At 150 tall it was a third the height of a city
   * tower and read as a model; at 340 against a 210-unit base radius the proportion matches a real one and it
   * stands as the landmark of the region.
   */
  COOLING_BASE_RADIUS: 105,
  COOLING_HEIGHT: 340,

  /**
   * The hyperboloid profile: where the waist is as a fraction of the height, how narrow it is, and how far the
   * rim flares back out above it.
   *
   * **The waist is the whole silhouette.** A straight cone or cylinder of the same size reads as a silo; the
   * pinch and the flare are what make it unmistakable, and they are the reason this shape was worth having.
   */
  COOLING_WAIST_AT: 0.76,
  COOLING_WAIST_RATIO: 0.6,
  COOLING_TOP_RATIO: 0.68,

  /** Rings up a cooling tower and sides around it. Enough that the curve reads as a curve. */
  COOLING_RINGS: 9,
  COOLING_SIDES: 14,

  /** How far apart a *pair* of cooling towers stands, centre to centre, in world units. */
  COOLING_SPACING: 230,

  /**
   * Radius of the circle three cooling towers stand on, in world units.
   *
   * **A triangle rather than a row, and it is a fit problem before it is a look one.** Three towers in a line span
   * their spacing twice plus a full diameter — around 670 units — which overruns a 620-unit block into the roadway and
   * the block beyond. On a circle the group's extent is this radius plus one tower's, which fits inside the block with
   * room to spare. It also happens to be what a real four-reactor station looks like from above.
   */
  COOLING_GROUP_RADIUS: 145,

  // --- chimneys ------------------------------------------------------------

  /**
   * A tall tapering stack. **The tallest thing in the biome**, and the only one taller than a cooling tower — which
   * is what gives an industrial skyline the same one-landmark-above-the-rest shape a city has.
   */
  CHIMNEY_HEIGHT: 470,
  CHIMNEY_BASE_RADIUS: 26,
  CHIMNEY_TOP_RATIO: 0.62,
  CHIMNEY_RINGS: 7,
  CHIMNEY_SIDES: 10,

  /** How many stacks a stack block gets, and how far apart they stand in world units. */
  MIN_STACKS: 1,
  MAX_STACKS: 2,
  STACK_SPACING: 150,

  // --- tanks ---------------------------------------------------------------

  /**
   * How many cylindrical tanks a tank farm holds, and their size range in world units.
   *
   * Wider than they are tall, unlike everything else here, which is what a real tank is and what makes a farm read
   * as the low mass in an estate full of verticals.
   *
   * The radius is capped where it is because a farm is the widest group in the biome: nine tanks on a three-by-three
   * grid puts the corner ones a diagonal out from the block centre, so the spacing and this together decide whether
   * the group fits inside its block once rotated. See `BLOCK_SIZE`.
   */
  MIN_TANKS: 6,
  MAX_TANKS: 9,
  MIN_TANK_RADIUS: 40,
  MAX_TANK_RADIUS: 62,
  TANK_HEIGHT_RATIO: 0.85,
  TANK_RINGS: 3,
  TANK_SIDES: 12,

  /**
   * Spacing of the grid tanks are laid out on, in world units. Regular, because a real tank farm is.
   *
   * Must stay above `MAX_TANK_RADIUS * 2` or the largest tanks intersect their neighbours, and low enough that the
   * corner of a three-by-three grid plus one radius clears the block — which is 295 units at the current block size.
   */
  TANK_SPACING: 160,

  /** Spherical pressure vessels, which nothing else in the world resembles. */
  MIN_SPHERES: 3,
  MAX_SPHERES: 4,
  SPHERE_RADIUS: 42,
  SPHERE_RINGS: 7,
  SPHERE_SIDES: 12,

  /** How high a sphere is held off the ground on its skirt, as a fraction of its radius. */
  SPHERE_LIFT: 0.55,

  /** How far apart spheres stand, centre to centre, in world units. */
  SPHERE_SPACING: 120,

  // --- sheds and pipe racks ------------------------------------------------

  /** How many long sheds a shed block gets, and their footprint in world units. */
  MIN_SHEDS: 2,
  MAX_SHEDS: 3,
  SHED_LENGTH: 330,
  SHED_WIDTH: 110,
  SHED_HEIGHT: 78,

  /** Ribs across a shed's roof. What makes a long box read as an industrial roof rather than a blank slab. */
  SHED_RIBS: 6,

  /** How far apart sheds stand, in world units. */
  SHED_SPACING: 40,

  /**
   * A pipe rack: parallel runs carried on trestles, low to the ground.
   *
   * The run length is bounded by the block, not by looks: a tank stands at each end of it, so the block has to hold
   * half the run plus two tank radii. See `BLOCK_SIZE`.
   */
  PIPE_RUN_LENGTH: 400,
  PIPE_HEIGHT: 26,
  PIPE_LINES: 3,
  PIPE_SPACING: 16,
  PIPE_TRESTLES: 8,

  // --- the transmission network -------------------------------------------

  /**
   * Pylon proportions, in world units. Tall and slender, with a wide splayed base.
   *
   * One pylon stands in the middle of every line corridor block, so the span between two of them is `BLOCK_SIZE`.
   * That is a shorter span relative to the tower height than a real line has, which reads as a busy estate rather
   * than as open country — the right answer here.
   */
  PYLON_HEIGHT: 210,
  PYLON_BASE_HALF: 38,
  PYLON_WAIST_HALF: 15,

  /** Where the body stops splaying, as a fraction of the height. Below this the legs spread out. */
  PYLON_WAIST_AT: 0.42,

  /** Crossarm heights as fractions of the pylon's height, and their half-span in world units. */
  PYLON_ARM_LEVELS: [0.62, 0.8, 0.96],
  PYLON_ARM_HALF: 58,

  /** How far the insulator hangs below a crossarm tip, in world units. */
  PYLON_INSULATOR_DROP: 15,

  /** Bracing panels up the body. Each is an X, which is what makes it read as a lattice rather than a post. */
  PYLON_BRACE_PANELS: 4,

  /**
   * Conductor levels actually strung, and the sag of a span as a fraction of its length.
   *
   * Two of the three crossarm levels carry cable, which is enough to read as a transmission line without trebling
   * the segment count. The sag is what makes them cables rather than wires stretched taut — the same parabola the
   * suspension bridge uses, and for the same reason.
   */
  CONDUCTOR_LEVELS: 2,
  CONDUCTOR_SAG_RATIO: 0.05,

  /** Segments each conductor span is drawn in. Enough for the curve to read; more is invisible. */
  CONDUCTOR_STEPS: 5,

  // --- the region as a whole ----------------------------------------------

  /** Clear space the works keeps from another biome's structures, in world units. */
  CLEARANCE: 40,

  /**
   * Probes per axis when asking whether another biome has reserved a block's ground.
   *
   * **Measured, not chosen.** A clearance test cannot be scaled up by growing its radius: the water clearance checks a
   * centre and a ring at whatever it is given and sees nothing in between. So a block is covered by many small
   * overlapping queries instead, and the only honest way to set their number is to measure the largest hole the layout
   * leaves. Over this block, worst hole in world units: 3x3 gives 80, 5x5 gives 42, 7x7 gives 24, 9x9 gives 21.
   *
   * Three was not enough and it showed — a substation stood with a corner in a lake, 83 units off its block centre.
   * Nine buys almost nothing over seven for two-thirds again as many queries. Lower this only with a fresh measurement.
   */
  CLEARANCE_PROBES: 7,
};

/**
 * Gradient for industrial structures.
 *
 * Declared as the biome's `structurePalette`, the same trick the forest uses for its trees and suburbia for its
 * roofs: the ground stays cold and dim so it recedes, and the plant carries the region's identity. Hot amber at the
 * top against the city's cold cyan, so a downtown and an industrial estate are never confused at fog distance even
 * though both are grids of tall things.
 */
export const INDUSTRIAL_PALETTE = {
  /** Deep steel blue at ground level. */
  LOW: 0x22303f,

  /** Hot amber at the tops of the towers and stacks. Over the bloom threshold, so the skyline glows. */
  HIGH: 0xffb163,
};

// ---------------------------------------------------------------------------
// Bridges — the one thing in the world that is unmistakably engineered
// ---------------------------------------------------------------------------

/**
 * Crossings over water: a shallow arch for a modest span, a suspension bridge for a wide one.
 *
 * ## Span decides the type, so there is no pool to tune
 *
 * A suspension bridge over a 250-unit stream would be silly and an arch over a 900-unit one impossible. Letting the span
 * choose is both what real engineering does and one fewer arbitrary weighting to balance — unlike the city's buildings,
 * which genuinely needed a weighted pool.
 *
 * ## The arch is deliberately shallow
 *
 * Nothing here should look like the rock arches in the badlands. Those are tall and round because erosion made them; a
 * bridge arch is flat because it carries a level road. `ARCH_RISE_RATIO` is the dial and it is set low on purpose.
 *
 * ## Placement needs no new machinery
 *
 * A bridge must sit across the water, square to it, and reach both banks. All three come from the **gradient of the water
 * depth** — depth rises toward the middle of a channel, so its gradient points across. See `world/bridges.js`.
 */
export const BRIDGE = {
  /**
   * Spacing of the slot grid crossings are considered on, in world units.
   *
   * Coarse. A bridge should be something you come upon, and there are only so many places a river is the right width for
   * one anyway — most candidates are rejected by the span limits below.
   */
  SLOT_SPACING: 1900,

  /** Chance an eligible slot is even considered, before the water search and the span limits. */
  DENSITY: 0.9,

  /** How far a candidate is nudged from its slot centre before the search starts, in world units. */
  SLOT_JITTER: 420,

  /**
   * Shortest and longest crossing that gets a bridge, in world units, abutment to abutment.
   *
   * The lower bound keeps bridges off trickles, where one would read as a plank. The upper bound is what a suspension
   * bridge can plausibly span, and it also rejects the case where the search has wandered into a lake.
   */
  MIN_SPAN: 240,
  MAX_SPAN: 920,

  /** Span at or above which a suspension bridge is used instead of an arch. */
  SUSPENSION_MIN_SPAN: 440,

  /** How far past the waterline each end lands, in world units, so a bridge sits on the bank rather than in the water. */
  ABUTMENT: 46,

  /** Width of the deck, in world units. Wide enough to read as a road, narrow enough to stay a line in the air. */
  DECK_WIDTH: 34,

  /** Points along the deck. Sets how many rungs it has, which is what makes it read as a deck rather than two lines. */
  DECK_STOPS: 13,

  /** Points along an arch rib. */
  ARCH_STOPS: 11,

  /**
   * Rise of the arch as a fraction of the span.
   *
   * **Low, and this is the number that keeps a bridge from looking like a rock formation.** The badlands arches rise to
   * most of their span; this is a sixth of it.
   */
  ARCH_RISE_RATIO: 0.16,

  /** Gap between the crown of the arch and the underside of the deck, in world units. Keeps the posts non-degenerate. */
  ARCH_DECK_GAP: 9,

  /** Where the towers stand, as a fraction along the span from each end. */
  TOWER_INSET: 0.18,

  /** Tower height above the deck, as a fraction of span. */
  TOWER_HEIGHT_RATIO: 0.22,

  /** How far the main cable sags below the tower tops at mid-span, as a fraction of tower height. */
  CABLE_SAG_RATIO: 0.62,

  /** Points along the sagging part of the main cable. Enough that it reads as a curve rather than a bend. */
  CABLE_STOPS: 11,

  /** Hangers from cable to deck, per side. Spaced strictly inside the towers so none collapses against one. */
  HANGERS: 7,

  /** Height of the deck above the higher bank, in world units. */
  BANK_RISE: 14,

  /**
   * Least clearance between the water and the deck, in world units.
   *
   * Also what stops a deck sitting *in* the river when both banks happen to be low.
   */
  MIN_WATER_CLEARANCE: 46,

  /**
   * Largest height difference tolerated between the two abutments, in world units.
   *
   * A bridge is level, so a crossing between mismatched banks is refused rather than tilted. Same reasoning as the
   * telescope array's site spread.
   */
  MAX_BANK_SPREAD: 40,

  /** Offset used to difference the depth field when finding which way is across the water, in world units. */
  GRADIENT_STEP: 8,

  /** Newton steps allowed while walking from a slot centre onto the water, and the cap on each one. */
  SEARCH_STEPS: 4,
  SEARCH_LIMIT: 700,

  /** Step and reach for walking out to each waterline, in world units. */
  PROBE_STEP: 14,
  PROBE_LIMIT: 620,

  /**
   * How far a finished crossing may sit from the slot centre that produced it, in world units.
   *
   * **Two things depend on this being bounded.** Four search steps of up to 700 units each could carry a bridge nearly
   * three thousand units away — far enough to leave the biome whose weight was checked at the start, which is how one
   * ended up on dry ground in a forest; and far enough that the clearance test's ring of neighbouring slots would not find
   * it, so nothing would keep trees out of it.
   *
   * Kept under the slot spacing, so a bridge always stays in the neighbourhood of its own slot.
   */
  MAX_DRIFT: 1500,

  /** Clear space a bridge needs from other structures, in world units, beyond its own half-span. */
  CLEARANCE: 26,
};

/**
 * Gradient for bridges.
 *
 * **Fixed rather than taken from the host biome, which is a deliberate exception.** Every other structure is drawn in its
 * biome's gradient, but a bridge would then come out in water colours — invisible against the river it crosses. A bridge
 * is steel wherever it stands, and it is the one thing in the world that is unmistakably engineered, so it gets to say so.
 */
export const BRIDGE_PALETTE = {
  /** Dark steel at the footings, so the abutments recede into the bank. */
  LOW: 0x33405e,

  /** Cold bright white at the tower tops and the crown of an arch, well over the bloom threshold. */
  HIGH: 0xdfe9ff,
};

// ---------------------------------------------------------------------------
// Water — rules shared by every biome that holds any
// ---------------------------------------------------------------------------

/**
 * Rules that belong to water itself rather than to a river or a lake.
 *
 * Split out when the second water biome arrived. The surface drawing and the shore reservation are generic, and a generic
 * module reaching into `RIVER` for its constants would have made lakes quietly inherit a river's tuning.
 */
export const WATER = {
  /**
   * Biome weight below which no water is drawn at all.
   *
   * Water depth scales with its biome's weight, which is what confines it to its own region. But a weight of a few
   * percent still leaves a film a unit or two deep — measured at 25 segments a tile of barely-visible water lying over a
   * city — and there is nothing to gain from drawing that. The remaining weight is rescaled above this floor, so water
   * still fades to nothing smoothly rather than stopping at a step.
   */
  MIN_WEIGHT: 0.18,

  /**
   * Dry ground an object needs beyond its own radius before it may stand near water, in world units.
   *
   * A tree touching the waterline reads as growing out of the water even when it technically is not, so a footprint is
   * inflated a little before the test. Small: a shoreline margin, not a setback.
   */
  SHORE_CLEARANCE: 14,

  /**
   * How far past a water body's rim the shore test still looks, in units of the body's own half-width.
   *
   * **Deliberately looser than the water body itself.** Water stops well inside the hollow that holds it, so an object
   * centred *beyond* the rim can still be wide enough to overhang the water — and those are exactly the cases this test
   * exists to catch. A gate set at the rim would reject them.
   */
  CLEARANCE_GATE: 2,
};

// ---------------------------------------------------------------------------
// Biome: lakeland — hummocky low ground pitted with small lakes
// ---------------------------------------------------------------------------

/**
 * Kettle-hole country: bumpy ground scattered with small, irregular, unconnected lakes. Finland, Minnesota, the parts
 * of Canada a glacier left behind.
 *
 * ## Blobs are the easy case, and for once that is the point
 *
 * Every water feature so far has fought the fact that thresholded noise gives **blobs rather than channels** — it is why
 * a river had to be carved along a field's zero-crossing contours instead of flooded into low ground. A lake wants
 * exactly the blob that was the enemy. Threshold a low-frequency field, carve a hollow wherever it is over the line,
 * and fill it. The hardest problem in the river biome is this one's starting point.
 *
 * ## Small and many, never one big sheet
 *
 * The steering file forbids large uniform water and it is right to: a wide flat surface in wireframe has no distance cue
 * and nothing to look at. So these are sized so the far shore is always visible, and there are lots of them. A lake you
 * can see across reads as a lake; one you cannot reads as the edge of the map.
 *
 * ## Everything here is reused
 *
 * The hollow profile is shared with the river channel (`hollow.js`), the surface drawing and the shore reservation are
 * shared with the river (`waterSurface.js`). This biome is a field, a threshold and a config block — which is the payoff
 * for having built the river's water as something general rather than as part of the river.
 */
export const LAKELAND = {
  /** Height of the regional ground level, in world units. Must exceed `DEPTH` so a basin floor never clamps at zero. */
  BASE_HEIGHT: 150,

  /** Amplitude and wavelength of the regional drift. Long and gentle, as it sets the water level. */
  BASE_RELIEF: 48,
  BASE_WAVELENGTH: 4800,

  /**
   * Amplitude of the hummocks laid over the base, in world units.
   *
   * Higher than the river valley's hills, because bumpy ground between the water is what makes this glacial rather than
   * merely wet. Still well short of rolling hills.
   */
  GROUND_RELIEF: 120,

  /** Wavelength of those hummocks. Short, so the ground is busy between lakes. */
  GROUND_WAVELENGTH: 900,

  /** Octaves and falloff for the hummock field. */
  GROUND_OCTAVES: 3,
  GROUND_PERSISTENCE: 0.48,

  /** Frequency multiplier per octave, shared by all three fields. */
  LACUNARITY: 2.09,

  /**
   * Wavelength of the field that decides where lakes are, in world units.
   *
   * **Sets the size of a lake and the distance between lakes together**, the same coupling the river channel has. Short
   * enough that a lake is comfortably crossable and there are several in view.
   */
  BASIN_WAVELENGTH: 1700,

  /** Octaves for the basin field. Few: a lake wants a rounded outline, not a fractal coastline. */
  BASIN_OCTAVES: 2,

  /**
   * Field value above which a basin forms, and the range over which it deepens to full.
   *
   * **Both were measured, and the first guesses were badly wrong in opposite directions.** The field runs 0.16 to 0.84
   * at the 1st and 99th percentiles with a median of 0.50, so a threshold of 0.58 put a *third* of the biome inside a
   * basin — 22% of it under water, with lakes only four seconds apart. That is a swamp, not lakeland. And a ramp of 0.16
   * put full depth at 0.74, which only 7% of the land reaches, so most basins never got deep enough to hold much water.
   *
   * At 0.70 and 0.08: 13% of the biome is basin, 8% is water, and full depth is reachable. fBm never approaches its own
   * extremes and this project has now been caught by that five times — measure the occupied range of every new field.
   */
  BASIN_THRESHOLD: 0.7,
  BASIN_RAMP: 0.08,

  /**
   * How far a basin cuts below the regional base, in world units.
   *
   * The water fills the bottom `DEPTH - WATER_DROP` of it, leaving the rest as dry shore.
   */
  DEPTH: 74,

  /**
   * Exponent on the basin's cross-section.
   *
   * Above 1 gives a flat bottom rather than a funnel — and, as `hollow.js` records, it is also the strongest lever on how
   * wide the water ends up. Tune it expecting both effects.
   */
  SHORE_SHARPNESS: 2.6,

  /**
   * How far the water sits below the regional base, in world units.
   *
   * `DEPTH - WATER_DROP` is the depth of the water and has to stay under `CAMERA.MIN_GROUND_CLEARANCE` of 45, so nothing
   * can ever pass beneath the surface and the flight model needs to know nothing about water. Currently 38.
   */
  WATER_DROP: 36,

  /** Spacing of the world-aligned grid the surface is drawn on. Shared reasoning with the river's. */
  WATER_GRID: 64,

  /**
   * Thumb on the scale for how much of the world is lakeland.
   *
   * Slightly negative. This is the second water biome and the two would otherwise between them make the world read as
   * wetland; holding this back keeps rivers the more common water.
   */
  SUITABILITY_BIAS: -0.02,
};

/**
 * The same lakes on gentle open ground instead of glacial hummocks.
 *
 * ## Why a second lake biome rather than a wider tuning range
 *
 * Reported after flying the first one: the bumpy ground reads as rocky, which is fine and worth keeping, but it makes the
 * lakes feel like they belong to broken country. Gentle rounded ground gives the same water an entirely different
 * character — quiet lowland rather than scoured rock — and the difference only exists if both are present. A single biome
 * with a wide range of ground roughness would average out into neither.
 *
 * Defined as overrides on `LAKELAND` so the *differences* are the visible thing, which is what matters for sibling biomes.
 */
export const ROLLING_LAKES = {
  ...LAKELAND,

  /**
   * Ground relief and wavelength: lower and longer than the glacial version.
   *
   * The pairing is what matters. Dropping the amplitude alone leaves the same busy shape at a smaller scale; lengthening
   * the wavelength too is what turns hummocks into shoulders.
   */
  GROUND_RELIEF: 88,
  GROUND_WAVELENGTH: 1900,

  /** One fewer octave. Detail here would put a rocky texture back on ground meant to read as soft. */
  GROUND_OCTAVES: 2,

  /**
   * Held back slightly harder than the glacial version.
   *
   * There are now three lake biomes and a river, and water should not become the condition of the world. This and
   * `WOODED_LAKES.SUITABILITY_BIAS` are the first numbers to lower if it does.
   */
  SUITABILITY_BIAS: -0.03,
};

/**
 * Gentle lake country with trees on it.
 *
 * ## Why the trees belong here and not in the glacial version
 *
 * Reported, and it is a good observation: the bumpy lakeland ground implies rock, so trees on it would look like they were
 * growing out of scree. Soft rounded ground is where woodland belongs. So the three lake biomes divide as bumpy-and-bare,
 * gentle-and-bare, gentle-and-wooded — which also means each pair differs in exactly one way.
 *
 * Plain forest stays as it is. This is not a replacement for it.
 */
export const WOODED_LAKES = {
  ...ROLLING_LAKES,

  /**
   * A touch more relief than the open version, so the woods have something to drape over.
   *
   * Matching the forest's own character rather than the lakeland's: enough shape to give stands somewhere to sit, not
   * enough to make the trees look like they are clinging on.
   */
  GROUND_RELIEF: 104,

  /**
   * Slightly larger and rarer basins than the open version.
   *
   * Trees hide a small pond completely. A lake in woodland has to be big enough to read as a clearing full of water.
   */
  BASIN_WAVELENGTH: 2000,
  BASIN_THRESHOLD: 0.72,

  /** Held back, like its siblings. */
  SUITABILITY_BIAS: -0.03,
};

// ---------------------------------------------------------------------------
// Radio telescope array — an installation, not a region
// ---------------------------------------------------------------------------

/**
 * A field of dishes on open rolling country. The Very Large Array, ALMA, Parkes.
 *
 * ## Not a biome, and the same test decided it
 *
 * Does the feature need its own *base* elevation, or does it only need particular ground? Mesa, canyon and badlands
 * each needed their own base. An array needs ground that is *level*, which rolling hills already provides in places
 * — so this is an **installation** placed on an existing biome rather than a region of its own. It is the first
 * thing in the world built that way.
 *
 * ## Why it is worth building before the other installations
 *
 * A railyard, an airfield, an industrial plant and a set of ruins are all the same shape of problem: one localised
 * compound with a deliberate internal arrangement, placed rarely, straddling tile edges. Nothing in the world did
 * that before — the city is a region, farmland is fields, a forest is a scatter. This is the simplest possible
 * version of it, so the pattern gets established on the cheapest case.
 *
 * ## The dish is the only curved surface in the world
 *
 * Concentric rings in wireframe read as a dish immediately; there is nothing else they could be. That distinctness
 * is doing most of the work, which is why the array needs no supporting buildings to say what it is.
 *
 * ## Every dish in a site points the same way
 *
 * The signature of a real array, and free: azimuth and elevation are rolled once per *site*, not per dish. A field
 * of identical hardware aimed at one patch of sky is unmistakably deliberate, where dishes at random angles would
 * read as scattered junk.
 */
export const TELESCOPE = {
  /**
   * Spacing of the slot grid sites are placed on, in world units.
   *
   * Far coarser than anything else in the world. It has to exceed the site's own diameter by a comfortable margin,
   * which is what guarantees two arrays can never overlap without a collision check.
   *
   * **This is the rarity dial that actually works.** `SITE_DENSITY` is already near 1, and the ground checks reject
   * most of what is offered, so frequency has to come from offering more slots.
   *
   * ## Raised from 2,200 after review, and clustering was the real complaint
   *
   * Reported: two arrays fairly near each other. At 2,200 the grid put adjacent sites a minimum of 1,520 units apart,
   * and measured over 500 hills tiles there were **22 pairs close enough to be seen at once** — fog reaches 7,000
   * units, so that is a low bar to clear.
   *
   * | spacing | arrays | per hills tile | closest pair | co-visible pairs |
   * |---|---|---|---|---|
   * | 2,200 | 52 | 1 per 10 | 2,078 u | 22 |
   * | 3,200 | 27 | 1 per 19 | 3,278 u | 2 |
   * | **3,600** | 22 | 1 per 23 | 3,365 u | 3 |
   * | 4,200 | 17 | 1 per 29 | 9,812 u | 0 |
   *
   * 3,600 makes an array about 2.4 times rarer and cuts clustering by roughly 90%. **4,200 removes it outright**, which
   * is worth recording because the extra cost is only a step from one per 23 hills tiles to one per 29. Guaranteeing two
   * are never co-visible from spacing alone would take 7,700, which is far too rare to be worth having.
   */
  SITE_SPACING: 3600,

  /**
   * Chance an eligible slot holds a site, before biome weight and the ground checks.
   *
   * The rarity dial, and it reads much lower than it looks: the ground checks reject most candidates, because level
   * enough for a whole array is a demanding requirement on rolling country.
   */
  SITE_DENSITY: 0.9,

  /**
   * Arms radiating from the site centre, and dishes along each.
   *
   * Three arms is the Very Large Array, and a Y is the right choice here for a reason beyond authenticity: the city
   * already owns rectangular grids, so a three-fold pattern cannot be mistaken for one from the air.
   */
  ARMS: 3,
  DISHES_PER_ARM: 3,

  /** Distance from the site centre to the innermost dish on an arm, in world units. */
  ARM_INNER: 130,

  /**
   * How much further out each successive dish sits, as a multiple of the last.
   *
   * Above 1 because real arrays space their dishes logarithmically — it is what gives the array its reach without
   * needing dozens of them, and it looks purposeful rather than evenly stamped.
   */
  ARM_GROWTH: 1.5,

  /** Rim radius of one dish, in world units. Large enough to read as a dish from a distance. */
  DISH_RADIUS: 46,

  /**
   * Depth of the bowl at its rim, as a fraction of the rim radius.
   *
   * Shallow, like a real parabolic reflector. Deep enough that the rings clearly step back from the rim and the
   * shape reads as a bowl rather than as a flat disc.
   */
  DISH_DEPTH_RATIO: 0.28,

  /** Concentric rings in the bowl. Four steps back from the rim legibly without much cost. */
  DISH_RINGS: 4,

  /**
   * Points per ring. **The one place in this world where roundness matters**, since the dish is the only curved
   * surface in it — a coarse polygon would read as a hexagonal pan.
   */
  DISH_SIDES: 12,

  /** Height of the bowl's vertex above the ground, in world units. */
  MOUNT_HEIGHT: 34,

  /** Splayed legs under the bowl, in addition to the central mast. */
  MOUNT_LEGS: 3,

  /**
   * Largest offset a site may take from its slot centre, in world units.
   *
   * **Kept small for a performance reason, not a visual one.** The reservation has to search every slot whose site
   * could reach the query point, and that search widens with the jitter. Left at the full slack the 3,000-unit grid
   * allows — over 1,100 units — it forced a nine-slot scan on every tree, rock column and farm structure in the
   * world, which measured at five milliseconds a forest tile. At this setting the scan is usually one slot.
   *
   * Nothing is lost: arrays are 3,000 units apart and far beyond fog range from each other, so the grid they sit on
   * is unobservable either way. Clamped against the spacing below regardless, so two arrays can never overlap.
   */
  SITE_JITTER: 340,

  /** Spread of the mount's feet, as a fraction of the dish radius. */
  MOUNT_FOOT_RATIO: 0.38,

  /**
   * Range the whole array is aimed over: angle above the horizon, in radians.
   *
   * Never at the horizon and never quite at the zenith. A dish pointing level reads as a satellite receiver and one
   * pointing straight up loses its silhouette entirely; between those it is unmistakable.
   */
  MIN_ELEVATION_ANGLE: 0.6,
  MAX_ELEVATION_ANGLE: 1.25,

  /**
   * Above this much vertical component in the pointing axis, the ring frame swaps its reference direction.
   *
   * Guards a cross product against collapsing when the dish points near the zenith, which would silently produce a
   * dish of zero width rather than anything that looked like an error.
   */
  AXIS_SINGULARITY_GUARD: 0.98,

  /**
   * Steepest ground the site *centre* may sit on, as rise over run.
   *
   * Standing in for "on a flat spot". Rolling hills has a median slope of 0.25, so anything below that selects
   * hilltops, shoulders and valley floors, which is where an array would actually be built.
   */
  MAX_GROUND_SLOPE: 0.2,

  /**
   * Largest height difference tolerated across every dish position, in world units.
   *
   * **The dial that decides whether arrays exist at all, and the honest one to argue about.** The ground is not
   * flattened — see below — so this is the limit on how much an array is allowed to drape over a rise.
   *
   * Measured: at 58 only 12% of candidate sites survived and an array turned up once in 57 hills tiles, which is
   * effectively never. 90 units across a 680-unit site is an average incline of about seven degrees — enough that an
   * array reads as sitting on a broad shoulder rather than on a graded pad. That is the tradeoff of not flattening,
   * and it is the thing to look at first: if arrays look like they are sliding downhill, lower this and raise
   * `SITE_DENSITY` to compensate.
   */
  MAX_SITE_SPREAD: 90,

  /**
   * How far each dish's foot is pushed below its own sampled ground, in world units.
   *
   * Small. Terrain is drawn on a lattice and a mount foot lands between vertices, so without this a leg can end up
   * a few units clear of the surface it is supposed to be standing on.
   */
  FOOT_SINK: 3,

  /**
   * Clear space the site needs from other structures, in world units, measured beyond its own reach.
   *
   * Consulted both ways: the array keeps clear of another biome's buildings, and the site is published as a
   * reservation so trees and rock formations keep out of it. A spire through a dish would be the tree-in-a-building
   * bug again, in a more expensive place.
   */
  CLEARANCE: 30,
};

// ---------------------------------------------------------------------------
// Biome: farmland — the pattern is the point
// ---------------------------------------------------------------------------

/**
 * Worked land, seen from the air.
 *
 * ## Why this works in wireframe when a plain field would not
 *
 * Flat ground is the thing the uniformity rule warns about — but farmland is not really flat *ground*, it
 * is a **pattern drawn on** flat ground. Field boundaries, tracks and hedgerows are already lines, which
 * is the one thing this renderer draws natively. The interest comes from the quilt, not the relief, and
 * that makes this the only biome where being level is an asset.
 *
 * ## Why a grid is authentic here and not a second city
 *
 * The user asked for the Kansas look, and Kansas genuinely is a grid: the Public Land Survey System put
 * section lines on a one-mile pitch across the whole state. So a regular lattice is correct rather than
 * lazy. What keeps it from reading as a city is scale and posture — fields are several times a city block
 * across, the lines lie flat on the ground instead of standing up, and the only vertical thing is the
 * occasional windpump.
 *
 * Irregularity comes from *editing* the grid rather than distorting it: some boundaries are left undrawn
 * so neighbouring fields merge, and some fields are split into strips.
 */
export const FARMLAND = {
  /**
   * Size of one worked field, in world units. **The main dial for this biome.**
   *
   * A field every 520 units is roughly 2 seconds of flight at cruise, so the patchwork reads as a patchwork
   * rather than as either a blur or one enormous field. Well above `CITY.BLOCK_SIZE` 220, which is part of
   * what stops it looking like streets.
   */
  FIELD_SIZE: 520,

  /**
   * Distance between crop rows, in world units.
   *
   * **This is what makes a field read as worked ground rather than as an empty area with an outline.** The
   * first version of this biome drew only field boundaries and no rows at all, which is exactly why it read
   * as lines painted on the ground.
   *
   * Wider than it sounds. Rows fine enough to be realistic would alias into shimmer at 1px, and the goal is
   * legible corduroy at flight distance, not agricultural accuracy.
   */
  ROW_SPACING: 62,

  /**
   * Height a row's crest stands above the ground, in world units.
   *
   * Rows are small raised ridges, not lines lying on the surface. A decal reads as paint; a ridge reads as
   * ground that has been worked. Each row's ends drop back down to the terrain so the ribbon has visible
   * thickness at a field edge.
   */
  ROW_HEIGHT: 11,

  /**
   * Segments a row is drawn with along its length.
   *
   * Each samples the ground at its own ends, so more segments drape the row over a swell more closely. This
   * is the biggest single cost in the biome, so it is the first thing to lower if farmland gets expensive.
   */
  ROW_SEGMENTS: 5,

  /**
   * How far a row's direction may wander from the true contour, in degrees.
   *
   * Real contour ploughing follows the land but not slavishly, and a little inconsistency between fields is
   * what stops the whole biome looking machine-generated. Zero would make every field on one hillside share
   * exactly the same angle.
   */
  ROW_ANGLE_JITTER: 14,

  /**
   * Slope below which the ground is treated as level and the contour direction is meaningless.
   *
   * On genuinely flat ground the gradient points nowhere in particular, so rows would swing wildly between
   * neighbouring fields. Below this, each field picks its own direction at random instead — which is what
   * real flat farmland looks like anyway.
   */
  CONTOUR_MIN_SLOPE: 0.035,

  /**
   * Steepest ground that may be farmed, as rise over run.
   *
   * **Rows climbing a mountainside was the most obviously wrong thing about the first version.** Farmland
   * borders the mountains like any other biome, and without this the pattern marched straight up the flanks.
   * Nobody ploughs a cliff.
   *
   * 0.30 is about 17 degrees, comparable to the city's limit — both are things you only build on ground that
   * is close to level.
   */
  MAX_ROW_SLOPE: 0.3,

  /**
   * Chance a field boundary is drawn between two fields.
   *
   * Below 1 so neighbouring fields merge into larger irregular ones. With rows carrying the texture, the
   * boundary is now supporting detail rather than the whole biome.
   */
  BOUNDARY_CHANCE: 0.55,

  /** Height a boundary hedge stands above the ground, in world units. Taller than a row: it is a hedgerow. */
  BOUNDARY_LIFT: 14,

  /** Segments a boundary is drawn with, so it follows the ground rather than cutting through it. */
  BOUNDARY_SEGMENTS: 4,

  /**
   * Minimum rows in a worked field. **A field is either properly worked or not worked at all.**
   *
   * Thinning by weight used to take a fringe field down to one or two rows, and a couple of isolated ridges
   * read as stray lines for no apparent reason — the same failure the whole first version had, in miniature.
   * Below this count the field is skipped entirely, so farmland has a ragged edge of unworked ground rather
   * than a scattering of orphan rows.
   *
   * With `FIELD_SIZE` 520 and `ROW_SPACING` 62, five rows needs a biome weight of about 0.6, so this is also
   * the main control over how far farmland reaches toward its neighbours.
   */
  MIN_ROWS: 5,

  /**
   * Chance a field holds a farmstead — a house, a barn and a silo.
   *
   * Reduced after flying it: farmland read as busy, and a farmstead roughly every field was most of the reason.
   * Its job is to tell the viewer what they are looking at, and one per several fields does that just as well
   * while leaving the rows to carry the view.
   *
   * **This is a per-field chance, so it had to rise even though the result halved.** Two other changes landed
   * at the same time — a minimum field size, and a fix for fields being emitted by two tiles at once — and
   * together they cut the number of worked fields per tile from about sixteen to about eight. Holding the old
   * per-field number would have cut farmsteads by 83% rather than the 50% asked for. Tuned against measured
   * density per tile, which is what a player actually perceives, not against the chance itself.
   */
  FARMSTEAD_CHANCE: 0.09,

  /** Farmhouse footprint and height in world units. Small, and dwarfed by the barn as it should be. */
  HOUSE_WIDTH: 26,
  HOUSE_DEPTH: 20,
  HOUSE_WALL_HEIGHT: 14,
  HOUSE_ROOF_HEIGHT: 11,

  /** Barn footprint and height. The recognisable building: long, with a steep roof. */
  BARN_WIDTH: 46,
  BARN_DEPTH: 26,
  BARN_WALL_HEIGHT: 20,
  BARN_ROOF_HEIGHT: 18,

  /** Silo radius and height. A narrow cylinder with a domed cap — vertical, so it catches the eye first. */
  SILO_RADIUS: 8,
  SILO_HEIGHT: 40,
  SILO_SIDES: 8,

  /**
   * Biomes whose presence stops the land being farmed.
   *
   * ## Why farmland needs this and no other biome does
   *
   * Every biome fades in by thinning out, which works when two can plausibly share ground — trees among open
   * grass, towers thinning toward a hillside. Farmland cannot: **a field inside a city is not a transition, it
   * is a mistake**, and neither is a ploughed row part-way up a mountain. Farming is an exclusive land use in a
   * way that trees and towers are not.
   *
   * Where the combined weight of these biomes exceeds `MAX_RIVAL_WEIGHT`, nothing is farmed. Farmland therefore
   * stops *before* a city rather than overlapping into it, leaving a fringe of unworked ground — which is what
   * the edge of real farmland looks like.
   *
   * A local rule rather than special-case transition code, exactly like the city refusing to build on steep
   * ground. The biome field is untouched: terrain height and colour still blend normally, so the ground stays
   * continuous and only the *working* of it stops.
   *
   * **Hills and baseline are deliberately absent**, so farmland blends freely over open and rolling ground.
   * That is what makes farmland over rolling hills work with no extra machinery.
   *
   * **Forest is absent too, on purpose:** fields beside and among woods is what real farmland looks like, and
   * hedgerow trees are a feature rather than a defect. Add `'forest'` here if that turns out to read badly.
   *
   * **Every new rock biome has to be added here.** Mesa and canyon were both reported as farmland appearing on
   * bare rock — nobody ploughs the top of a butte. This list does not update itself, so a new dramatic biome will
   * grow fields on it until it is named. A test asserts every id here is a registered biome, which catches typos
   * but cannot catch omissions.
   */
  EXCLUSIVE_AGAINST: ['city', 'mountains', 'mesa', 'canyon', 'badlands'],

  /**
   * Combined weight of `EXCLUSIVE_AGAINST` biomes above which nothing is farmed.
   *
   * Low, because the point is to stop farmland reaching *into* a city or onto a mountain at all. Raising it lets
   * fields push further into a rival region; lowering it pulls them back and widens the fringe of unworked
   * ground between the two.
   */
  MAX_RIVAL_WEIGHT: 0.12,

  /**
   * Chance a field holds a windpump.
   *
   * Reduced alongside `FARMSTEAD_CHANCE`, and raised as a per-field number for the same reason: fewer fields
   * are worked now, so the chance per field has to rise to halve the density per tile. A windpump should be a
   * landmark in the pattern, and a landmark you pass every few seconds is just scenery.
   */
  WINDPUMP_CHANCE: 0.13,

  /** Height of the windpump tower, in world units, before the wheel on top. */
  WINDPUMP_HEIGHT: 46,

  /** Tower footprint at the base, in world units. Tapers to a quarter of this at the top. */
  WINDPUMP_BASE_WIDTH: 13,

  /** Radius of the fan wheel, in world units. Generous, because it is the recognisable part. */
  WINDPUMP_WHEEL_RADIUS: 11,

  /** Blades in the wheel. A farm windpump has many short vanes, which is what distinguishes it. */
  WINDPUMP_BLADES: 10,

  /**
   * Steepest ground a windpump may stand on, as rise over run.
   *
   * Permissive compared with a tower: a windpump is small and sits on a simple footing, so it copes with
   * ground a building could not.
   */
  WINDPUMP_MAX_SLOPE: 0.55,

  /**
   * Height of the tallest ground swell, in world units.
   *
   * Low, deliberately: this is the *flat* farmland the user asked to keep separate from the version over
   * rolling hills. Not level, though — a completely flat plane reads as a void in wireframe, and a slight
   * roll is what gives the field pattern something to drape over.
   */
  MAX_ELEVATION: 145,

  /** Wavelength of that swell in world units. Long and lazy. */
  WAVELENGTH: 6200,

  /** Octaves for the ground. Two: any more puts detail under a pattern meant to read as the subject. */
  OCTAVES: 2,

  /** Amplitude falloff per octave. */
  PERSISTENCE: 0.4,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /**
   * Thumb on the scale for how much of the world is farmland.
   *
   * Neutral, like the other non-default biomes. Farmland is calm, open country — the same reasoning that
   * let rolling hills in without rebalancing applies, and this needs measuring the same way.
   */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Biome: city — the first biome made of structures rather than terrain
// ---------------------------------------------------------------------------

/**
 * A city is a street grid with a wireframe tower in most blocks.
 *
 * ## Why the grid is anchored to absolute world coordinates
 *
 * Block boundaries fall on multiples of `BLOCK_SIZE` in world space, never relative to a
 * tile. A grid laid out per tile would restart at every tile edge, putting a visible
 * discontinuity in the street pattern every 1600 units.
 *
 * ## Why buildings cannot intersect
 *
 * One building per block, and a footprint that can never exceed `BLOCK_SIZE - STREET_WIDTH`.
 * Two buildings therefore always have at least a street between them. That is a structural
 * guarantee rather than a collision check, which is both cheaper and impossible to get
 * subtly wrong.
 */
export const CITY = {
  /**
   * Distance between street centre lines, in world units. This is the block pitch, so it
   * sets the density of the whole grid. Smaller means a tighter, busier city.
   *
   * 220 against a 1600-unit tile gives roughly 7 blocks per tile edge.
   */
  BLOCK_SIZE: 220,

  /**
   * Width of the gap left between a building and its block boundary, in world units. Half
   * of it belongs to each side, so the clear space between two neighbouring towers is a
   * full STREET_WIDTH.
   */
  STREET_WIDTH: 70,

  /**
   * Smallest and largest fraction of the available block interior a building's footprint
   * takes. Below 1 always, so a building never fills its block and the streets stay open.
   * The variation is what stops the city reading as a regular array of identical boxes.
   */
  FOOTPRINT_MIN: 0.45,
  FOOTPRINT_MAX: 0.92,

  /**
   * Shortest and tallest tower at the city core, in world units.
   *
   * 550 was chosen at the building catalogue, which draws towers at a settable height — so this is the
   * height the archetypes were actually judged at rather than a number picked in the abstract.
   */
  MIN_HEIGHT: 90,
  MAX_HEIGHT: 550,

  /**
   * Exponent applied to the random height roll. Above 1 this makes tall towers rarer than
   * short ones, so a skyline has a few landmarks rather than a uniform wall of equal boxes.
   */
  HEIGHT_SKEW: 2.4,

  /**
   * Chance a block holds a building at all, at full city weight. Below 1 so that even the
   * densest part of a city has gaps — plazas and empty lots — which reads far more like a
   * city than a completely filled grid.
   */
  BASE_DENSITY: 0.82,

  /**
   * Exponent applied to the city's biome weight before it scales density.
   *
   * This is what makes a city thin out believably instead of linearly. Above 1, density
   * falls away quickly as the weight drops, so the outskirts are genuinely sparse
   * outbuildings rather than a slightly thinner version of downtown.
   */
  DENSITY_FALLOFF: 2.2,

  /**
   * Fraction of full height available at the very edge of a city.
   *
   * Not zero, because a building that shrinks to nothing at the boundary looks like it is sinking into
   * the ground rather than being a small building.
   *
   * This is the *only* height falloff. `CORE.SHARPNESS` shapes the curve between here and full height at
   * the core; scaling by biome weight as well would apply the same signal twice, since coreness is
   * derived from that weight.
   */
  MIN_HEIGHT_SCALE: 0.3,

  /** Towers at least this tall, in world units, get an antenna. */
  ANTENNA_MIN_HEIGHT: 380,

  /** Antenna length as a fraction of the tower's height. */
  ANTENNA_LENGTH: 0.18,

  /**
   * Height fractions at which a horizontal accent ring is drawn around a tower. These are
   * the "lit floors" that give a tower a sense of scale — without them a wireframe box is
   * just a box and could be any size.
   */
  ACCENT_RINGS: [0.38, 0.72],

  /** Towers shorter than this, in world units, get no accent rings. They would only clutter. */
  ACCENT_MIN_HEIGHT: 200,

  /**
   * Height of the tallest ground swell the city biome produces, in world units.
   *
   * Low, because a street grid on steep terrain looks wrong. Not flat, though: a completely
   * level plane reads as a void in a wireframe with no other cues, and a slight roll gives
   * the grid something to drape over.
   */
  MAX_ELEVATION: 170,

  /** Wavelength of that swell in world units. Long and lazy. */
  WAVELENGTH: 5200,

  /** Octaves for the ground. One more than baseline would add detail nothing benefits from. */
  OCTAVES: 2,

  /** Amplitude falloff per octave. */
  PERSISTENCE: 0.4,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /**
   * Thumb on the scale for how much of the world is city. Slightly negative: cities should
   * be somewhere you come across, not the default condition of the world.
   */
  SUITABILITY_BIAS: -0.02,

  /**
   * Steepest ground a building may stand on, as rise over run across its own footprint.
   *
   * Where a city borders the mountains the blended ground is genuinely steep, and towers were
   * being planted on mountain flanks and part-way up ridges. This is the fix, and it is a local
   * rule rather than special-case transition code: you do not build a tower on a cliff. It also
   * thins buildings out along a mountain border for free, which is what puts visible space
   * between the two biomes.
   *
   * 0.35 is about a 19 degree slope. Lower values push cities further onto flat ground; raise
   * it and hillside districts come back.
   */
  MAX_GROUND_SLOPE: 0.35,

  /**
   * Clear space kept around a building that other biomes' structures must stay out of, in world units.
   *
   * Where a city borders a forest both biomes have weight, so both place structures, and neither knows
   * about the other — trees were growing up through towers and clipping into their walls.
   *
   * Small on purpose. Trees pressed right up against a building look like a city edge; trees *inside* one
   * look like a bug. This is the gap, not a keep-out zone.
   */
  STRUCTURE_CLEARANCE: 10,

  /**
   * Downtown: how a city varies from its core to its edge.
   *
   * ## Why this exists
   *
   * Before it, every block across a whole city region drew from the same height range and the same
   * density, so a city had no *core*. Every part of it looked like every other part, and the towers had
   * nothing short beside them to be tall against. A real city has a dense tall centre that falls away
   * fast to low-rise, and that contrast is most of what makes a skyline read as one.
   *
   * ## What "core" means here
   *
   * The city's own biome weight, which already peaks in the middle of a region and falls to zero at its
   * edges. No new field is needed and no new noise is sampled — the shape of a city's downtown is the
   * shape of its region, which is why this costs nothing.
   */
  CORE: {
    /**
     * Exponent applied to biome weight to get "coreness".
     *
     * Above 1 concentrates the tall centre. This is the main dial for how compact a downtown is: at 1 a
     * city ramps up gently across its whole width, at 3 it is a tight cluster of towers ringed by
     * low-rise.
     */
    SHARPNESS: 2.1,

    /**
     * Coreness below which a block only ever gets a podium.
     *
     * The outskirts should be uniformly low-rise. Letting a spire spawn out there would undo the whole
     * effect, because a lone tall building on the edge reads as the centre.
     */
    PODIUM_ONLY_BELOW: 0.2,

    /**
     * Coreness above which the tallest, most distinctive archetypes become eligible.
     *
     * Keeps spires and twin towers as landmarks belonging to a downtown rather than shapes scattered
     * evenly across every city.
     */
    LANDMARK_ABOVE: 0.55,
  },
};

/**
 * Which archetypes a city may use, and where.
 *
 * Separate from `BUILDING`, which holds each shape's *proportions*. This is about *distribution* — the
 * mix is what a city looks like, and it is the thing most likely to want tuning after a flight.
 *
 * Ids must exist in `buildingShapes.js`; an unknown one falls back to a plain box rather than throwing,
 * so a rename degrades instead of emptying every city.
 */
export const CITY_SHAPES = {
  /**
   * Weighted pool for ordinary blocks. Higher weight means more common.
   *
   * Boxes stay dominant on purpose. The archetypes are seasoning: a city where every building is
   * distinctive has no ordinary fabric for the distinctive ones to stand out from, which is the same
   * mistake as a forest with no clearings.
   */
  COMMON: [
    { id: 'box', weight: 30 },
    { id: 'podium', weight: 18 },
    { id: 'slab', weight: 10 },
    { id: 'setback', weight: 8 },
    { id: 'taper', weight: 7 },
    { id: 'cylinder', weight: 6 },
    { id: 'twist', weight: 4 },
    { id: 'lattice', weight: 3 },
    { id: 'dome', weight: 3 },
  ],

  /** Added to the pool only near a core, so these read as landmarks. */
  LANDMARKS: [
    { id: 'spire', weight: 5 },
    { id: 'twin', weight: 4 },
  ],

  /**
   * Archetypes that are low by nature, and the ceiling they are capped to.
   *
   * A dome stretched to tower height stops being a dome, and a podium is defined by being short. The cap
   * is a fraction of what the block's height roll would otherwise have given.
   */
  LOW_RISE: { ids: ['podium', 'dome'], HEIGHT_CAP: 0.34 },
};

// ---------------------------------------------------------------------------
// Biome: suburbia — the town between downtown and open country
// ---------------------------------------------------------------------------

/**
 * A small town: streets lined with pitched-roof houses, and now and then a church, a school or a water tower.
 *
 * ## The gap it fills
 *
 * The world had a downtown and it had open ground, and nothing in between — a city stopped and fields began.
 * This is what a city actually has around it, and it needs no new capability at all: the pitched-roof box
 * farmland uses for its barns is exactly a house, so the common case was already built.
 *
 * ## What makes it read as a town rather than a scattering of sheds
 *
 * Three things, in order of how much they matter from the air:
 *
 * 1. **Houses line the streets and leave the middle of a block empty.** Only the perimeter plots of a block are
 *    built on, which is both what a real block looks like from above and what makes the street grid legible
 *    without needing much drawn.
 * 2. **The streets are drawn.** Houses are small and low, so at altitude the arrangement is what carries the
 *    signal, and faint lines along the block edges are what turn a field of dots into a town.
 * 3. **One or two things stand well above the roofline.** A church spire and a water tower are the whole
 *    difference between a town and a housing estate, which is why they are rare rather than absent.
 */
export const SUBURB = {
  /** Highest ground this biome produces, in world units. Low: a town sits on ground that is nearly level. */
  MAX_ELEVATION: 170,

  /** Wavelength of its ground, in world units. Long and gentle, so streets are never on a gradient. */
  WAVELENGTH: 2600,

  /** Octaves, persistence and lacunarity of that ground. Few octaves, because a town wants no fine roughness. */
  OCTAVES: 3,
  PERSISTENCE: 0.45,
  LACUNARITY: 2.13,

  /** Thumb on the scale for how much of the world is town. Neutral: it takes what its field earns. */
  SUITABILITY_BIAS: 0,

  /**
   * Edge of one house plot, in world units.
   *
   * **This and `HOUSE_CHANCE` are the two dials on how expensive the biome is**, because everything else about a
   * town is cheap. A 1,600-unit tile holds about `(1600 / PLOT_SIZE)²` plots, so small changes here move the
   * building count sharply — at 74 a tile is about 470 plots.
   *
   * The ratio of house width to plot is what decides whether it reads as suburban or rural. Around 0.4 looks
   * like a street of houses; much below that looks like smallholdings.
   */
  PLOT_SIZE: 74,

  /**
   * Plots along one edge of a block, so a block is this squared.
   *
   * Four gives a block of sixteen plots, twelve of them on the perimeter. Larger blocks leave bigger empty
   * middles and fewer streets; smaller ones stop reading as blocks at all.
   */
  BLOCK_PLOTS: 4,

  /**
   * Chance a perimeter plot holds a house, before biome weight scales it.
   *
   * Below 1 on purpose: a street with every plot filled reads as a terrace, and the gaps are what make it
   * suburban. Also the main lever on cost — see `PLOT_SIZE`.
   */
  HOUSE_CHANCE: 0.56,

  /** House footprint range, in world units. Across the ridge and along it. */
  MIN_HOUSE_WIDTH: 26,
  MAX_HOUSE_WIDTH: 38,
  HOUSE_DEPTH_RATIO: 0.72,

  /** Wall and roof heights, in world units. The roof is a good fraction of the wall, so the pitch reads. */
  MIN_WALL_HEIGHT: 10,
  MAX_WALL_HEIGHT: 15,
  MIN_ROOF_HEIGHT: 7,
  MAX_ROOF_HEIGHT: 11,

  /** How far a house may sit off its plot centre, as a fraction of the plot. Small: front gardens are shallow. */
  HOUSE_JITTER: 0.12,

  /** Steepest ground a town will sit on, as rise over run. Tight, because a street grid on a slope looks wrong. */
  MAX_GROUND_SLOPE: 0.16,

  /**
   * Chance per block of a church, a school and a water tower, before biome weight.
   *
   * All three are rare by design. A town with a church on every block is not a town, and the whole value of
   * these is that they are the exception the houses are measured against. Rolled per block and mutually
   * exclusive, so a block holds at most one of them and gives up its houses for it.
   *
   * **These are per *block*, and a tile holds about 29 of them, which is what made the first guesses wrong by
   * a factor of five.** At 0.055 a church landed on more or less every tile: with fog reaching 7,000 units you
   * would have had nine in view at once, and a landmark you can see nine of is not a landmark. Around 0.01
   * gives one church per four tiles, so one or two are in sight — which is what a small town looks like.
   */
  CHURCH_CHANCE: 0.01,
  SCHOOL_CHANCE: 0.007,
  WATER_TOWER_CHANCE: 0.008,

  /** Church nave footprint and heights, in world units. */
  CHURCH_NAVE_LENGTH: 52,
  CHURCH_NAVE_WIDTH: 22,
  CHURCH_WALL_HEIGHT: 20,
  CHURCH_ROOF_HEIGHT: 14,

  /**
   * Church tower and spire, in world units.
   *
   * **The spire is the point of the whole building.** It is the only tapered vertical thing in the world outside
   * the rock formations, and against a roofline of small triangles it reads instantly. Tall enough to clear
   * every house by a wide margin.
   */
  CHURCH_TOWER_WIDTH: 15,
  CHURCH_TOWER_HEIGHT: 34,
  CHURCH_SPIRE_HEIGHT: 28,

  /** School: a long flat-roofed range with a shorter wing across one end, in world units. */
  SCHOOL_LENGTH: 80,
  SCHOOL_WIDTH: 20,
  SCHOOL_HEIGHT: 15,
  SCHOOL_WING_LENGTH: 38,
  SCHOOL_WING_WIDTH: 18,

  /**
   * Playing field beside the school, in world units.
   *
   * A rectangle of lines on the ground with a halfway line. Cheap, and **it is what identifies the school** — a
   * long flat-roofed building on its own could be anything, and a long building next to a marked pitch could
   * not be much else.
   */
  FIELD_LENGTH: 104,
  FIELD_WIDTH: 66,

  /** Segments each field edge is drawn in, so it drapes over the ground rather than cutting through it. */
  FIELD_SEGMENTS: 6,

  /** Water tower: an octagonal tank on legs, in world units. Small-town silhouette, and the second landmark. */
  TOWER_LEG_HEIGHT: 30,
  TOWER_TANK_RADIUS: 11,
  TOWER_TANK_HEIGHT: 15,
  TOWER_TANK_SIDES: 8,
  TOWER_LEG_SPREAD: 9,

  /** How high the drawn streets sit above the ground, in world units, so they are never buried by it. */
  STREET_LIFT: 1.5,

  /**
   * Segments a street is drawn in along one block edge.
   *
   * Five over a 296-unit edge is one line per 59 units, which is already finer than the 50-unit terrain lattice
   * needs — the ground cannot bend between lattice points, so subdividing further only adds vertices. Streets are
   * the second largest thing this biome emits after the houses, so it is worth not overdoing.
   */
  STREET_SEGMENTS: 5,

  /** Clear space the town keeps from another biome's structures, in world units. */
  CLEARANCE: 26,
};

/**
 * Gradient for a town.
 *
 * Warmer and dimmer than the city's, which is the distinction the eye needs at a border: downtown is cold and
 * bright, the town around it is low and lit from inside. Kept well below the city's high stop so a skyline still
 * dominates its suburbs.
 */
export const SUBURB_PALETTE = {
  /** Dim slate at ground level, where the streets are. */
  LOW: 0x33455c,

  /** Warm sodium at the rooflines. The one warm structure palette in the world, and what says "lived in". */
  HIGH: 0xffc98a,
};

// ---------------------------------------------------------------------------
// Biome: forest — scattered trees, deliberately patchy
// ---------------------------------------------------------------------------

/**
 * A forest here is **not a canopy**. That is the hard requirement.
 *
 * An unbroken sea of treetops fails for exactly the same reason a large lake does, just higher
 * up: every frame looks like the last one. The steering file states this as a general rule, and
 * this biome is the reason it exists. The visual interest has to come from the gaps.
 *
 * ## How patchiness is produced
 *
 * Two levels. Trees occupy slots on a fine world-aligned grid, and a separate medium-frequency
 * **stand field** decides how much of that grid is used. Where the stand field is low, nothing
 * grows at all — those are the clearings. Where it is high, most slots fill in. The result is
 * stands of trees with open ground between them, at a scale you fly across in a few seconds.
 *
 * A single flat density would give an even sprinkle everywhere, which reads as texture rather
 * than as woodland and has no gaps to make it interesting.
 */
export const FOREST = {
  /**
   * Distance between tree slots, in world units. One tree per slot at most.
   *
   * Has to stay comfortably wider than the widest crown, or trees would overlap and merge into
   * the canopy this biome exists to avoid.
   */
  TREE_SPACING: 58,

  /**
   * How many slots across each square group shares one biome-weight sample.
   *
   * The biome field varies over 14,000 units by design, so sampling it every 58 units would be
   * many times more expensive for an answer that cannot have changed. At 4 that is one sample
   * per 232 units, which is still far finer than the field it is measuring.
   */
  WEIGHT_SAMPLE_SLOTS: 4,

  /**
   * Wavelength of the stand field, in world units — roughly the distance from the middle of one
   * stand to the middle of the next clearing.
   *
   * 720 is deliberately small. At cruise speed that is under three seconds, so trees and gaps
   * alternate constantly rather than resolving into one large mass.
   */
  STAND_WAVELENGTH: 720,

  /** Octaves in the stand field. Two gives stands an irregular edge without fragmenting them. */
  STAND_OCTAVES: 2,

  /**
   * Stand field value below which nothing grows.
   *
   * **This is the clearings dial.** Raise it for wider open ground between stands; lower it and
   * the gaps start closing up.
   */
  STAND_THRESHOLD: 0.42,

  /**
   * How far above the threshold the stand field has to rise before a stand is at full density.
   *
   * Deliberately narrow. Dividing by the whole remaining range instead — the obvious choice —
   * produced a forest of 80 scattered trees per tile rather than stands: fBm concentrates around
   * the middle of its range and almost never approaches 1, so a full-range ramp leaves stand
   * strength stuck around 0.1 everywhere, and raising that to a power made it worse.
   *
   * A narrow ramp makes the field close to a switch: inside a stand nearly every slot fills, and
   * outside it nothing does. That is what gives dense clumps with genuinely empty ground between
   * them, rather than an even sprinkle of trees over everything.
   */
  STAND_RAMP: 0.1,

  /**
   * Exponent applied to stand strength after the ramp. Above 1 keeps stand edges thin and their
   * middles dense, so a stand has a recognisable shape rather than fading evenly out.
   */
  STAND_SHARPNESS: 1.3,

  /** Chance a slot inside a full-strength stand holds a tree. Below 1 so even dense stands breathe. */
  BASE_DENSITY: 0.85,

  /**
   * Exponent applied to the forest's biome weight before it scales density. Same role as the
   * city's: makes the edge of a forest region thin out faster than linearly.
   */
  DENSITY_FALLOFF: 1.8,

  /** Shortest and tallest tree at full weight, in world units. */
  MIN_HEIGHT: 30,
  MAX_HEIGHT: 78,

  /** Above 1 makes tall trees rarer, so a stand has a few standouts rather than one flat top. */
  HEIGHT_SKEW: 1.6,

  /** Crown radius as a fraction of tree height. */
  CROWN_RADIUS_RATIO: 0.26,

  /**
   * Hard cap on crown radius, as a fraction of TREE_SPACING.
   *
   * Without this, raising MAX_HEIGHT far enough would make crowns wider than the gap between
   * slots and neighbouring trees would merge — the canopy this biome exists to avoid, arrived at
   * by tuning an unrelated number. Capping here means the no-overlap guarantee survives any
   * combination of height and spacing values.
   */
  CROWN_MAX_FRACTION: 0.34,

  /**
   * Smallest clear distance between two neighbouring crowns, in world units.
   *
   * What makes trees read as individually distinct rather than as foliage. Position jitter is
   * limited to whatever slack remains after reserving this, exactly as building jitter is
   * limited by street width.
   */
  MIN_TREE_GAP: 8,

  /** Height fraction where the crown starts — below it is bare trunk. */
  TRUNK_RATIO: 0.26,

  /** Trees at least this tall, in world units, get a second narrower crown tier. */
  TIER_MIN_HEIGHT: 74,

  /** Height fraction of the second tier's ring. */
  TIER_HEIGHT: 0.62,

  /**
   * Fraction of trees drawn in the warm accent colour instead of green.
   *
   * The steering file allows yellow accents, and a handful of amber trees scattered through a
   * green stand is what stops a patch of forest reading as one flat mass. Kept low: this is
   * meant to be noticed, not to dominate.
   */
  ACCENT_CHANCE: 0.05,

  /**
   * Steepest ground a tree will grow on, as rise over run across its own crown.
   *
   * Far more permissive than the city's limit — trees grow on hillsides, towers do not.
   *
   * **Derived, not guessed.** The crown ring is horizontal, sitting `TRUNK_RATIO` of the tree's
   * height above its base and reaching `CROWN_RADIUS_RATIO` of that height outwards. So ground
   * rising faster than `TRUNK_RATIO / CROWN_RADIUS_RATIO` — currently 1.0 — swallows the uphill
   * corner of the ring and the tree looks half-buried. This has to stay below that ratio; 0.9
   * leaves a margin.
   */
  MAX_GROUND_SLOPE: 0.9,

  /**
   * Height of the tallest ground swell, in world units. Between open ground and the mountains:
   * forested hills, with enough relief to give the stands something to drape over.
   */
  MAX_ELEVATION: 320,

  /** Wavelength of that swell in world units. */
  WAVELENGTH: 4200,

  /** Octaves for the ground. */
  OCTAVES: 3,

  /** Amplitude falloff per octave. */
  PERSISTENCE: 0.45,

  /** Frequency multiplier per octave. */
  LACUNARITY: 2.13,

  /** Thumb on the scale for how much of the world is forest. */
  SUITABILITY_BIAS: 0,
};

// ---------------------------------------------------------------------------
// Palettes — the primary "how does it look" dial
// ---------------------------------------------------------------------------

/**
 * Each biome maps elevation to a colour gradient: `low` is used in valleys, `high` on
 * peaks, interpolated by normalised elevation.
 *
 * Brightness matters as much as hue here. BLOOM.THRESHOLD decides what glows, so dim
 * colours stay calm and bright ones bloom. Keeping valley colours dark and peak colours
 * bright means ridges glow while low ground recedes, which does a lot of the visual work.
 */
export const PALETTE = {
  mountains: {
    /** Deep blue valley floors. Deliberately dark enough to sit below the bloom threshold. */
    LOW: 0x1030a0,
    /** Hot pink crests, bright enough to bloom. Blue-to-pink passes through violet. */
    HIGH: 0xff54c8,
  },

  baseline: {
    /**
     * Dark indigo hollows. Darker than the mountains' low end, so open ground recedes
     * further and the eye is drawn to whatever is on the horizon.
     */
    LOW: 0x14224e,
    /**
     * Soft teal-green swells. Green rather than pink is the clearest way to signal a change
     * of terrain without raising the intensity, and the steering file allows green accents.
     * Kept dimmer than the mountains' pink so leaving a range reads as calming down.
     */
    HIGH: 0x38c49c,
  },

  canyon: {
    /**
     * Near-black at the canyon floor. Deeper than any other biome's low end, because a gorge floor is genuinely in
     * shadow and the darkness is what sells the depth.
     */
    LOW: 0x241218,

    /**
     * Dusty orange on the rim. Warm like the mesas but a shade duller and clearly darker, so the two related rock
     * biomes stay separable while still reading as the same country.
     */
    HIGH: 0xc9764e,
  },

  mesa: {
    /**
     * Deep plum shadow at the cliff base.
     *
     * Warm and very dark, so canyon floors recede and the eye is drawn up to the terraces. Ties to the palette's
     * violet family rather than being an unrelated brown.
     */
    LOW: 0x2e1a30,

    /**
     * Sunlit terracotta on the tops. The one warm earth tone in the world.
     *
     * Because elevation here is quantised, colour comes out **banded** — each terrace its own shade, which reads
     * as sedimentary strata. That was a free consequence of quantising rather than a designed effect.
     *
     * Kept below a mountain crest in brightness: mesas are dramatic in *shape*, and letting them also be the
     * brightest thing would leave the mountains with nothing of their own.
     */
    HIGH: 0xd98a6a,
  },

  rollingLakes: {
    /**
     * Deep soft green in the hollows.
     *
     * Warmer than the glacial lakeland's slate, which is the point: same water, quiet lowland instead of scoured rock.
     */
    LOW: 0x152e30,

    /** Soft sea green on the rises. Muted, so the lakes keep the eye. */
    HIGH: 0x74c2a4,
  },

  woodedLakes: {
    /**
     * Dark green hollows, deliberately close to the forest's ground.
     *
     * The trees and the water both carry their own gradients here, so the ground's only job is to recede behind them.
     */
    LOW: 0x102a26,

    /** Muted green on the rises. Kept dimmer than the trees, the same trick the plain forest uses. */
    HIGH: 0x3f8f74,
  },

  lakeland: {
    /**
     * Cold slate in the hollows between the water.
     *
     * Cooler and bluer than the river valley's green, because this is glacier country rather than watered lowland — the
     * two are the only water biomes and they need telling apart from the air.
     */
    LOW: 0x172c3e,

    /**
     * Pale blue-grey on the hummock tops.
     *
     * Desaturated on purpose: the lakes carry their own bright gradient, and ground competing with them would turn a
     * field of small waters into visual noise.
     */
    HIGH: 0x7fa8bd,
  },

  river: {
    /**
     * Deep blue-green in the valley bottoms.
     *
     * Cool and dark, so the dry floodplain recedes and the water drawn over it carries the eye. The water has its own
     * gradient entirely — see `WATER_PALETTE`.
     */
    LOW: 0x14263a,

    /**
     * Soft sage on the hill shoulders.
     *
     * Muted deliberately, the same reasoning as the forest's ground: the subject of this biome is the river, and a
     * bright hillside would compete with it. Green rather than violet so it reads as low, watered country rather than
     * as rolling hills.
     */
    HIGH: 0x63a88c,
  },

  badlands: {
    /**
     * Cold near-black in the gully bottoms.
     *
     * Cooler than mesa's plum and canyon's maroon on purpose. All three are eroded rock and they need telling
     * apart at a glance; the warm two are the *sunlit* rock family and this one is the bleached one.
     */
    LOW: 0x1f1d2e,

    /**
     * Pale, chalky, barely-pink white on the crests.
     *
     * **Desaturation is the identity here.** Real badlands are Brule and Chadron clay — bone white with faint
     * pink and ochre banding — which is exactly what distinguishes them from red rock country. So this is the one
     * high colour in the world that is nearly neutral rather than saturated.
     *
     * Bright enough to bloom, which matters more than usual: since the full gradient repeats across every ridge,
     * a field of crests picks out the network of ridgelines and the gullies fall away to nothing.
     */
    HIGH: 0xe6c6d6,
  },

  farmland: {
    /** Dark olive hollows. Warm and low, so the ground recedes and the field pattern reads on top of it. */
    LOW: 0x1b2a18,
    /**
     * Dry yellow-green rises. The steering file allows green accents, and this is the one place a warm
     * green belongs — it says "worked land" where the forest's blue-green says "trees".
     */
    HIGH: 0x6f9e42,
  },

  hills: {
    /**
     * Deep violet hollows. Darker than the mountains' valley blue and clearly not baseline's indigo, so
     * the two kinds of open ground are separable even from a distance.
     */
    LOW: 0x241a52,
    /**
     * Soft lavender crests. Violet sits between the palette's blue and pink ends, which keeps it in
     * family while being used nowhere else.
     *
     * Deliberately dimmer than the mountains' hot pink: hills should look calm, and brightness is what
     * signals drama here because it decides what blooms. The first attempt at this lavender came out
     * marginally *brighter* than a mountain crest, which inverted the signal entirely.
     */
    HIGH: 0x9a78e0,
  },

  city: {
    /** Dark cyan for the ground and the base of every tower. */
    LOW: 0x0d3a56,
    /**
     * Bright cyan for the tops. Cyan rather than the mountains' pink so the two biomes are
     * separable at a glance even at fog distance.
     */
    HIGH: 0x54e8ff,
  },

  forest: {
    /** Dark blue-green hollows. */
    LOW: 0x0e2a3c,
    /**
     * Muted blue-green for high ground. Deliberately restrained: the *trees* carry this biome's
     * identity, and ground bright enough to compete with them would leave the forest reading as
     * one green mass rather than as trees standing on something.
     */
    HIGH: 0x2f7f6a,
  },

  industrial: {
    /**
     * Near-black slate, so the plant standing on it carries everything.
     *
     * **Deliberately kept a shade above the canyon's floor**, which holds the claim to the darkest ground in the world
     * and has a test saying so. A shadowed gorge earns that more than an estate does, and industry does not need it:
     * what makes the plant read is the hot amber it is drawn in, not how black the ground under it is.
     */
    LOW: 0x171f26,
    /**
     * Dim warm grey for high ground. Restrained for the same reason the forest's and suburbia's are: the *structures*
     * carry this biome, in a hot amber ramp, and ground bright enough to compete would swallow them.
     */
    HIGH: 0x4a4a44,
  },

  suburbia: {
    /** Dark blue-grey. Cooler and duller than the city's cyan, so a town's ground recedes beside a downtown. */
    LOW: 0x14212e,
    /**
     * Muted steel for high ground. Restrained for the same reason the forest's is: the *buildings* carry this
     * biome, drawn in a warm sodium ramp, and ground bright enough to compete would swallow a town of small
     * low roofs.
     */
    HIGH: 0x46606f,
  },

  vineyard: {
    /**
     * Dark slate-green for the foot of the hillside.
     *
     * Muted for the same reason farmland's ground is: **the worked texture carries this biome**, and ground
     * bright enough to compete would swallow a hillside covered in fine contour lines.
     */
    LOW: 0x1c2a24,

    /**
     * Warm stone for the upper slopes.
     *
     * A hillside terraced for vines is dry ground with rock showing through, and warming the top end is what
     * separates it from the forest's cool green — the two would otherwise be neighbours in the same hue.
     */
    HIGH: 0x6b6a4a,
  },

  park: {
    /**
     * Near-black plum for the fairground floor.
     *
     * **Darker than any built biome except the canyon**, and deliberately. A park is a flat site carrying a dozen
     * bright rides, so the ground has one job: to be the black the rides are lit against. Anything brighter and the
     * midway reads as a texture rather than as lights.
     *
     * Held just above the canyon's gorge floor, which has a tested claim to being the darkest ground in the world.
     * The first value here was 0x1a1020 and undercut it, exactly as industry's first value did — the canyon's darkness
     * is what makes a gorge read as a cut into the land, and it should not be quietly taken by a fairground car park.
     */
    LOW: 0x1e1428,

    /** Dusty violet for the low rises around the site, keeping it in the pink family without competing. */
    HIGH: 0x5c4470,
  },
};

/**
 * Gradient for the trees themselves, distinct from the ground they stand on.
 *
 * A biome may declare a `structurePalette` separate from its terrain `palette`; the city uses
 * one gradient for both, the forest does not. Trees need to read as clearly brighter than the
 * hillside beneath them or the whole biome flattens into a single colour.
 */
export const FOREST_TREE_PALETTE = {
  /** Deep green at the trunk. */
  LOW: 0x14614a,
  /** Bright spring green at the tip, well above the bloom threshold so crowns glow. */
  HIGH: 0x5cf0a0,
};

/**
 * Structures use their biome's gradient, but interpolated along their *own* height rather
 * than along ground elevation. A tower is dark at street level and bright at the roof, which
 * is what gives a skyline depth and makes tall buildings read as tall.
 *
 * Kept separate from `PALETTE` because these apply to structures across every biome, not to
 * any one biome's terrain.
 */
/**
 * Gradient for farmland's field boundaries and windpumps.
 *
 * Separate from the farmland ground for the same reason the forest's trees are: the ground is deliberately
 * muted so the *pattern* carries the biome, and one shared gradient would leave both the same colour and
 * the quilt invisible.
 *
 * Bright enough to bloom, because these lines are the subject rather than the setting.
 */
/**
 * Gradient for a water surface, which is the only thing in the world drawn *across* the ground rather than on it.
 *
 * Interpolated by **depth** rather than by height, which is the trick that stops a river reading as a flat plane: the
 * middle of the channel is deepest and therefore brightest, so the ribbon has a lit core that fades out at the banks.
 * A single flat colour over a flat surface is exactly the uniform expanse the steering file warns about.
 */
export const WATER_PALETTE = {
  /** Almost the background at the waterline, so the edge dissolves into the bank rather than drawing a hard outline. */
  LOW: 0x12314a,

  /** Bright cyan-white down the deepest line of the channel. Well over the bloom threshold, so the river glows. */
  HIGH: 0x7fe9ff,
};

/**
 * Gradient for telescope dishes, which have to read as machinery standing on grass.
 *
 * Rolling hills is violet and lavender. Dishes drawn in that gradient would look like an outcrop of the hillside,
 * and the entire point of an array is that it is *built*. So they get their own cool steel-to-ice ramp, the same
 * trick the forest uses to stop its trees dissolving into its ground.
 */
export const TELESCOPE_PALETTE = {
  /** Dark steel at the mount feet, so the legs recede and the bowl carries the shape. */
  LOW: 0x2a3f6a,

  /**
   * Pale ice blue at the rim.
   *
   * Bright enough to bloom. The rim is the outermost and highest ring of the bowl, so the gradient picks out exactly
   * the circle that makes a dish legible, and the rings behind it fall away.
   */
  HIGH: 0xbfe4ff,
};

export const FARMLAND_LINE_PALETTE = {
  /** Rows and hedges on low ground. Already well clear of the ground colour beneath them. */
  LOW: 0x4e8f5e,

  /**
   * Rows and hedges on high ground.
   *
   * **Green, not yellow.** The first version was a yellow-green and read as "yellow lines on the ground"
   * rather than as anything growing — the colour was doing as much damage as the missing rows were.
   *
   * Also kept below a mountain crest in brightness. These cover the whole ground plane, so their brightness
   * adds up across a whole view rather than at one landmark, and going bright risks the pileup that once
   * made cities read as a blur.
   */
  HIGH: 0x86d089,
};

/**
 * Gradient for a vineyard's vine rows.
 *
 * **Purple, because that is what is growing there.** Nothing else on open ground uses this hue: farmland is
 * green and the forest greener, so a green vineyard would have been indistinguishable from either.
 *
 * A narrow ramp rather than a wide one, and that is the second version. The first ran deep violet to pale
 * limestone so it could draw retaining walls at the bright end as well as rows at the dark end — and the bright
 * lines ended up being the only thing anyone saw, with the terraced ground beneath reading as a smooth hill
 * with stripes on it. The terracing is the biome; these rows are a note about what is planted on it, so they
 * stay within one colour and one narrow range of brightness.
 */
export const VINEYARD_LINE_PALETTE = {
  /** Deep grape violet. */
  LOW: 0x5b3f7a,

  /** A lighter violet for the same rows. Enough variation that a bench is not one flat colour, and no more. */
  HIGH: 0xa876d8,
};

/**
 * Gradient for a theme park's rides, which are the one thing in this world allowed to look festive.
 *
 * Every other structure palette in the project is a *cool* ramp, or amber for industry. A park is lit for pleasure
 * rather than for work, so it gets the pink the steering file names alongside blue — and pink against the city's cyan
 * and industry's amber keeps the three built regions apart at fog distance, which matters more than any of them
 * looking good alone.
 *
 * Held to two colours like every other ramp. A park is where it would be tempting to use six, and six colours over a
 * whole region is exactly the intensity the brief rules out.
 */
export const PARK_PALETTE = {
  /** Deep violet at ground level, where the paths and stall bases are. */
  LOW: 0x4a2a6b,

  /**
   * Hot pink at the top.
   *
   * The tall things in a park are its wheel rim, its tent peaks and its lift hill, so the bright end of the ramp lands
   * exactly on the silhouettes that identify it.
   */
  HIGH: 0xff8ad4,
};

// ---------------------------------------------------------------------------
// Terraced vineyard — a quantised hillside with contour rows
// ---------------------------------------------------------------------------

/**
 * The terraced vineyard: a hillside cut into agricultural steps, planted along the contours.
 *
 * ## Why this biome is shaped differently from the last three
 *
 * The city, the industrial estate and the fairground are all block grids of *structures* on flat ground. This
 * one is the opposite in every respect: its identity is in the **terrain**, it needs a slope rather than
 * refusing one, and what it plants is a field of ground lines rather than objects. It is much closer to
 * farmland and mesa, and it reuses both — mesa's elevation quantisation, and farmland's idea of following the
 * contour.
 *
 * ## The one idea the whole biome rests on
 *
 * Rows and retaining walls are the **level curves of this biome's own elevation field**. That single decision
 * pays for itself three times: the rows curve around a spur and pinch into a gully with no path-following
 * code, they cross tile seams without either tile knowing, and they crowd together on steeper ground the way
 * real terracing does. See `world/contourLines.js`.
 *
 * The terrain is quantised at `TERRACE_STEP`, and the walls are drawn at contour levels that are exact
 * multiples of it — so **a wall lands on the lip of a riser by construction** rather than by being positioned
 * there.
 */
export const VINEYARD = {
  // --- the hillside --------------------------------------------------------

  /**
   * Height of the tallest slope this biome produces, in world units.
   *
   * Well above farmland's 145 and well under a mountain's 900. It has to be a genuine hillside — terraces
   * need something to climb — while staying a place you could plausibly work.
   *
   * Raised 360 → 480 with the terracing, so a hillside carries six or seven steps rather than four. The number
   * of visible steps is `MAX_ELEVATION / TERRACE_STEP`, and a staircase needs more than a few treads before it
   * reads as one.
   */
  MAX_ELEVATION: 480,

  /**
   * Wavelength of the hillside, plus its octaves and falloff.
   *
   * **The number that decides whether terraces resolve at all.** Terrace width in plan is the step height
   * divided by the slope, so a short wavelength means a steep hill means treads narrower than the 50-unit
   * terrain lattice — at which point the steps alias into noise rather than reading as steps. Long and smooth
   * is what makes the quantisation legible.
   */
  WAVELENGTH: 3200,
  OCTAVES: 3,
  PERSISTENCE: 0.4,
  LACUNARITY: 2.13,

  /**
   * Height of one terrace step, in world units.
   *
   * ## Why it nearly doubled after the first version was flown
   *
   * At 38 the terraces were technically there and nobody could see them: what read instead was the vine rows,
   * and the ground under them looked like a smooth hill with stripes on it. **The step has to be large enough,
   * and its riser steep enough, that the terrain itself is the thing you notice.**
   *
   * Tread width in plan is `TERRACE_STEP * (1 - RISER_FRACTION) / slope`, and the riser's steepness is the
   * hillside's own slope divided by `RISER_FRACTION` — which is the number that was really wrong. Measured at
   * the median hillside slope:
   *
   * | step | riser fraction | tread | riser width | riser slope |
   * |------|----------------|-------|-------------|-------------|
   * | 38   | 0.34           | 3.1 cells | 1.6 cells | 0.48 — a ramp |
   * | 72   | 0.10           | 7 cells   | 0.8 cells | **1.6 — a cliff** |
   *
   * A riser under one lattice cell wide is drawn as a one-cell cliff, which is the sharpest edge the terrain
   * can express and exactly what makes mesa's tables read as tables. Seven cells of tread then reads as a
   * genuine bench rather than as the top of a slope.
   */
  TERRACE_STEP: 72,

  /**
   * How much of each step's height is spent on the riser, as a fraction.
   *
   * The rest is flat tread.
   *
   * **This is the dial that decides whether a terrace is a step or a ramp**, and it is not obvious from the
   * name: the riser's slope is the hillside's own slope *divided* by this. At 0.34 a riser was under three
   * times as steep as the hill it interrupts, which is a ramp; at 0.10 it is ten times, which is a cliff.
   *
   * **Not zero, though.** A hard floor leaves a discontinuity in the field, and the terrain gradient is read by
   * the flight floor and by every structure that asks about slope. Easing it over a tenth of the step keeps the
   * field continuous while still being narrower than one lattice cell, so it is drawn as a one-cell cliff —
   * the sharpest edge the terrain can express either way. Mesa reaches the same place from the other end.
   */
  RISER_FRACTION: 0.1,

  /**
   * A gentle undulation added on top of the terraces, and its wavelength.
   *
   * **A perfectly flat tread reads as a void in wireframe** — the same reason farmland is not level. Kept far
   * below `TERRACE_STEP` so a terrace is never in doubt, exactly as mesa keeps its floor relief under its step
   * height.
   */
  RELIEF: 12,
  RELIEF_WAVELENGTH: 850,

  /** Thumb on the scale for how much of the world is vineyard. Neutral: it takes what its field earns. */
  SUITABILITY_BIAS: 0,

  // --- the planting --------------------------------------------------------

  /**
   * How many vine rows are planted across each terrace tread.
   *
   * Contour levels are spaced `TERRACE_STEP / ROWS_PER_TERRACE` apart, and the level that lands exactly on a
   * terrace boundary is skipped — so four gives **three rows of vines across each tread**.
   *
   * ## The lip line was removed, and that is the point of the second version
   *
   * The first version drew a bright limestone contour on every terrace boundary, on the theory that a retaining
   * wall is what identifies a terrace. Flown, that was backwards: the lines were all anyone saw, and the ground
   * beneath them read as a smooth hill with stripes on it. **The terrain has to be the thing you notice**, so
   * the boundary line is gone and what remains is a few rows of vines lying on each flat bench — which is what
   * a line of purple on a step is for.
   */
  ROWS_PER_TERRACE: 4,

  /**
   * Spacing of the lattice the contours are traced on, in world units.
   *
   * Matched to the terrain's own lattice spacing on purpose. Finer would trace a smoother curve than the
   * ground it is drawn on can actually show, which costs segments for detail that cannot appear.
   */
  GRID: 50,

  /**
   * How far the lines sit above the ground, in world units.
   *
   * A line at exactly surface height fights the terrain wireframe for the same depth and flickers at
   * distance — the same z-fighting the occlusion masks needed a bias for. A vine row also stands off the
   * ground in reality, so a few units reads as planting rather than as paint.
   */
  ROW_LIFT: 7,

  /**
   * Where the rows sit on the colour ramp, and how much that varies between the rows on one tread.
   *
   * The tint is carried per vertex rather than derived from height, which is what the shared emitter is for.
   * With the boundary lines gone there is only one kind of line left, so the variation is small — just enough
   * that three rows on a bench are not a single flat colour.
   */
  ROW_TINT: 0.45,
  ROW_TINT_VARIATION: 0.3,

  /**
   * The slope band that gets planted, as rise over run.
   *
   * Both ends matter and for opposite reasons. **Below the minimum there is nothing to terrace** — contour
   * spacing goes to infinity on flat ground, so the rows would be absurdly far apart and would not read as
   * planting at all. **Above the maximum nobody plants**, and the treads would be narrower than the lattice
   * can draw.
   */
  MIN_SLOPE: 0.07,
  MAX_SLOPE: 0.6,

  /**
   * The field deciding which parts of a hillside are planted, and the band over which it takes effect.
   *
   * Without this the whole region is under vine, which is both too much line and untrue: a real hillside is
   * planted in blocks separated by scrub, rock and tracks. The same reasoning as the forest's stand field, and
   * the threshold is set the same way — **measured against the range the field actually occupies**, since fBm
   * never approaches its own extremes.
   */
  PLANTED_WAVELENGTH: 1800,
  PLANTED_OCTAVES: 2,
  PLANTED_THRESHOLD: 0.38,
  PLANTED_RAMP: 0.1,

  /**
   * How much higher the planted threshold climbs as biome weight falls to nothing.
   *
   * **This is the thinning, and it has to work by raising the bar rather than by scaling the field.** The first
   * version multiplied the planted field by the weight and tested for greater than zero, which thins nothing —
   * a positive field times a positive weight is still positive, so planting stopped only where the weight was
   * exactly zero and a region ended at a hard line. Raising the threshold means only the most suitable ground
   * stays planted at a border, which is what a region's edge should look like.
   */
  EDGE_TIGHTEN: 0.14,

  // --- the winery ----------------------------------------------------------

  /**
   * Grid the estate buildings are placed on, in world units, and the chance one appears.
   *
   * One winery to an estate, sited on a tread rather than a riser. Rare enough that finding one is an event,
   * and it is the only thing in the biome with a roof — which is what makes the rows read as *cultivated*
   * rather than as a pattern in the ground.
   *
   * **Measured and made much rarer.** At a 760-unit estate and a 0.4 chance a tile held two thirds of a
   * winery, so there was one every other tile — which makes it scenery rather than a find. These numbers put
   * it at roughly one in three tiles.
   */
  ESTATE_SIZE: 900,
  WINERY_CHANCE: 0.3,

  /** Steepest ground a winery will stand on. Tight, so it lands on a tread and not across a step. */
  WINERY_MAX_SLOPE: 0.1,

  /** The house, the cellar beside it, and the tank at its end, in world units. */
  HOUSE_WIDTH: 30,
  HOUSE_DEPTH: 22,
  HOUSE_WALL_HEIGHT: 16,
  HOUSE_ROOF_HEIGHT: 12,
  CELLAR_WIDTH: 54,
  CELLAR_DEPTH: 28,
  CELLAR_WALL_HEIGHT: 18,
  CELLAR_ROOF_HEIGHT: 13,
  TANK_RADIUS: 9,
  TANK_HEIGHT: 26,
  TANK_SIDES: 8,
};

// ---------------------------------------------------------------------------
// Theme park — the fairground ride shapes
// ---------------------------------------------------------------------------

/**
 * Every dimension of a theme park's rides, in world units.
 *
 * ## What has to read from the air, and what does not
 *
 * A park is identified by **three silhouettes**: a wheel, a peaked tent, and a lift hill. Nothing else on a
 * real fairground has a profile anyone would recognise at a distance — the stalls, halls and pavilions are
 * low boxes, and their job is to be the dense low mass those three stand out of.
 *
 * So the tall things are tuned to be unmistakable and the rest is tuned to be cheap.
 *
 * ## Why a park is deliberately low
 *
 * The city runs 90–550 and industry 340–470. A park's tallest ride is the wheel at 240 and its drop tower at
 * 260, with everything else under 40. That gives the region a **flat profile broken by three or four
 * spikes**, which is a different shape on the horizon from either of the other built biomes — and the
 * cheapest way to make a new region recognisable is to give it a profile the others cannot produce.
 */
export const PARK = {
  // --- the ground, and how the site is laid out ----------------------------

  /**
   * Highest ground this biome produces, in world units.
   *
   * Low and gentle, like industry's, and for a sharper reason: a fairground is a *levelled site*. A coaster
   * circuit is three hundred units across and has to look like it was built on flat land, not draped over a
   * hill.
   */
  MAX_ELEVATION: 130,

  /** Wavelength of that ground, plus its octaves and falloff. Long and soft. */
  WAVELENGTH: 2900,
  OCTAVES: 3,
  PERSISTENCE: 0.45,
  LACUNARITY: 2.13,

  /** Thumb on the scale for how much of the world is fairground. Neutral: it takes what its field earns. */
  SUITABILITY_BIAS: 0,

  /**
   * Edge of one slot in a park's layout, in world units.
   *
   * **Set by the largest ride, measured rather than guessed.** Every ride is laid out in the park's own rotated
   * frame, so what has to fit is its bounding *circle* — the lesson industry learned when a tank farm's corner
   * tanks overran its block. The big top is the worst case here at 167 units of reach, against the 180 half a
   * slot gives. That fit is the entire no-overlap guarantee, since there is no collision checking anywhere in
   * this project, so a test measures it rather than trusting it.
   */
  SLOT_SIZE: 360,

  /**
   * Slots per axis in a park's core.
   *
   * Three by three is nine, which is exactly the number of distinct rides — so **a park holds one of each, and
   * the layout is a shuffle rather than a set of rolls.** That is the whole shape of the second version: what
   * was reported was a region with an entrance arch here and a shooting gallery there, and a fairground is not
   * a thing that comes in pieces.
   */
  SLOTS_PER_AXIS: 3,

  /**
   * Wavelength of the fairground's *suitability* field, in world units — how big one fairground region is.
   *
   * **Far shorter than every other biome's 28,000, and this is the number that fixes the reported bug.** What
   * was flown was a fairground region holding several complete fairgrounds a few thousand units apart, which
   * does not read as a big fairground, it reads as a mistake. A fairground is one site. Its region therefore
   * has to be about the size of one site, and no biome-sized region could ever be.
   *
   * ## Measured as a region is actually flown
   *
   * Counting patches is misleading here — a noise field throws off a great many tiny slivers that drag any
   * median down — so what was measured is the **area-weighted width**: every patch of park-led ground counted in
   * proportion to how much ground it covers, which is what a flyer meets. At 11,000 that comes out 3,500 to 3,900
   * across four seeds, against 6,300 for city, 6,300 for mountains and 7,300 for industry. So a fairground region
   * is a little over half the width of an ordinary one, and about twice the park standing in it.
   *
   * Going further does not work. At 8,000 the full-weight interiors are **narrower than the park itself** and the
   * world held four parks in an area 128,000 units square — the feature was effectively gone. 9,000 was still
   * bad, at five to seven.
   *
   * Shortening this does **not** change how much of the world is fairground. Squeezing the domain of a noise
   * field leaves its distribution of values alone, so the field still beats its rivals exactly as often; the
   * same total area is simply cut into many more, much smaller patches. How rare a park is comes from
   * `PEAK_REACH` instead.
   */
  FIELD_WAVELENGTH: 11000,

  /**
   * How far apart candidate park sites sit, in world units.
   *
   * Comfortably smaller than a region, so a region contains candidates at all. **This was the real reason the
   * first attempt built almost nothing**: at a 2,600 spacing, a region only a couple of thousand units across
   * frequently contained no candidate centre whatsoever, so it was not that the sites were rejected — there were
   * none to reject. It has to be several times finer than the smallest region worth building in.
   *
   * Not the dial for how far apart parks are. `PEAK_REACH` is, and keeping the two separate is what lets this
   * one be as fine as it needs to be.
   */
  SITE_SPACING: 1200,

  /**
   * Closest two parks may ever come, in world units.
   *
   * **This is what makes it one park per region, and it is the dial for how rare a park is.** A candidate holds
   * its region's park only if its suitability beats every other candidate within this distance. A region is a
   * patch where the fairground's suitability leads its rivals, and a smooth field over a patch has a single
   * peak, so that rule picks one cell per region without any code needing to find where a region ends — and it
   * guarantees the spacing outright, whatever shape the field happens to take.
   *
   * Set to rather more than a region so that when two small regions sit close together only the better of them
   * gets the park. That is the difference between "one per region" and what was actually reported, which was
   * several complete fairgrounds within sight of each other.
   *
   * **7,500 because `CAMERA.FAR` fog reaches black at 7,000**, so this is the shortest distance at which two
   * parks can never be in view at once — which is the property that matters, and a more honest reason than a
   * round number. Longer costs parks for nothing visible: measured across four seeds, raising it to 9,000 took
   * the world from 12–19 parks per 128,000 square down to 9–15. The gap the terrain checks actually leave is far
   * wider than the floor in any case — the closest two parks came on any seed measured was 10,253.
   *
   * Costs `(2 × ceil(PEAK_REACH / SITE_SPACING) + 1)²` suitability samples for the first candidate on a tile,
   * then almost nothing: neighbouring candidates share nearly all of that neighbourhood and it is memoised for
   * the length of the build. Tiles with no fairground anywhere near them pay nothing at all, because the weight
   * check runs first and is a single sample.
   */
  PEAK_REACH: 7500,

  /**
   * How far a site's centre may wander inside its cell, as a fraction of the spacing.
   *
   * Enough to hide the lattice, and no more: it comes straight off the minimum gap between two parks, so this and
   * `SITE_SPACING` have to be checked together. A test does that rather than trusting the arithmetic here.
   */
  SITE_JITTER: 0.1,

  /**
   * How many extra small pieces ring the core, and how far out they sit as a fraction of the core's half-width.
   *
   * The randomness the report asked for, kept where it cannot strand anything: a couple of arches and stall
   * rows around the edge of a park that is already complete. Every one of them is within sight of the wheel.
   */
  MIN_FRINGE: 2,
  MAX_FRINGE: 5,
  FRINGE_REACH: 1.35,

  /** Which rides may appear in the fringe. Small things only — a landmark out there would look stranded. */
  FRINGE_RIDES: ['gate', 'stallRow', 'hall', 'carousel'],

  /**
   * The coaster variants, of which a park builds exactly one.
   *
   * They are alternatives rather than two attractions: the same kind of thing built two ways. Building both would
   * be the single duplication in an otherwise one-of-everything layout, and choosing between them is what makes
   * two parks worth visiting. It is also what makes nine rides fit nine slots exactly.
   */
  COASTER_VARIANTS: ['coaster', 'coasterLoop'],

  /**
   * Steepest ground a park will stand on, as rise over run.
   *
   * **Measured against this biome's own terrain, which is what a site actually stands on.** Over a pure park
   * region the steepest slope the ground reaches is 0.19, so a limit of 0.18 sat just *below* the ceiling and
   * threw away sites for no reason. 0.25 clears it while still refusing anything a fairground could not be
   * levelled onto.
   */
  MAX_GROUND_SLOPE: 0.25,

  /**
   * How much of the biome a site needs under it before a park is built there.
   *
   * **Not "any weight at all", which is what it was and which nearly removed the feature.** Elevation is a
   * weighted sum, so at a low park weight the ground is mostly a neighbour's — and a neighbour can be a mountain.
   * Measured across 841 lattice cells of real world: of the cells with *any* park weight only 17% were level
   * enough for a park, and the result was five parks in an area 128,000 units square.
   *
   * Requiring the site to be well inside its own region means the ground under it is the park's own flat terrain,
   * where 100% of sites are level enough. A fairground needs a levelled site, and only a region's interior has
   * one — so this is the honest rule rather than a workaround.
   *
   * ## Raised once the regions shrank
   *
   * Half was the balance point while a region was 28,000 units across, because half weight was still deep inside
   * one. Now that a region is a few thousand across and a park's footprint is 1,818, half weight leaves the
   * footprint hanging over the edge into whatever the neighbour is — and the slope check then threw away **more
   * than two thirds of the sites the peak rule chose**, which was the largest single loss of parks in the world.
   *
   * Measured over 11,236 candidate cells: at half weight, 673 cells qualify, 39 win their neighbourhood and only
   * 12 are level. Raising it cuts the candidates but *increases* the parks, because the ones left stand on ground
   * the biome actually owns. It turns over again at the top, where interiors are too small to hold a candidate at
   * all — at 0.99 the count falls back. Three quarters is the best of the sweep at this region size.
   */
  MIN_SITE_WEIGHT: 0.75,

  /** Clear space the park keeps from another biome's structures, in world units. */
  CLEARANCE: 34,

  /**
   * Probes per axis when checking a whole park's footprint for slope and for another biome's claim.
   *
   * A clearance test cannot be scaled up by growing its radius — the water clearance checks a centre and a *ring*
   * at whatever it is given and sees nothing in between — so a footprint is covered by many small overlapping
   * queries, and the only honest way to set their number is to measure the largest hole the layout leaves.
   *
   * Over this 1,818-unit footprint, with each query asking about a radius of a third of the spacing plus the
   * clearance:
   *
   * | probes per axis | 7 | 9 | **11** | 13 |
   * |---|---|---|---|---|
   * | worst hole, units | 79 | 51 | **34** | 23 |
   * | queries per site | 49 | 81 | **121** | 169 |
   *
   * **Seven was not enough and a test caught it**: a reservation sitting in a diagonal gap between probes was
   * missed, and a park was sited on top of it. Eleven is affordable in a way it would not have been for a block
   * grid — a site is rejected at its first failing probe, so only the handful of sites that are actually used pay
   * for all 121.
   */
  CLEARANCE_PROBES: 11,

  // --- the ferris wheel: the one silhouette that says fairground -----------

  /**
   * Radius of the wheel rim.
   *
   * The whole feature rests on this reading as a *circle* from the air, so it is the largest single curve in
   * the project. Below about 80 the polygon starts to read as a polygon.
   */
  WHEEL_RADIUS: 105,

  /** Sides in the rim polygon. Enough that it reads as round; more is invisible and costs two segments each. */
  WHEEL_RIM_SIDES: 24,

  /**
   * Spokes from hub to rim.
   *
   * Fewer than the rim has sides, so the spokes land on every other corner and the rim keeps its roundness
   * without the hub becoming a solid disc of lines.
   */
  WHEEL_SPOKES: 12,

  /** How far the two rims sit either side of the hub, which is what gives the wheel depth rather than being a disc. */
  WHEEL_RIM_OFFSET: 13,

  /** Radius of the hub the spokes converge on. */
  WHEEL_HUB_RADIUS: 9,

  /**
   * Clearance under the rim at its lowest point.
   *
   * Not decoration: it sets how high the hub sits, and a wheel whose rim touches the ground reads as a
   * water wheel. Real wheels load at about a metre.
   */
  WHEEL_CLEARANCE: 18,

  /** A gondola hanging under each spoke: how far below the rim, and how big. */
  WHEEL_GONDOLA_DROP: 11,
  WHEEL_GONDOLA_SIZE: 9,

  /** Half-spread of the A-frame legs at the ground, as a fraction of the radius. */
  WHEEL_LEG_SPREAD: 0.52,

  // --- the big top: three poles, because two looks like a marquee ----------

  /** Footprint of the tent. An oval, since a big top is longer than it is wide. */
  TENT_LENGTH: 250,
  TENT_WIDTH: 155,

  /** Height of the wall the roof sits on, and of the peaks above the ground. */
  TENT_EAVE_HEIGHT: 34,
  TENT_PEAK_HEIGHT: 118,

  /**
   * How many peaks.
   *
   * **Three, and it is the whole shape.** One cone is a circus tent in a cartoon; two read as a marquee at a
   * wedding; three in a row is unmistakably a big top, and it is the only tent profile that could not be
   * anything else.
   */
  TENT_PEAKS: 3,

  /** Sides in each peak's cone. Low: a tent is fabric over a few ribs, not a dome. */
  TENT_CONE_SIDES: 10,

  /** Radius of each peak's cone at the eave, as a fraction of half the tent width. Above 1 so the cones overlap. */
  TENT_CONE_SPREAD: 1.15,

  /** Sides in the oval wall. */
  TENT_WALL_SIDES: 16,

  /**
   * How far the scalloped valance dips between eave corners.
   *
   * The cheapest detail in the park and the one that does the most: a plain eave ring reads as a shed roof,
   * and the same ring with a dip in each span reads as canvas.
   */
  TENT_VALANCE_DROP: 7,

  /** A pennant on each peak. */
  TENT_FLAG_HEIGHT: 14,
  TENT_FLAG_LENGTH: 11,

  // --- roller coasters ------------------------------------------------------

  /** Plan extent of a coaster circuit. */
  COASTER_LENGTH: 300,
  COASTER_WIDTH: 170,

  /**
   * Height of the lift hill.
   *
   * The third of the park's three identifying silhouettes, and the reason it is this tall: a coaster below
   * about 100 disappears into the low mass of stalls and halls around it.
   */
  COASTER_LIFT_HEIGHT: 148,

  /** Height the humps after the first drop reach, as a fraction of the lift hill. */
  COASTER_HUMP_SCALE: 0.42,

  /** How many humps follow the first drop. Three is enough to read as a sequence; more is a picket fence. */
  COASTER_HUMPS: 3,

  /**
   * Steps the track is drawn in.
   *
   * The single dial for how much a coaster costs. At 72 a rail reads as a smooth curve; at 40 the drop
   * becomes visibly faceted, which on the one shape in the park that is *about* its curve is the wrong
   * saving to make.
   */
  COASTER_STEPS: 72,

  /** Distance between the two rails. Narrow, because a wide gauge reads as a bridge deck. */
  COASTER_GAUGE: 9,

  /** How many steps between cross ties, and between support columns. */
  COASTER_TIE_EVERY: 2,
  COASTER_SUPPORT_EVERY: 6,

  /** Radius of the vertical loop on the looping variant. */
  COASTER_LOOP_RADIUS: 36,

  // --- the drop tower -------------------------------------------------------

  /** Height of the mast. The tallest thing in the park, and the only one taller than the wheel. */
  TOWER_HEIGHT: 258,

  /** Half-width of the lattice mast, and how many braced panels it is built from. */
  TOWER_HALF_WIDTH: 8,
  TOWER_PANELS: 11,

  /** Where the car sits on the mast, as a fraction of its height, and how far its seats stick out. */
  TOWER_CAR_AT: 0.34,
  TOWER_CAR_REACH: 16,

  /** A crown at the top, so the mast terminates in something rather than just stopping. */
  TOWER_CROWN_RADIUS: 15,

  // --- the chair swing ------------------------------------------------------

  /** Height of the mast the canopy sits on. */
  SWING_HEIGHT: 92,

  /** Radius of the conical canopy, and how far it stands above the chairs. */
  SWING_CANOPY_RADIUS: 33,
  SWING_CANOPY_RISE: 16,

  /** How many chairs hang from it, and how far out and down they swing. */
  SWING_CHAINS: 12,
  SWING_REACH: 46,
  SWING_DROP: 26,

  /** Sides in the canopy cone. */
  SWING_CANOPY_SIDES: 12,

  // --- the carousel ---------------------------------------------------------

  /** Radius of the platform and of the conical roof above it. */
  CAROUSEL_RADIUS: 38,

  /** Height of the platform, the eave, and the roof apex. */
  CAROUSEL_PLATFORM_HEIGHT: 5,
  CAROUSEL_EAVE_HEIGHT: 26,
  CAROUSEL_PEAK_HEIGHT: 44,

  /** Sides in the platform and roof polygons, and how many poles run between them. */
  CAROUSEL_SIDES: 12,
  CAROUSEL_POLES: 12,

  /** A finial above the apex, which is what makes the roof read as a carousel rather than a gazebo. */
  CAROUSEL_FINIAL_HEIGHT: 12,

  // --- the low mass: stalls, halls and the entrance ------------------------

  /** A row of midway stalls: how many, and the size of each. */
  STALL_COUNT: 5,
  STALL_WIDTH: 24,
  STALL_DEPTH: 17,
  STALL_HEIGHT: 13,

  /** Gap between stalls, and how far the awning reaches out over the front. */
  STALL_GAP: 9,
  STALL_AWNING_REACH: 9,
  STALL_AWNING_DROP: 4,

  /** A wide low hall for bumper cars or an arcade. */
  HALL_WIDTH: 96,
  HALL_DEPTH: 62,
  HALL_EAVE_HEIGHT: 15,
  HALL_PEAK_HEIGHT: 27,

  /** How far the hall's scalloped eave dips, matching the tent's so the two read as the same site. */
  HALL_VALANCE_DROP: 5,

  /** The entrance: two towers with an arched sign between them. */
  GATE_SPAN: 84,
  GATE_TOWER_WIDTH: 15,
  GATE_TOWER_HEIGHT: 34,
  GATE_ARCH_RISE: 18,
  GATE_ARCH_STEPS: 10,
  GATE_SIGN_DEPTH: 9,
};

export const STRUCTURE_PALETTE = {
  /**
   * Colour for accent rings and antennas, shared by all biomes.
   *
   * A single warm colour against the cool terrain is what makes the detail read as *lit*
   * rather than as more wireframe. The steering file allows yellow accents, and this is the
   * one place in the world using one, which is precisely why it draws the eye.
   */
  ACCENT: 0xffd166,
};

// ---------------------------------------------------------------------------
// Building shapes — the city's silhouette vocabulary
// ---------------------------------------------------------------------------

/**
 * Parameters for each building archetype.
 *
 * ## Why archetypes differ in silhouette, not detail
 *
 * At chase-camera distance, outline is the only thing that reads — established when the aircraft
 * models were built, and the same is true of a tower two kilometres away. Adding window detail or
 * facade texture to a wireframe box does nothing at that range. Changing whether the outline is
 * stepped, tapered, twisted or round changes everything.
 *
 * So every archetype here is chosen because its *profile* is unmistakable, and each is drawn from
 * real architecture rather than invented.
 *
 * Review them in `debug/buildings.html` before changing any of these numbers — the page draws every
 * archetype side by side and in a mixed street, which is the only honest way to judge whether two
 * shapes are actually distinguishable.
 */
export const BUILDING = {
  /** Empire State, and 1920s New York zoning setbacks generally. A stepped wedding-cake mass. */
  SETBACK: {
    /** Number of stacked tiers. Two reads as a plinth; four or more starts to look like stairs. */
    TIERS: 3,

    /**
     * How much narrower each tier is than the one below, as a fraction of the base footprint.
     *
     * This is the dial for how dramatic the stepping is. Too small and it reads as a plain box with
     * scratches; too large and the top tier becomes a needle on a plinth.
     */
    SHRINK_PER_TIER: 0.18,
  },

  /** Transamerica Pyramid: a frustum, wider at the base. */
  TAPER: {
    /** Top footprint as a fraction of the base. Below about 0.2 it stops reading as a building. */
    TOP_SCALE: 0.34,
  },

  /** The Shard: tapering almost to a point. Same shape function, a much smaller top. */
  SPIRE: {
    /** Deliberately near zero. Not exactly zero, so the apex is still a small roof rather than a dot. */
    TOP_SCALE: 0.07,
  },

  /** Turning Torso, Shanghai Tower: each floor rotated slightly against the one below. */
  TWIST: {
    /**
     * Horizontal rings drawn up the tower, including the base and roof. The verticals connect
     * corresponding corners between consecutive rings, so this also sets how smooth the helix looks.
     */
    LEVELS: 5,

    /**
     * Rotation added at each level, in degrees. The whole effect. At 14 over five levels the tower
     * turns 56 degrees from base to roof, which is plainly a twist without looking broken.
     */
    DEGREES_PER_LEVEL: 14,
  },

  /** Marina City, the BT Tower: a round footprint among rectangular ones. */
  CYLINDER: {
    /**
     * Sides of the polygon standing in for a circle. Six is visibly faceted, twelve is smooth and
     * costs twice as much. Eight reads as round at flight distance.
     */
    SIDES: 8,
  },

  /** Unité d'Habitation: a broad thin plate that changes completely with viewing angle. */
  SLAB: {
    /**
     * Depth as a fraction of the footprint it was given, which is what makes it a slab rather than a
     * box. Edge-on it becomes almost a single line, and that transition is the point of the shape.
     */
    DEPTH_SCALE: 0.28,

    /** Horizontal floor bands. A bare slab has no scale cue; these supply it, as accent rings do. */
    BANDS: 4,
  },

  /** Petronas: a horizontal element between two verticals, which nothing else in the world has. */
  TWIN: {
    /** Gap between the two towers as a fraction of the total footprint width. */
    GAP_FRACTION: 0.22,

    /** Height of the sky bridge as a fraction of tower height. Around two thirds looks deliberate. */
    BRIDGE_HEIGHT_FRACTION: 0.62,

    /** Bridge depth as a fraction of tower height, so it reads as a structure rather than a wire. */
    BRIDGE_THICKNESS_FRACTION: 0.05,
  },

  /**
   * Eiffel, or a transmission pylon: an open braced frame.
   *
   * The one archetype where **the wireframe is the real structure**, so it needs no depth mask at
   * all — there is no solid surface to hide anything behind. That makes it cheaper than its segment
   * count suggests.
   */
  LATTICE: {
    /** Bracing tiers up the tower. Each adds a ring and a set of diagonals. */
    TIERS: 3,

    /** Top footprint as a fraction of the base. Pylons taper hard; 0.4 is recognisably a pylon. */
    TOP_SCALE: 0.4,
  },

  /** A stadium or arena: horizontal emphasis, to punctuate a skyline of verticals. */
  DOME: {
    /** Sides of the base polygon. */
    SIDES: 6,

    /** Latitude rings between the base and the apex. More is rounder and more expensive. */
    LATITUDE_RINGS: 2,
  },

  /**
   * The low block that most of a real city is actually made of.
   *
   * Easy to dismiss as filler, and it is the most important entry here: towers only feel tall when
   * there is something short beside them for scale.
   */
  PODIUM: {
    /** Inset of the roof band from the footprint edge, as a fraction. Reads as a parapet. */
    ROOF_BAND_INSET: 0.12,
  },
};

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export const INPUT = {
  /**
   * When true, pushing up pitches the nose *down*, the flight-sim and Star Fox
   * convention. Default false, so up climbs — the less surprising mapping for a casual
   * keyboard player, which is who this is for. Flip if it feels wrong.
   */
  INVERT_PITCH: false,

  /**
   * Key that steps to the next aircraft, wrapping at the end of the list.
   *
   * A `KeyboardEvent.code`, so it is layout independent. Well clear of the flight keys: pressing it
   * by accident mid-turn should be impossible.
   */
  CYCLE_AIRCRAFT_KEY: 'KeyC',

  /** Opens and closes the pause menu. */
  PAUSE_KEY: 'Escape',

  /**
   * Speed trim, one step per press.
   *
   * Bracket keys because they read as "less" and "more" without needing a label, and because nothing
   * on the flight controls is anywhere near them.
   */
  TRIM_DOWN_KEY: 'BracketLeft',
  TRIM_UP_KEY: 'BracketRight',

  /**
   * Tilt the camera further down, and back toward level.
   *
   * Comma and period, which carry a natural less-and-more sense and sit next to each other. Deliberately not the brackets,
   * which are the cruise trim — the two get adjusted in the same sort of experimental session and confusing them would be
   * easy.
   */
  CAMERA_TILT_DOWN_KEY: 'Period',
  CAMERA_TILT_UP_KEY: 'Comma',

  /**
   * Switch between the cab and the outside view, in train mode.
   *
   * V for view. Only does anything on the rails; in the air there is one camera and nothing to switch to.
   */
  TOGGLE_VIEW_KEY: 'KeyV',
};

// ---------------------------------------------------------------------------
// Flight model — THE FEEL. Every value here is a Task 5 checkpoint dial.
// ---------------------------------------------------------------------------

/**
 * Nothing in this section should respond instantly. The steering file is explicit that
 * every axis is damped and that a left turn is a slow bank into a turn, never a snap.
 *
 * Time constants are expressed as seconds to close roughly 63% of the remaining gap,
 * which is what a first-order exponential ease does in one time constant. Bigger values
 * are heavier and floatier; smaller values are twitchier. If any of these are lowered far
 * enough the aircraft starts to feel like a cursor, which is the failure mode to avoid.
 */
export const FLIGHT = {
  /**
   * Base cruise speed in world units per second, before trim. Carried over from the Task 4 rail,
   * which read as unhurried against the 2600-unit ridge wavelength.
   */
  CRUISE_SPEED: 260,

  /**
   * Slowest and fastest the speed trim may reach, as multipliers of CRUISE_SPEED.
   *
   * **The minimum is deliberately well above zero.** The requirements are explicit that there is
   * never a full stop: hovering would turn a flight into a photograph, and the whole experience is
   * built on continuous motion. 0.55 is a slow drift, 1.6 a brisk cruise — 143 to 416 units per
   * second at the base speed.
   */
  SPEED_TRIM_MIN: 0.55,
  SPEED_TRIM_MAX: 1.6,

  /**
   * How much one press of the trim keys moves the multiplier. Seven steps across the range, which is
   * few enough to reach either end quickly and enough to find a speed you like.
   */
  SPEED_TRIM_STEP: 0.15,

  /**
   * Seconds for actual speed to close most of the gap to the trimmed target.
   *
   * Long, because speed is the slowest thing an aircraft changes and because a snap between speeds
   * would be the most jarring possible violation of "nothing responds instantly". At 2.5 a trim
   * press reads as the world gradually gathering or losing pace.
   */
  SPEED_TIME_CONSTANT: 2.5,

  /**
   * Maximum bank angle in degrees. The requirements call for roughly 30, reached
   * gradually. This caps the *target* roll, so full deflection asks for 30 and the ease
   * approaches it without overshoot.
   */
  MAX_ROLL: 30,

  /**
   * Seconds for roll to close most of the gap to its target. This is the single most
   * important number for how the aircraft feels: it is the delay between pressing left
   * and the aircraft actually being in a turn.
   */
  ROLL_TIME_CONSTANT: 1.1,

  /**
   * Heading change in degrees per second when fully banked. Turning comes from bank, not
   * from a rudder, so this is what makes a turn a turn.
   *
   * 18 means a full 360 takes 20 seconds at full deflection and a 180 takes 10 — calm,
   * but not so slow that turning back to look at something becomes a chore.
   */
  TURN_RATE_AT_MAX_ROLL: 18,

  /** Maximum nose-up or nose-down angle in degrees. Kept modest so the horizon stays. */
  MAX_PITCH: 20,

  /** Seconds for pitch to close most of the gap to its target. Heavier than roll. */
  PITCH_TIME_CONSTANT: 1.4,

  /**
   * Closest the aircraft may come to the ground, in world units. A hard floor: the player
   * cannot crash, so this is never negotiable. It is a backstop rather than the usual
   * behaviour — FLARE_BAND normally keeps the aircraft away from it smoothly.
   */
  MIN_GROUND_CLEARANCE: 120,

  /**
   * Clearance range over which descent is progressively limited, in world units.
   *
   * Inside this band the allowed nose-down angle scales to zero as the ground approaches,
   * so holding a dive flares out into a level skim instead of hitting an invisible wall.
   * A bare position clamp would stop the aircraft dead with its nose still pointed down,
   * which is exactly the kind of snap this project does not permit.
   */
  FLARE_BAND: 400,

  /** Highest the aircraft may fly, in world units. The player cannot leave the world. */
  CEILING: 3400,

  /**
   * Altitude range below the ceiling over which climb is progressively limited, matching
   * FLARE_BAND at the other end. Same reasoning: no invisible walls.
   */
  CEILING_BAND: 500,

  /**
   * Starting altitude in world units. Matches the height the Task 3 look checkpoint was
   * approved at, so the first thing seen is the framing that was signed off.
   */
  START_ALTITUDE: 1200,
};

// ---------------------------------------------------------------------------
// Chase camera — also a Task 5 checkpoint dial
// ---------------------------------------------------------------------------

/**
 * Star Fox 64 style: above and behind, floating rather than rigidly attached. The lag is
 * the point. Pitching up should let the aircraft rise in frame before the camera catches
 * up and re-centres it.
 */
export const CAMERA = {
  /** How far behind the aircraft the camera settles, in world units. */
  DISTANCE_BEHIND: 260,

  /** How far above the aircraft the camera settles, in world units. */
  HEIGHT_ABOVE: 85,

  /**
   * Seconds for the camera to close most of the gap to its ideal position. This is the
   * float. Too small and the camera is welded on, killing the sense of the aircraft
   * moving independently; too large and it feels like being towed on a rope.
   */
  POSITION_TIME_CONSTANT: 0.42,

  /**
   * Where the aircraft sits vertically in frame during steady flight, as a fraction up
   * from the bottom. The requirement is 15–20%, leaving room below to descend into.
   *
   * Expressed as a screen fraction rather than a world-space offset so it stays correct
   * if RENDER.FIELD_OF_VIEW or the chase distance is retuned.
   */
  AIRCRAFT_SCREEN_HEIGHT: 0.19,

  /**
   * How far the camera is tilted down from level, in degrees.
   *
   * **Exists because at level the top half of the frame is empty black sky.** Permanent night means there is nothing up
   * there to look at, so aiming down trades sky for terrain: the horizon rises in the frame and the land fills more of it.
   *
   * Applied to the view direction, on top of the tilt that puts the aircraft at `AIRCRAFT_SCREEN_HEIGHT` — so raising this
   * also carries the aircraft up the frame, by roughly the tilt as a fraction of the field of view. At the maximum of 20
   * degrees against a 60-degree field of view that is about a third of the frame, which lands the aircraft near the middle.
   *
   * **20 after flying it.** It shipped at 0 — exactly the old behaviour — so there was a baseline to judge against, and
   * the answer was the top of the range: at 20 degrees the land fills the frame and the black sky is a band along the top
   * rather than half the view. Zero is still reachable with the keys for anyone who wants the level framing back, and the
   * pause menu lists them, because a default that is not the extreme of a hidden control is undiscoverable otherwise.
   */
  PITCH_DOWN: 20,

  /**
   * Range and granularity of the tilt.
   *
   * The single authoritative home for the range: the settings store clamps to it and the keys step within it, so the two
   * cannot disagree about what a valid tilt is. Capped at 20 degrees because beyond that the aircraft rises past the middle
   * of the frame and the view starts to read as looking at the ground rather than flying over it.
   */
  MIN_PITCH_DOWN: 0,
  MAX_PITCH_DOWN: 20,
  PITCH_DOWN_STEP: 2,

  /**
   * Seconds for the camera's aim point to catch up to the aircraft.
   *
   * **This, not the position lag, is what makes the aircraft move within the frame.** The
   * camera always aims so the aircraft lands at AIRCRAFT_SCREEN_HEIGHT, so lagging the
   * camera's *position* only changes the angle it looks from — the framing barely shifts.
   * Lagging what it aims *at* is what lets the aircraft climb up the frame before settling.
   *
   * Raise this for a looser, more floating frame; drop it toward zero to pin the aircraft
   * in place. It scales the vertical excursion during a climb or dive almost directly.
   *
   * 0.45 moves the aircraft about 9% of frame height during a full-deflection climb or dive.
   * Much more than that and a full dive pushes it off the bottom of the screen, since it only
   * sits 19% up to begin with.
   */
  AIM_TIME_CONSTANT: 0.45,

  /**
   * Seconds for the aim point's forward lead to follow a change of direction.
   *
   * The aim point leads along the aircraft's horizontal travel, which is what stops the lag
   * from simply dragging the aim backwards along the flight path and throwing the framing
   * off. The lead is deliberately *not* applied vertically — that is where the climb and
   * dive movement in frame comes from.
   *
   * Because the lead follows direction changes slowly, a turn leaves it pointing slightly
   * wide, which swings the aircraft sideways in frame. Larger values make turns swing more.
   */
  AIM_LEAD_TIME_CONSTANT: 0.65,

  /**
   * Fraction of the aircraft's bank the camera copies. 0 keeps the horizon dead level;
   * 1 rolls the camera as much as the aircraft, which reads as nauseating. A little adds
   * a lot of flow.
   *
   * **If the camera banks the wrong way, negate this.** The sign convention is worked out
   * in `render/camera.js` but it is exactly the kind of thing that is easier to judge by
   * looking than by reasoning.
   */
  ROLL_FOLLOW: 0.3,

  /** Seconds for the camera's own roll to follow the aircraft's. Slower than the aircraft. */
  ROLL_TIME_CONSTANT: 0.6,

  /**
   * Closest the camera may come to the ground, in world units.
   *
   * The aircraft has its own floor, but the camera sits well behind it, where the terrain
   * may be considerably higher — climbing steeply away from a ridge would otherwise drag
   * the camera through it and fill the screen with the inside of a mountain.
   */
  MIN_GROUND_CLEARANCE: 45,
};

// ---------------------------------------------------------------------------
// Aircraft
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Saving and settings
// ---------------------------------------------------------------------------

export const SAVE = {
  /**
   * `localStorage` key for the saved world.
   *
   * Namespaced, because `localStorage` is shared across everything served from the same origin — and
   * on the LAN box this sits behind the same host as another project.
   */
  KEY: 'vibe-flyer:save:v1',

  /**
   * Format version, written into every save and checked on load.
   *
   * Bump this when the shape of a save changes incompatibly. A mismatch is treated as no save at all
   * rather than as an error: the player gets a fresh world instead of a failure they cannot act on.
   */
  VERSION: 1,

  /** Separate key for preferences, so losing settings and losing a world stay separate problems. */
  SETTINGS_KEY: 'vibe-flyer:settings:v1',

  /** Format version for the settings blob, checked the same way. */
  SETTINGS_VERSION: 1,
};

// ---------------------------------------------------------------------------
// HUD — what the player sees, as opposed to the debug overlay
// ---------------------------------------------------------------------------

export const HUD = {
  /**
   * Seconds between HUD refreshes.
   *
   * Slower than the debug overlay. The HUD shows nothing that changes fast, and numbers that flicker
   * draw the eye — which is the opposite of what this project wants.
   */
  INTERVAL: 0.35,

  /**
   * How long the altitude and speed readout stays up after something changes it, in seconds.
   *
   * **The readout is transient rather than permanent**, which is the logical end of this file's own rule
   * that the HUD should say as little as possible. Altitude and speed are worth knowing at the moment you
   * change speed or reframe the camera, and worth nothing the rest of the time — there is no goal to fly
   * toward and no fail state to avoid, so a number sitting in the corner is only there to draw the eye.
   */
  READOUT_SECONDS: 2,

  /**
   * Fade durations for that readout, in milliseconds.
   *
   * Asymmetric on purpose. Appearing has to be quick or it is not an acknowledgement of the keypress that
   * caused it; leaving should be slow enough that the eye is not pulled back to the corner by the change.
   */
  FADE_IN_MS: 140,
  FADE_OUT_MS: 700,

  /**
   * How long a transient message stays on screen, in seconds. Used for "Saved" and similar.
   *
   * Long enough to read without looking for it, short enough that the screen returns to empty.
   */
  MESSAGE_SECONDS: 2.2,
};

// ---------------------------------------------------------------------------
// Debug aids
// ---------------------------------------------------------------------------

export const DEBUG = {
  /**
   * Show the on-screen readout of position, altitude, and live biome weights.
   *
   * On during development because biome blending is very hard to judge without seeing the
   * numbers — "is this a transition or just a small mountain" is not answerable by eye.
   *
   * **Off now that there is a real HUD and the world is worth looking at uncluttered.** It is a developer
   * readout, and having it up by default means every session starts with the view spoiled. `TOGGLE_KEY`
   * brings it back, the pause menu says which key, and the choice is persisted — so turning it on for a
   * tuning session costs one keypress and stays on until it is turned off.
   */
  SHOW_OVERLAY: false,

  /** Key that toggles the overlay, so the view can be cleared without a rebuild. */
  TOGGLE_KEY: 'F1',

  /** Seconds between overlay refreshes. Updating every frame is wasted DOM work. */
  OVERLAY_INTERVAL: 0.2,

  /**
   * How many recent frame durations the readout averages over.
   *
   * A single frame time is useless — it swings wildly. What matters is the average and, more than
   * that, the *worst* frame in recent memory, because one 40 ms frame is felt where a slightly
   * higher average is not. 120 frames is about two seconds at 60 Hz.
   */
  FRAME_SAMPLE_COUNT: 120,

  /**
   * Log a per-phase frame breakdown to the console.
   *
   * **Off. Turn it on to diagnose a reported hitch, and turn it off again** — it is a developer readout and it
   * writes to the console every few seconds.
   *
   * What it exists to settle is a question no average can answer: when a frame is lost, was it because our
   * JavaScript overran, or because the frame went missing outside our JavaScript entirely? The two need
   * opposite fixes, and picking wrong means a session spent optimising something that was never the
   * problem. It earned its keep once already: it showed the lost frames were tile builds overrunning on the
   * main thread, which is why generation now happens on a worker. See `core/frameProfiler.js`.
   */
  PROFILE_FRAMES: false,

  /**
   * Work above this, in milliseconds, counts as a long frame.
   *
   * Deliberately well under a 16.7 ms refresh interval. The point is to catch frames *heading for* an
   * overrun rather than only the ones that already missed: rendering has to happen after our work, and the
   * browser needs time to composite, so a frame that spends 12 ms in our code has already lost most of its
   * margin.
   */
  LONG_FRAME_MS: 12,

  /**
   * Interval above `refresh * this` counts as a display frame the browser lost.
   *
   * 1.5 sits between one refresh and two, so a frame that arrives a whole interval late is caught and
   * ordinary jitter is not. Must exceed 1, since no interval is shorter than the refresh.
   */
  LATE_FRAME_FACTOR: 1.5,

  /** Seconds between profile summaries. Long enough that the console stays readable while flying. */
  PROFILE_INTERVAL: 5,

  /**
   * Individual long-frame lines per summary window.
   *
   * Capped because at sixty frames a second an uncapped log is both unreadable and slow enough to distort
   * what it is measuring. The summary says how often and how badly; these say what one looked like.
   */
  PROFILE_LOG_LIMIT: 4,
};

export const AIRCRAFT = {
  /**
   * Multiplier applied to the model's hand-authored vertex list, which is drawn roughly
   * two units long. At 17 the aircraft is about 34 units nose to tail, which against a
   * 260-unit chase distance fills a bit over a tenth of the frame height — present
   * without dominating.
   */
  SCALE: 17,

  /** Wireframe colour. Bright enough to bloom, so it reads as the one lit object. */
  COLOR: 0x8ce8ff,
};



// ---------------------------------------------------------------------------
// Railway — a surveyed route through the world, and the corridor it needs
// ---------------------------------------------------------------------------

/**
 * A railway laid through the existing world, for the train experience.
 *
 * ## Why the route is authored and the terrain yields to it
 *
 * Every other feature in this world refuses itself when the ground will not cooperate — an airfield rejects
 * a site with more than 44 units of relief, a bridge refuses mismatched banks, a fairground refuses its
 * whole footprint. **A railway cannot refuse.** It has to be continuous, so feasibility has to be
 * guaranteed by construction rather than handled by rejection, and that inverts the usual relationship:
 * the route is laid out first and the ground is shaped to meet it.
 *
 * That is also the only way to get a cutting. Terrain is a weighted sum of biome contributions, so a biome
 * cannot impose a level surface — a pad at 70% weight comes out 70% flat plus 30% of whatever the
 * neighbour is doing, which is why the airfield sits on an embankment and never touches the ground. A
 * corridor applied *after* the blend has no such problem.
 *
 * ## Measured before any of it was built
 *
 * Three routes of 120,000 units through the real world at seed 1337, and the result that decided the
 * design: **about 95% of a route ends up within 150 units of the natural ground.** Median gap 40 units,
 * ninetieth percentile around 130. So corridor shaping is the main mechanism and tunnels and viaducts are
 * rare events, which is what makes the whole thing affordable.
 *
 * The other measured result was negative and just as useful. Sweeping the ruling grade from 1.5% to 4%,
 * the lookahead from 3,000 to 9,000, terrain-blind against actively steering toward low ground, and aiming
 * at the mean ground ahead against aiming at valley floors, every variant landed between 93.8% and 95.5%.
 * **The simplest surveyor was the best one measured**, so there is deliberately no clever router here.
 */
export const RAILWAY = {
  // --- the chain ------------------------------------------------------------

  /**
   * Distance between chain stations, in world units.
   *
   * Finer than the terrain lattice, because the track is the one thing the player looks at from a few units
   * away and a faceted rail would be obvious. It also sets the resolution of everything derived from the
   * chain — grade, curvature, and the corridor's own centre line.
   */
  STATION_SPACING: 40,

  /**
   * Steepest grade the route will use, as a rise over run.
   *
   * Real mainlines sit at 1% or less and anything past about 2.2% is rare, so 2.5% is at the upper end of
   * plausible. **It is a look constraint rather than a physical one**: there is no motion platform, so the
   * player cannot feel the difference between 1.5% and 4%. That matters, because it means this limit can be
   * broken locally when the alternative is worse — a forty-second tunnel, say.
   *
   * Measured, it barely moves the earthworks: 1.5% gave 95.2% of the route within shaping range and 2.5%
   * gave 95.5%. It is set here for the look of the profile, not to reduce cost.
   */
  MAX_GRADE: 0.025,

  /**
   * Most the grade may change per station, which is the vertical equivalent of a minimum curve radius.
   *
   * **The camera rides the track**, so this is a comfort limit rather than an engineering one, and it is
   * stricter than anything terrain needs: a seam in the ground only has to match in height, while a ridden
   * profile has to match in slope as well or every joint reads as a jolt.
   */
  MAX_GRADE_CHANGE: 0.0006,

  /**
   * Tightest curve the route ordinarily uses, in world units.
   *
   * Also a look number rather than a physical one. A real railway's minimum radius comes from lateral
   * acceleration, which nobody here can feel — so what this actually decides is how much the line visibly
   * winds. At 2,500 a quarter turn takes about 3,900 units, which reads as a mountain railway rather than a
   * high-speed line, and it leaves enough steering authority to bend the route around a region.
   *
   * Not the hard floor: `CURVE_TIGHTEN` lets the occasional stretch go below it.
   */
  MIN_RADIUS: 2500,

  /**
   * How much tighter than `MIN_RADIUS` the occasional stretch of line may turn, as a fraction.
   *
   * Flown, the alternation between straight and curved read well but every curve was the same curve. **Raising
   * the limit everywhere would not fix that** — it would move the whole route to a new normal and lose exactly
   * the contrast that makes a curve register. So the limit itself varies, on a wavelength far longer than the
   * wander, which makes a sharper curve a *place on the route* rather than a wobble in it.
   */
  CURVE_TIGHTEN: 0.1,

  /**
   * How often a tighter stretch comes round, in world units.
   *
   * More than three times the wander's wavelength, so a sharper stretch lasts long enough to be a feature of
   * somewhere rather than a twitch in the middle of an ordinary curve.
   */
  CURVE_TIGHTEN_WAVELENGTH: 46000,

  /**
   * The band of the tightening field over which the increase ramps in, as 0..1 levels.
   *
   * Below `FROM` the route is exactly as it would be with none of this, which is what keeps a sharper stretch
   * occasional — roughly a quarter of the line gets anything at all.
   *
   * **`FULL` is 0.9 rather than 1.0, and that was measured rather than chosen.** A two-octave field almost never
   * reaches the ends of its nominal range, so a ramp that only completed at 1.0 delivered half the configured
   * increase and no more — the realised peak was 5% against a configured 10%. Naming the level where the ramp
   * completes is what makes the number in `CURVE_TIGHTEN` mean what it says.
   */
  CURVE_TIGHTEN_FROM: 0.72,
  CURVE_TIGHTEN_FULL: 0.9,

  /**
   * Wavelength of the field driving the plan alignment, in world units.
   *
   * How often the line changes its mind. Long, so a curve is something you travel through rather than
   * something that flicks past.
   */
  WANDER_WAVELENGTH: 14000,

  /**
   * How far ahead the surveyor looks before choosing a grade, in world units, and how many samples it
   * averages over that distance.
   *
   * **An average rather than a single point**, so one crest cannot yank the profile. Measured, 3,000 beat
   * 6,000 and 9,000 — a longer view commits the line to a level too early and then it has to bore through
   * whatever is in the way.
   */
  LOOKAHEAD: 3000,
  LOOKAHEAD_SAMPLES: 10,

  // --- steering -------------------------------------------------------------

  /**
   * How far ahead the biome-scale probes look, and how far to either side, in world units.
   *
   * **Sized to the field being read, and getting this wrong makes steering useless.** Steering on terrain
   * height cannot work at all: ridges run at a 2,600-unit wavelength and no railway curve can dodge them.
   * Biome regions run at 28,000, so their gradient is a reliable long-range signal — measured, this takes
   * the mountains' share of a route from 6.8% down to 0.7%.
   *
   * The matching trap is probing *too* wide. A first attempt at following canyon floors probed 8,000 units
   * apart while gorges sit about 9,000 apart, so the two probes landed in different canyons and the
   * gradient between them was noise. Any future steering term has to be probed at the scale of its own
   * field.
   */
  STEER_REACH: 8000,
  STEER_SIDE: 4000,

  /**
   * Mountain weight the route aims to hold as it passes a range.
   *
   * **Not zero, deliberately.** Fleeing mountains entirely wastes the best scenery in the world; holding a
   * modest weight runs the line along the foot of the range with the peaks filling the view to one side,
   * which is both what real railways do and what keeps them on screen for minutes rather than flashing past
   * a tunnel mouth.
   */
  MOUNTAIN_TARGET: 0.25,

  /**
   * How hard the alignment leans on the steering cost, as a fraction of the maximum curvature.
   *
   * One combined cost rather than a nudge per objective. Measured, separate hand-summed terms cancel each
   * other out — avoiding mountains pushes the line into canyon country, following canyons lets it drift
   * back into the mountains — so anything added here belongs in the same cost function rather than beside
   * it.
   */
  STEER_STRENGTH: 1,

  // --- the corridor ---------------------------------------------------------

  /**
   * Half-width of the level trackbed, in world units.
   *
   * The formation itself: ground held exactly at rail level, so the track has something flat under it. Kept
   * tight — the point of the train is to see things close up, and a wide graded platform would push the
   * interesting ground away from the window.
   */
  FORMATION_HALF_WIDTH: 26,

  /**
   * How far the corridor's influence reaches from the centre line, in world units.
   *
   * Beyond this the ground is exactly what it would have been with no railway in the world, which is what
   * makes the corridor invisible where it did no work. **On flat country there is nothing to see**, because
   * the shaping and the natural ground agree — a corridor exists logically everywhere and shows up only
   * where it had to cut or fill.
   *
   * The default, for ordinary country. Five lattice cells: enough to draw a cut face at 50-unit resolution,
   * and no more. Rock and gorge country override it — see `GORGE_REACH` and `CUT_REACH`.
   */
  CORRIDOR_REACH: 260,

  // --- crossing gorge country -----------------------------------------------

  /**
   * How far the corridor reaches, how it falls, and how it benches where a canyon leads, in world units.
   *
   * ## The mistake this replaces
   *
   * The first version tried to *steer the train into* a gorge, and it produced exactly the artefact that was
   * reported: from the air, a canyon running one way and a railway crossing it, making a giant X. That was
   * never going to work. A canyon is a winding line and so is a railway, and two independent winding lines
   * cross — steering only changed how often.
   *
   * **So the gorge is carved around the train instead.** The corridor already pulls ground toward rail level;
   * in canyon country it reaches much further out and the rail runs far below the plateau, so the walls come
   * up either side of a route that stays roughly level. It cannot make an X, because the gorge *is* the
   * route. The reference is the Royal Gorge: a line along the lowest ground with the walls above it, never
   * climbing over them.
   *
   * The reach is sized against the drop. At 1,100 units against `GORGE_DEPTH` the walls average a slope of
   * about 0.3, and the batter makes them flatter at the floor and steeper at the rim — which is the same
   * profile `CANYON.WALL_SHARPNESS` gives a natural gorge, and it is deliberately the same number.
   *
   * The step is what makes it read as rock rather than as a moulded trench, and it is `CANYON.STEP_HEIGHT`
   * for the same reason. It is faded out toward the rim, or the quantising would leave a visible ring where
   * the corridor meets untouched ground.
   */
  GORGE_REACH: 1100,
  GORGE_BATTER: 2.6,
  GORGE_STEP: 80,

  /**
   * How far below the surrounding plateau the rail runs in gorge country, in world units.
   *
   * The rail has to aim *low* here, or there is nothing for the corridor to carve — aiming at the average
   * ground ahead puts the line on the rim, where the plateau already sits at rail level and no gorge appears.
   *
   * **Only where a gorge biome leads.** Measured globally, aiming low is much worse: over three routes it
   * took the share needing nothing but shaping from 95.2% down to 79.2% and quadrupled the tunnelling,
   * because a line below the general ground level is in cut everywhere. It is right here and wrong
   * everywhere else.
   *
   * Reached gradually, since a biome transition runs thousands of units and the grade limit holds — so the
   * gorge deepens as the line enters it, which is what a gorge does anyway.
   */
  GORGE_DEPTH: 330,

  /**
   * How far the corridor reaches where hard rock leads, in world units.
   *
   * Badlands and mesa. **Deliberately the tightest setting in the table**: the idea is a path blasted just
   * wide enough to get the train through and no wider, with the crests rising immediately either side. Rock
   * holds a near-vertical face where soil does not, which is the physical reason a cut here is narrow and a
   * cut through farmland would not be.
   */
  CUT_REACH: 110,

  /**
   * Exponent shaping the fall from the formation back to natural ground.
   *
   * The batter angle, in effect: above 1 holds the shaped level out before letting go, which gives a defined
   * cut face rather than a funnel. This is the same curve `carveHollow` puts under a river valley, and it is
   * the same reason — a hollow with a flat bottom rather than a V.
   *
   * **Wants to become per-biome.** Rock holds a near-vertical face and soil does not, so badlands, mesa and
   * canyon should cut narrower and steeper than farmland. One number for now.
   */
  CORRIDOR_BATTER: 1.6,

  // --- the track itself -----------------------------------------------------

  /**
   * Distance between the rails, in world units.
   *
   * Exaggerated far past a real gauge, in the same way everything else here is: a carousel in this world is
   * 76 units across. At a true 1.4 units the two rails would fall inside one lattice cell and read as a
   * single line, which is no use in the one view the whole experience is built around.
   */
  GAUGE: 16,

  /** How far the rails and sleepers sit above the formation, so they do not fight the terrain wireframe. */
  RAIL_LIFT: 3,

  /**
   * Distance between sleepers, in world units.
   *
   * **A distance rather than a count of stations, so the pitch is a look decision and the station spacing
   * stays a survey decision.** They were coupled first and the sleepers came out at 80 units apart, which
   * read as a ladder rather than a track — reported as needing about six times more of them. Sleepers are now
   * interpolated along each segment, so this can go as fine as it likes without making the chain longer.
   *
   * This is the detail that says *railway*. Two rails alone read as a road; what identifies a track is the
   * cross-hatching streaming past under the window, so it is worth more segments than anything else here.
   */
  SLEEPER_PITCH: 13,

  /** How far a sleeper overhangs the rails, in world units. */
  SLEEPER_OVERHANG: 5,

  /**
   * Half-width of the ballast shoulder line, in world units.
   *
   * Two dim lines outside the rails, which is what makes the track read as sitting on a raised bed rather
   * than painted on the ground. Cheap: two segments per station.
   */
  SHOULDER_HALF_WIDTH: 22,

  // --- the train, and how it is driven --------------------------------------

  /**
   * Fastest the train travels, in world units per second.
   *
   * **Deliberately well under the aircraft, and derived rather than chosen.** The flyer runs 143 to 416
   * across seven trim steps; a train at the top of that would blur the ground it exists to show. 180 is
   * roughly the flyer's second step, which is where it was asked to sit.
   *
   * It is also what makes a tunnel bearable. A mountain ridge is about 2,600 units apart and a bore through
   * one runs to something over a thousand, so at this speed a tunnel is a handful of seconds — long enough to
   * be a beat, short enough that nobody wonders whether the game has stopped.
   */
  MAX_SPEED: 180,

  /**
   * Speed steps, including a full stop at zero.
   *
   * **A stop is the one place this departs from the flying brief**, which says outright that there is never a
   * full stop because "hovering would turn a flight into a photograph". On rails the opposite is true: the
   * whole point is to look at things, and being unable to hold still to look at one would be the omission.
   */
  SPEED_STEPS: 6,

  /**
   * Seconds for the train to close most of the gap to a new speed.
   *
   * Longer than the aircraft's 2.5, because a train is the heaviest thing in the world and because coasting
   * to a stand is most of what makes it read as one. Nothing here responds instantly and this responds least.
   */
  SPEED_TIME_CONSTANT: 4.5,

  /**
   * How far the rider may swing their view either side, in degrees, and how long the swing takes.
   *
   * The only control that is not speed. Ninety-five degrees reaches a little past square, so you can watch
   * something go by rather than losing it at the edge — and holding the key the other way looks back down the
   * line the train has come along.
   */
  LOOK_LIMIT: 95,
  LOOK_TIME_CONSTANT: 0.45,

  /**
   * How far the train leans into a curve, in degrees at the tightest radius it will take.
   *
   * Cant. Small, because a real railway's superelevation is a few degrees rather than a bank — but it is the
   * detail that stops a curve reading as the world sliding sideways, and it costs one multiplication.
   */
  MAX_CANT: 6,

  // --- the two views --------------------------------------------------------

  /**
   * Eye height above the railhead in the cab view, in world units.
   *
   * **Low on purpose.** This is the view the whole experience is built around: at track level a cutting rises
   * over your head and the ballast streams past underneath, and both of those are lost from even a few tens of
   * units higher. Nothing is drawn in front of it — there is no train in this view, which is what was asked
   * for.
   */
  CAB_EYE_HEIGHT: 26,

  /** How far ahead the cab view aims, in world units. Far enough that the track reads as leading somewhere. */
  CAB_AIM_AHEAD: 2200,

  /** Where the outside view sits: behind and above the train, in world units. */
  CHASE_BEHIND: 620,
  CHASE_ABOVE: 210,

  /**
   * Seconds for the outside view to catch up with the train.
   *
   * Damped, like the aircraft's chase camera and for the same reason — an instant follow reads as the camera
   * being welded on, and the lag through a curve is most of what makes motion feel weighty.
   */
  CHASE_TIME_CONSTANT: 0.55,

  /**
   * Clear ground either side of the centre line that nothing may be built on, in world units.
   *
   * **Narrower than the corridor, deliberately.** The corridor reaches hundreds of units out to blend a cutting
   * back into the hillside, and a wooded cutting is a good thing — what has to stay clear is the formation and
   * a little either side. Reported after flying it: trees, houses, water towers and rock formations were
   * standing on the rails.
   *
   * Measured against what it has to cover: the formation is 26 either side and the ballast shoulder 22, so this
   * leaves a visible margin beyond both. A structure's own radius is added on top, so a wide tree keeps
   * proportionally further back without this having to know how wide anything is.
   *
   * Anything that crosses *over* is unaffected. A power line's cables are drawn between its pylons, so keeping
   * the pylons off is enough to leave the span crossing the track with nothing having to know about it.
   */
  TRACK_CLEARANCE: 60,

  // --- streaming ------------------------------------------------------------

  /**
   * How far past the rider the route is surveyed before any terrain is built, in world units.
   *
   * **This is the invariant the whole mode rests on, and it is not a performance number.** The corridor shapes
   * terrain from the chain, and `surveyor.nearest` can only see chain already walked — so a tile built before
   * the route reached it would disagree with the same tile built later. Worse, the heightfield caches lattice
   * elevations and has no way to invalidate them, and the worker builds its own chain independently.
   *
   * Generously more than `TILES.LOAD_DISTANCE`, because the line winds: a tile 7,800 units away as the crow
   * flies can be considerably further along the rails. At this margin every tile that can be built has had its
   * corridor fully determined on both threads, so the two cannot produce different geometry for it.
   */
  PREPARE_MARGIN: 20000,

  /**
   * Length of one drawn stretch of track, in world units, and how far either side of the rider they are kept.
   *
   * Chunked along the *line* rather than by tile, because the track is one-dimensional and chunking it that way
   * needs no spatial index at all. Behind is shorter than ahead for the obvious reason.
   */
  TRACK_CHUNK: 900,
  TRACK_AHEAD: 8200,
  TRACK_BEHIND: 2400,
};

/**
 * Gradient for the track.
 *
 * Cold steel, like the bridges, and for the same reason stated there: a railway is unmistakably engineered,
 * and drawing it in its host biome's colours would make it vanish into whatever it is crossing. It has to
 * stay legible against farmland, mountains and a city alike.
 */
export const RAILWAY_PALETTE = {
  /** Dim at the ballast, so the shoulder recedes into the ground. */
  LOW: 0x2f3d55,

  /** Bright on the railheads, well over the bloom threshold — the two lines the eye follows. */
  HIGH: 0xe6f0ff,
};

// ---------------------------------------------------------------------------
// Wind farm — an installation on farmland
// ---------------------------------------------------------------------------

/**
 * A row of large wind turbines: three to nine of them, evenly spaced, all facing the same way.
 *
 * ## Why farmland hosts it
 *
 * By the usual test it needs *open, level* ground rather than a new kind of ground, so it is an installation
 * rather than a biome — the same call the airfield and the telescope arrays made. Farmland is the host because
 * it is flat (145 units of relief against rolling hills' 470), because real wind farms stand in fields more
 * than anywhere else, and because it is the only open biome with no installation of its own yet. Baseline has
 * the airfields and rolling hills has the telescope arrays, and one host each is the rule: two independent site
 * grids could put two installations on the same spot across a transition.
 *
 * It also gives farmland something it was missing. The biome already has a Kansas windpump 40 units tall; a
 * turbine at nearly ten times that, in the same fields, is the scale contrast that makes both read properly.
 *
 * ## The row is aimed by a roll, not by the terrain
 *
 * The airfield aims itself along the local contour, and pays for it: because the reservation has no terrain
 * sampler it cannot know the aim, so it has to reserve a rotation-invariant *circle* over the whole site. For a
 * row nearly 3,500 units long that circle would deny a vast area of farmland and leave a conspicuous round
 * clearing.
 *
 * So the aim comes from a roll instead, which is both cheaper and more nearly right: turbines face the
 * prevailing wind, which has nothing to do with which way the hill runs. The reservation can then be a capsule
 * along the actual row, which is the honest shape and a fraction of the ground.
 */
export const WIND_FARM = {
  /**
   * Spacing of the slot grid sites are placed on, in world units.
   *
   * Coarse, and it has to be: a nine-turbine row is about 3,400 units long, so the spacing must exceed twice
   * the row's reach plus its jitter or two farms could overlap. That guarantee is arithmetic rather than a
   * collision check, the same as the telescope arrays'.
   *
   * ## Measured, because the first value made them hard to find
   *
   * At 8,400 the world held **six wind farms in an area 128,000 units square**, a median 25,800 units apart —
   * rarer than the fairgrounds, for something that is meant to be a familiar sight in open country. Farmland is
   * only 6% of the world, so a slot grid this coarse puts barely a dozen candidate slots in it before the ground
   * checks take their share.
   *
   * At 6,400 the spacing is close to the width of a farmland region, which is the right target: roughly one farm
   * per region that will take one. Still comfortably more than twice a row's reach plus its clearance, which is
   * the arithmetic that stops two overlapping.
   */
  SITE_SPACING: 6400,

  /** Chance an eligible slot is considered, before the ground checks. Scaled by biome weight. */
  SITE_DENSITY: 0.6,

  /** How far a site may be nudged from its slot centre, in world units, so the grid never shows. */
  SITE_JITTER: 900,

  /**
   * Fewest and most turbines in a row.
   *
   * **Three is a floor rather than a target.** One turbine is a curiosity and two is a coincidence; three in a
   * line is unmistakably a wind farm, which is why a site with fewer usable positions than this builds nothing
   * at all rather than a shorter row.
   */
  MIN_TURBINES: 3,
  MAX_TURBINES: 9,

  /**
   * Distance between turbines along the row, in world units.
   *
   * A little over three blade diameters, which is roughly the real spacing — close enough that they read as one
   * installation and far enough that the rotors do not visually overlap from the side.
   */
  TURBINE_SPACING: 430,

  /**
   * Steepest ground a turbine will stand on.
   *
   * Tighter than the airfield's 0.45, because a turbine is a 300-unit vertical line and any lean would be
   * obvious — where a runway can sit on an embankment, a tower cannot be fudged. Each turbine stands at its own
   * ground height rather than on a graded pad, so a row steps gently across a rise the way a real one does.
   */
  MAX_GROUND_SLOPE: 0.16,

  /** Clear space a turbine needs from other structures, in world units, beyond its own blade reach. */
  CLEARANCE: 40,

  // --- the turbine itself ---------------------------------------------------

  /**
   * Height of the hub above the ground, in world units.
   *
   * **Large, which is the whole request.** With the blades this reaches about 430 — comparable to an industrial
   * chimney at 470 and well above anything farmland otherwise has, so a row of them is a landmark visible from
   * a long way off. That is what a modern turbine is.
   */
  HUB_HEIGHT: 300,

  /**
   * How much turbine height varies, as a fraction.
   *
   * **Between farms, never within one.** A real wind farm is a batch of identical machines, so varying them
   * inside a row would read as a mistake; varying between rows reads as different vintages, which is exactly
   * what it would be.
   */
  HEIGHT_VARIATION: 0.12,

  /** Radius of the tower at its base, and the fraction of that it tapers to at the hub. */
  TOWER_BASE_RADIUS: 15,
  TOWER_TOP_RATIO: 0.55,

  /**
   * Sides in the tower's cross-section, and how many horizontal rings it carries.
   *
   * Five sides rather than four, so the tower never presents a dead-flat face to the camera. Both numbers are
   * deliberately small: a tower is slender enough that its two silhouette edges are what actually read, and the
   * rings are there to give it scale against an empty sky rather than to describe a cylinder.
   */
  TOWER_SIDES: 5,
  TOWER_RINGS: 3,

  /** The nacelle: a box at the hub, along the rotor axis. What makes the tower read as machinery. */
  NACELLE_LENGTH: 46,
  NACELLE_WIDTH: 20,
  NACELLE_HEIGHT: 20,

  /**
   * Blades: how many, how long, how wide at the root, and how far out the hub they start.
   *
   * Three, because a two-bladed turbine reads as a propeller and four reads as a windmill. Each blade is drawn
   * as two converging edges with a chord or two across them, which is what makes it a tapered aerofoil rather
   * than a stick.
   */
  BLADES: 3,
  BLADE_LENGTH: 130,
  BLADE_ROOT_WIDTH: 16,
  BLADE_CHORDS: 2,
  HUB_RADIUS: 11,
};

/**
 * Gradient for wind turbines.
 *
 * **Fixed rather than taken from the host biome, the same deliberate exception a bridge gets.** Drawn in
 * farmland's own colours a turbine would sink into the fields it stands in, and these are the tallest things
 * for a long way in any direction — they are meant to be seen. Near-white, because that is what a turbine
 * actually is, and it is the one colour nothing else in the fields uses.
 */
export const WIND_TURBINE_PALETTE = {
  /** Grey at the foot of the tower, so the base settles into the ground rather than glowing at it. */
  LOW: 0x4a5568,

  /** Cold white at the hub and out along the blades, over the bloom threshold so the rotor catches the eye. */
  HIGH: 0xf0f6ff,
};
